import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import {
  FileAudio,
  Trash2,
  Check,
  X,
  Plus,
  Download,
  AlertTriangle,
  FileJson,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  Folder,
  ChevronRight,
  Pencil,
  Search,
  ArrowUpDown,
  ChevronDown,
  Layers,
  FlaskConical,
  TestTube,
  Droplets,
  PaintBucket,
  FileText,
  MapPin,
  ChevronLeft,
  RefreshCw, Mic, Copy,
  Table,
  FileSpreadsheet,
  Image as ImageIcon,
  Upload,
  CopyPlus,
  MoreHorizontal, FolderPlus, Settings2, UsersRound, X as XIcon, Camera, Info,
} from 'lucide';
import { projectIssues, boringIssues, BLOCKING } from '../utils/boring-issues.js';

/**
 * Een melding in de huisstijl, als vervanger van het kale window.showAlert().
 *
 * Zelfde aanroepvorm als showAlert() zodat elke bestaande plek ongewijzigd kan blijven: alleen de
 * naam verandert. Die meldingen zijn bijna allemaal een mislukte actie, en juist dan wil je
 * niet ook nog een venster van de browser zien dat 'localhost:3000 says' erboven zet.
 */
const showAlert = (message, title = 'Melding') => alertDialog({ title, message });

/**
 * Hoogte van de bediening in een lijst-werkbalk. Gelijk aan de zoekbalk in de topbalk: een
 * zoekveld van 32px naast knoppen van 26px viel op als 'net niet' zonder dat je zag waarom.
 */
const TOOLBAR_H = '36px';

/** Eén regel in het overloopmenu bij de projectkop. */
const PROJECT_MENU_ITEM = 'display:flex; align-items:center; gap:.65rem; width:100%; padding:.6rem .65rem; border:none; background:none; border-radius:.5rem; cursor:pointer; font:inherit; font-size:.88rem; color:var(--mv-text,#181c32); text-align:left;';
import { codeLabel } from '../utils/codelabels.js';
import { api } from '../api.js';
import { auth } from '../auth.js';
import './xml-viewer.js';
import './recording-row-detail.js';
import './boring-duplicate-dialog.js';
import './boring-bulk-update-dialog.js';
import './boring-inspreken-dialog.js';
import './activity-tile.js';
import { CONFIG } from '../config.js';
import './mv-table.js';
import styles from '../styles/project-detail.css?inline';
import { lucideIcon } from '../icon.js';
import './project-map.js';
import { loadPlannedPoints } from '../utils/planned-points.js';
import './vooronderzoek-panel.js';
import './map-import-dialog.js';
import './report-list.js';
import './field-order-panel.js';
import './lab-koppeling-page.js';
import { profielUitOpslag, profielOnthouden } from '../utils/bedrijfsprofiel-cache.js';
import { betaFeatureAllowed, usesNen6693Codes, iso14688, extractText, ProjectModel, BoringModel, COORDINATE_SYSTEMS, HEIGHT_DATUMS, DEFAULT_COORDINATE_SYSTEM, DEFAULT_HEIGHT_DATUM, SOIL_STANDARDS, DEFAULT_SOIL_STANDARD, normalizeSoilStandard, convertFromWgs84, REFERENCE_PLANES, DEFAULT_REFERENCE_PLANE, displayName, nen6693, monsterVerpakkingLabel, getBoringTypeLabel, DEFAULT_TIMEZONE, BORING_TYPE, normalizeBoringType } from '@mv/models';
import './searchable-select.js';
import { projectVelden } from '../utils/project-velden.js';
import {
  validateProjectData,
  validateRecordingData,
} from '../utils/project-data.js';
import { generateProjectPDF, buildProjectReportDoc } from '../utils/pdf-generator.js';
import { exportBoorprofielenPdf, buildBoorprofielenDoc } from '../utils/boorprofiel-pdf.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { isExtractableDoc, extractDocumentText } from '../utils/extract-text.js';
import { formatDateTime, formatDateTimeSlash, parseTimestamp } from '../utils/datetime.js';
import { inPorties } from '../utils/upload-porties.js';
import { haalFotosOp, bouwExportZip } from '../utils/export-met-fotos.js';
import { exportProjectExcel, buildExcelSheets } from '../utils/excel-export.js';
import { loadSublocations, SUBLOCATIONS_EVENT } from '../utils/sublocations.js';

/** Uitleg bij 'Foto's meenemen' in het exportvenster (knop 'Hoe werkt dit?'). */
const FOTO_EXPORT_UITLEG = "Je ontvangt een .zip-bestand dat je eerst moet uitpakken. Daarna heb je een map met een .xml-bestand en een map met de foto's erin.\n\nIn TerraIndex upload je eerst het project door het .xml-bestand te uploaden; daarna kun je binnen dat project handmatig de foto's uit de map 'fotos' uploaden.";

/**
 * Project detail view — recordings, processing status, and XML export.
 */
export class ProjectDetail extends LitElement {
  static styles = css`${unsafeCSS(styles)}`;


  static properties = {
    projectId: { type: String },
    project: { type: Object },
    recordings: { type: Array },
    loading: { type: Boolean },
    transcribing: { type: Boolean },
    processing: { type: Boolean },
    reprocessing: { type: Boolean },
    showReprocessWarning: { type: Boolean },
    showDeleteRecordingModal: { type: Boolean },
    showUploadModal: { type: Boolean },
    _inspreekOpen: { type: Boolean, state: true },
    _nieuwKeuzeOpen: { type: Boolean, state: true }, // keuzevenster 'Nieuwe boring' (drie tegels)   // 'Nieuwe boring → Inspreken' (microfoon)
    _duplicateSource: { type: Object, state: true },
    _bulkOpen: { type: Boolean, state: true },       // venster 'Bulk bijwerken'
    _bulkUpdatedIds: { type: Object, state: true },  // Set: badge 'Bijgewerkt' tot je de pagina verlaat
    _bulkNotice: { type: String, state: true },      // '10 boringen bijgewerkt'
    _projectMenuOpen: { type: Boolean, state: true },
    _teamOpen: { type: Boolean, state: true },
    _team: { type: Object, state: true },
    // Gast uitnodigen vanuit de teampopup: het formulier is ingeklapt tot je erom vraagt, want
    // het is de uitzondering — meestal kijk je alleen wie erbij kan.
    _guestFormOpen: { type: Boolean, state: true },
    _guestForm: { type: Object, state: true },
    _guestBusy: { type: Boolean, state: true },
    _guestResult: { type: Object, state: true },
    showDeleteModal: { type: Boolean },
    error: { type: String },
    // XML export/preview
    xmlContent: { type: String },
    exportGenerating: { type: Boolean },
    exportWarnings: { type: Array },
    exportError: { type: String },
    // Progress overlay
    showLoader: { type: Boolean },
    progressData: { type: Object },
    currentMessage: { type: String },
    // Pagination
    recordingsPage: { type: Number },
    recordingsPageSize: { type: Number },
    recordingsTotal: { type: Number },
    // Filtering
    showFilterModal: { type: Boolean },
    exportFormat: { type: String },
    selectedMeetpuntIds: { type: Array },
    parentProject: { type: Object },
    deelprojects: { type: Array },
    projectBorings: { type: Array },
    /** Geplande boorlocaties en tekeningen van dit project (kaart-tab). */
    _plannedPoints: { state: true },
    _mapLayers: { state: true },
    _mapImportOpen: { state: true },
    showCreateModal: { type: Boolean },
    editingDeelproject: { type: Object },
    projectNameValue: { type: String },
    projectCodeValue: { type: String },
    instructionsValue: { type: String },
    coordinateSystemValue: { type: String },
    heightDatumValue: { type: String },
    soilStandardValue: { type: String },
    /** Korrelverdeling invullen bij een laag? Per (deel)project. */
    grainSizeInputValue: { type: Boolean },
    timezoneValue: { type: String },
    _openBoringTab: { state: true },   // { boringId, tab } — subtab bij springen vanuit de tegel
    _visibleColumns: { state: true },  // welke kolommen de gebruiker aan heeft staan
    _sublocations: { state: true },    // deellocaties van het project, voor de kolom Deellocatie
    _cell: { state: true },            // cel die in de boringenlijst bewerkt wordt: { id (boring), col, value }
    _monstersOpen: { state: true },    // popup met alle grondmonsters van het project
    _peilbuizenOpen: { state: true },  // popup met alle peilbuizen van het project
    _peilbuisColumns: { state: true }, // extra kolommen in die tabel
    deepLink: { type: Object },        // { boringId, tab } uit de URL — spring hierheen zodra de data er is
                                       // (niet 'focus': dat is een bestaande methode op elk HTML-element)
    werksoortValue: { type: String },     // werksoort onderzoek (WERKSOORT) in de bewerk-modal
    templateValue: { type: String },      // gekozen sjabloon in de bewerk-modal
    _templateInitial: { state: true },    // sjabloon bij openen (voor "gewijzigd?"-waarschuwing)
    _templates: { type: Array, state: true },
    _memberNames: { type: Array, state: true },   // boormeester-opties (teamleden)
    _currentUserName: { type: String, state: true }, // standaard-boormeester voor nieuwe boring
    createError: { type: String },
    isCreating: { type: Boolean },
    showXmlPreview: { type: Boolean },
    _exportFormat: { type: String }, // 'xml' | 'profiles' | 'report' | 'excel' — gekozen exportformaat
    _exportModalOpen: { type: Boolean }, // full-page export-venster open
    _exportMenuOpen: { type: Boolean },  // dropdown met de drie formaten open
    _exportFotos: { type: Boolean },     // TerraIndex-export: foto's van de gekozen boringen meenemen (zip)
    _exportVoortgang: { type: String },  // 'Foto's ophalen 3/12' tijdens het maken van de zip
    _fotoTelling: { type: Object },      // boringId → { aantal, bytes } voor de fototegel in de export
    _pdfPages: { type: Array },      // gerenderde PDF-voorbeeldpagina's (dataURL) via pdf.js
    _pdfPage: { type: Number },      // huidige voorbeeldpagina (0-based)
    _pdfBusy: { type: Boolean },     // PDF-voorbeeld wordt opgebouwd
    _pdfDirty: { type: Boolean },    // selectie/instellingen gewijzigd sinds laatste render
    _pdfError: { type: String },
    selectedProjectIds: { type: Array },
    aggregatedRecordings: { type: Array },
    selectedBoringGuids: { type: Object }, // Set of GUID strings
    expandedBoringGuids: { type: Object }, // Set of GUID strings
    activeTab: { type: String }, // 'overzicht' | 'vooronderzoek' | 'borings' | 'map' | 'labopdrachten' | 'rapportage' | 'export' ('opdracht' = oude link → vooronderzoek)
    _labOrderCount: { state: true },  // aantal labopdrachten, voor het cijfer op het tabblad
    // Licentie van het actieve bedrijf: 'geovoice' | 'geodesk'. Labopdrachten horen bij
    // GeoDesk. Zolang dit null is, weten we het nog niet en schermen we niets af — anders
    // ziet een GeoDesk-gebruiker bij elke paginalading kort de upgrademelding.
    _companyLicense: { type: String, state: true },
    _betaFeatures: { type: Array, state: true },  // aangezette bètafuncties van dit bedrijf (vooronderzoek, rapportage)
    _profielEcht: { state: true },  // het profiel is van de server (niet alleen uit de opslag)
    _activitySignal: { type: Number, state: true }, // bump → activiteitentegel herlaadt
    showBoringModal: { type: Boolean },
    _createFormVal: { type: Object, state: true },
    _createFormSaving: { type: Boolean, state: true },
    _inlineFormVals: { type: Object, state: true },
    _loadingRecordings: { type: Boolean, state: true },
    _loadingBorings: { type: Boolean, state: true },
    // Search + sort (created/name) for the opnamen and boringen tables — same as the
    // field-recorder's Opnames/Boringen toolbars.
    _recordingsSort: { type: String, state: true },
    _recordingsQuery: { type: String, state: true },
    _boringsSort: { type: String, state: true },
    _boringsQuery: { type: String, state: true },
  };

  // ─── Computed: duplicate boring names across all recordings ────────────────

  get _duplicateBoringNames() {
    const counts = {};
    for (const b of (this.projectBorings || [])) {
      if (b.name && b.name !== 'Naamloos') {
        counts[b.name] = (counts[b.name] || 0) + 1;
      }
    }
    return new Set(Object.keys(counts).filter(n => counts[n] > 1));
  }

  // Flat list of all boringen across selected projects (from normalized tables)
  /**
   * Bètafuncties: alleen met een GeoDesk-licentie én de schakelaar aan voor dit bedrijf (Beheer →
   * Bedrijven). Ook een systeembeheerder ziet ze alleen bij een bedrijf waar ze aanstaan. De
   * backend (requireBetaFeature) beslist; dit verbergt alleen tabbladen die anders een 403 geven.
   *   vooronderzoek → de tabbladen Vooronderzoek en Opdracht
   *   rapportage    → het tabblad Rapportage
   */
  get _reportAllowed() {
    return betaFeatureAllowed({ license: this._companyLicense, betaFeatures: this._betaFeatures }, 'rapportage');
  }

  get _vooronderzoekAllowed() {
    return betaFeatureAllowed({ license: this._companyLicense, betaFeatures: this._betaFeatures }, 'vooronderzoek');
  }

  /** De tabbladen van een project; ook gebruikt door het laad-skelet, zodat dat niet verspringt. */
  get _tabDefs() {
    return [
              { id: 'overzicht',     label: 'Overzicht',     count: null },
              // Bètafunctie Vooronderzoek (GeoDesk + aangezet): het monsterplan volgens NEN 5740 en
              // de veldwerkopdracht van de projectleider (inlezen, toetsen aan BRL SIKB 2000,
              // goedkeuren). Vóór het veldwerk, dus vóór Boringen.
              ...(this._vooronderzoekAllowed ? [
                { id: 'vooronderzoek', label: 'Vooronderzoek', count: null, beta: true },
              ] : []),
              // Het aantal BORINGEN, niet het aantal rijen. In de lijst staan ook opnamen die
              // (nog) geen boring hebben opgeleverd; die meetellen maakte van vier boringen zes.
              { id: 'borings',       label: 'Boringen',      count: (this._loadingBorings || this._loadingRecordings) ? null : (this.projectBorings || []).length },
              { id: 'map',           label: 'Kaart',         count: null },
              // Het aantal komt mee met het project (lab_order_count), zodat het er staat
              // voordat je het tabblad opent. Bij een bedrijf zonder GeoDesk telt het niet mee.
              // Labopdrachten alleen met GeoDesk; zonder bestaat het tabblad niet.
              ...(this._companyLicense === 'geodesk' ? [{ id: 'labopdrachten', label: 'Labopdrachten', count: this._labOrderCount ?? null }] : []),
              // Bètafunctie Rapportage (GeoDesk + aangezet).
              ...(this._reportAllowed ? [{ id: 'rapportage', label: 'Rapportage', count: null, beta: true }] : []),
              { id: 'export',        label: 'Export',        count: null },
    ];
  }

  /** Mag dit tabblad hier bestaan? Pas te beslissen zodra het bedrijfsprofiel er is. */
  _tabAllowed(tab) {
    if (tab === 'vooronderzoek' || tab === 'opdracht') return this._vooronderzoekAllowed;
    if (tab === 'rapportage') return this._reportAllowed;
    if (tab === 'labopdrachten') return this._companyLicense === 'geodesk';
    return true;
  }

  get _allBoringen() {
    const result = [];
    const selectedIds = new Set(this.selectedProjectIds || []);
    for (const b of (this.projectBorings || [])) {
      if (!selectedIds.has(b.projectId)) continue;
      const bm = BoringModel.fromFlat(b);
      result.push({
        guid: bm.guid,
        name: bm.name,
        type: bm.type,
        boormeester: bm.boormeester,
        gws: bm.gws,
        datum: bm.date,
        maxDepth: bm.maxDepth,
        lagenCount: bm.layers.length,
        layers: bm.layers,
        monsters: bm.monsters,
        peilbuizenCount: (b.peilbuizen || []).length,
        projectId: b.projectId,
        data: b,
      });
    }
    return result;
  }

  /** Boringen omgezet naar kaartpunten. `boring.x/y` worden — net als elders in de app —
   *  opgeslagen als **WGS84** (x=lengtegraad, y=breedtegraad); Leaflet gebruikt die direct.
   *  Voor de popup rekenen we WGS84 → het (deel)projectsysteem (default RD) via
   *  `convertFromWgs84`, zoals de boring-vorm dat ook doet. Geen coords → 'zonder locatie'. */
  _mapBorings() {
    return this._allBoringen.map(b => {
      const raw = b.data || {};
      const sys = this._coordSystemFor(b.projectId);
      const datum = this._heightDatumFor(b.projectId);
      let lat = null, lng = null, coordX = null, coordY = null;
      const x = Number(raw.x), y = Number(raw.y);
      // WGS84: x=lon (−180..180), y=lat (−90..90). Guard tegen per ongeluk opgeslagen
      // niet-WGS84-waarden (bv. RD-meters) zodat we ze niet op de verkeerde plek potten.
      if (raw.x != null && raw.y != null && Number.isFinite(x) && Number.isFinite(y)
          && Math.abs(x) <= 180 && Math.abs(y) <= 90) {
        lng = x; lat = y;
        const proj = convertFromWgs84(lng, lat, sys);
        if (proj) { coordX = proj.x; coordY = proj.y; }
      }
      const rec = raw.recordingId ? (this.recordings || []).find(r => r.id === raw.recordingId) : null;
      const depthCm = b.maxDepth ? Math.round(b.maxDepth * 100) : 0;
      const std = this._standardForRow(b);
      // Mini-profiel voor de popup: laagkleuren proportioneel op diepte.
      const profile = (b.layers || [])
        .filter(l => (l.lowerDepth ?? 0) > (l.upperDepth ?? 0))
        .map(l => ({ color: l.colorHex || '#B5B5C3', from: l.upperDepth, to: l.lowerDepth, label: [this._soilTerm(l, std), l.kleurLabel].filter(Boolean).join(' · ') }));
      const dd = HEIGHT_DATUMS[datum] || {};
      const heightText = raw.z != null
        ? `${Number(raw.z).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m ${dd.suffix || ''}`.trim()
        : null;
      return {
        guid: b.guid, id: raw.id ?? null, name: b.name,
        depth: depthCm, depthCm,
        lagen: b.lagenCount,
        peilbuisName: (raw.peilbuizen && raw.peilbuizen[0] && raw.peilbuizen[0].name) || null,
        profile,
        lat, lng,
        coordSys: (COORDINATE_SYSTEMS[sys] || COORDINATE_SYSTEMS[DEFAULT_COORDINATE_SYSTEM]).id,
        coordX, coordY,
        heightText,
        status: rec && (rec.status === 'processed' || rec.status === 'complete') ? 'verwerkt' : null,
        // Deellocaties (koppeltabel boring_sublocations): de kaart toont en beheert de toewijzing.
        sublocationIds: raw.sublocationIds ?? [],
        // Het geplande punt waaraan deze boring expliciet hangt (null = niet, of alleen op naam).
        plannedPointId: raw.plannedPointId ?? null,
      };
    });
  }

  /**
   * Geplande punten en tekeningen ophalen. Mislukt dat (bv. een oude backend zonder deze
   * routes), dan blijft de kaart gewoon werken met alleen de boringen.
   */
  async _loadMapData() {
    if (!this.projectId) return;
    try {
      // Geplande boringen zijn GeoDesk; zonder blijft de kaart bij boringen, deellocaties en tekeningen.
      const [p, l] = await Promise.all([
        this._companyLicense === 'geodesk' ? api.getPlannedPoints(this.projectId) : Promise.resolve({ points: [] }),
        api.getMapLayers(this.projectId),
      ]);
      this._plannedPoints = p?.points || [];
      this._mapLayers = l?.layers || [];
    } catch (e) {
      console.warn('Kaartgegevens laden mislukt:', e.message);
      this._plannedPoints = this._plannedPoints || [];
      this._mapLayers = this._mapLayers || [];
    }
  }

  // ── Vooronderzoek: Veldwerkopdracht en Monsterplan als subtabbladen ──────────

  /** Het subtabblad van Vooronderzoek staat in de URL (?deel=monsterplan); standaard de veldwerkopdracht. */
  get _vooDeel() {
    try { return new URLSearchParams(window.location.search).get('deel') === 'monsterplan' ? 'monsterplan' : 'veldwerkopdracht'; }
    catch (_) { return 'veldwerkopdracht'; }
  }

  _kiesVooDeel(deel) {
    const path = `/projects/${this.projectId}/vooronderzoek${deel === 'monsterplan' ? '?deel=monsterplan' : ''}`;
    if (window.location.pathname + window.location.search !== path) {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    this.activeTab = 'vooronderzoek';
    this.requestUpdate();
  }

  _renderVooronderzoekTabs() {
    const deel = this._vooDeel;
    const knop = (id, label) => html`
      <button @click=${() => this._kiesVooDeel(id)}
        style="padding:6px 26px; border:none; border-radius:6px; cursor:pointer; font:inherit; font-size:0.82rem; font-weight:700; white-space:nowrap;
               background:${deel === id ? 'var(--mv-primary-tint,#e9f5e6)' : 'transparent'}; color:${deel === id ? 'var(--mv-primary)' : 'var(--mv-text-sub)'};">
        ${label}</button>`;
    // Twee even brede knoppen, zo smal als de langste; geen badges (de pagina zelf toont de stand).
    // width:max-content + align-self: anders rekt de omringende flex-kolom de balk op tot paginabreedte.
    return html`
      <div style="display:inline-grid; grid-auto-flow:column; grid-auto-columns:1fr; width:max-content; max-width:100%; align-self:flex-start; justify-self:start; gap:2px; padding:3px; margin-bottom:0.9rem; border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface);">
        ${knop('veldwerkopdracht', 'Veldwerkopdracht')}
        ${knop('monsterplan', 'Monsterplan')}
      </div>`;
  }

  /** Een PDF/afbeelding opnieuw uitlijnen: de importdialoog in de uitlijnstap, met de bestaande paspunten. */
  async _realignMapLayer(layer) {
    this._mapImportOpen = true;
    await this.updateComplete;
    await this.renderRoot.querySelector('map-import-dialog')?.realign(layer);
  }

  /** Naar Vooronderzoek en daar de import van het monsterplan openen (boorpunten, deellocaties). */
  async _openMonsterplanImport() {
    this._mapImportOpen = false;
    this._kiesVooDeel('monsterplan');
    await this.updateComplete;
    this.renderRoot.querySelector('vooronderzoek-panel')?._openImportPopup();
  }

  /** Importdialoog openen; met `files` (een PDF uit het vooronderzoek) meteen dat bestand inlezen. */
  async _openMapImport(files) {
    this._mapImportOpen = true;
    if (!files?.length) return;
    await this.updateComplete;
    await this.renderRoot.querySelector('map-import-dialog')?._onFiles(files);
  }

  async _onMapImported() {
    // Het resultaat staat meteen op de kaart; een aparte melding voegt daar niets aan toe.
    await this._loadMapData();
    this._activitySignal = (this._activitySignal || 0) + 1;
  }

  async _updatePlannedPoint({ id, data }) {
    const prev = this._plannedPoints;
    this._plannedPoints = (prev || []).map(p => (p.id === id ? { ...p, ...data } : p));
    try { await api.updatePlannedPoint(id, data); await this._loadMapData(); }
    catch (err) { this._plannedPoints = prev; showAlert('Punt verplaatsen mislukt: ' + err.message); }
  }

  /**
   * Een bestaande boring aanwijzen als de uitvoering van een gepland punt (kaart: 'Vervangen
   * door boring'). Hing het punt expliciet aan een andere boring, dan gaat die koppeling eraf;
   * anders zouden er twee boringen hetzelfde punt claimen en telt de oudste.
   */
  async _linkPlannedPoint({ pointId, pointName, boringId, previousBoringId }) {
    if (!pointId || !boringId || boringId === previousBoringId) return;
    const kaart = this._mapBorings();
    const vorige = previousBoringId ? kaart.find((b) => b.id === previousBoringId) : null;
    try {
      if (vorige && vorige.plannedPointId === pointId) await api.updateBoring(previousBoringId, { plannedPointId: null });
      await api.updateBoring(boringId, { plannedPointId: pointId });
      await Promise.all([this._loadBorings(), this._loadMapData(), loadPlannedPoints(this.projectId, { refresh: true })]);
    } catch (err) {
      showAlert(`Koppelen aan ${pointName || 'het geplande punt'} mislukt: ${err.message}`);
    }
  }

  async _deletePlannedPoint({ id, name }) {
    const ok = await confirmDialog({
      title: 'Gepland punt verwijderen?',
      message: `${name || 'Dit punt'} verdwijnt van de kaart, ook in de veldapp. Een boring die al is gezet blijft staan.`,
      confirmLabel: 'Verwijderen', danger: true,
    });
    if (!ok) return;
    try { await api.deletePlannedPoint(id); await this._loadMapData(); }
    catch (err) { showAlert('Verwijderen mislukt: ' + err.message); }
  }

  async _updateMapLayer({ id, data }) {
    const prev = this._mapLayers;
    this._mapLayers = (prev || []).map(l => (l.id === id ? { ...l, ...data } : l));
    try { await api.updateMapLayer(id, data); }
    catch (err) { this._mapLayers = prev; showAlert('Tekening bijwerken mislukt: ' + err.message); }
  }

  async _deleteMapLayer({ id, name }) {
    const fromLayer = (this._plannedPoints || []).filter(p => p.sourceLayerId === id).length;
    const ok = await confirmDialog({
      title: 'Tekening verwijderen?',
      message: `'${name}' verdwijnt van de kaart, ook in de veldapp.${fromLayer ? ` De ${fromLayer} geplande punten uit deze tekening gaan mee.` : ''}`,
      confirmLabel: 'Verwijderen', danger: true,
    });
    if (!ok) return;
    try { await api.deleteMapLayer(id); await this._loadMapData(); }
    catch (err) { showAlert('Verwijderen mislukt: ' + err.message); }
  }

  /** "Open boring →" uit de kaartpopup: naar de Boringen-tab en de bijbehorende rij openklappen. */
  /** Kaart: gebruiker plaatste een locatie voor een boring zonder coördinaten. `x`/`y` zijn
   *  WGS84 (lengte/breedtegraad), net als elders in de app. Opslaan + herladen. */
  _setBoringLocation({ guid, x, y }) {
    const boring = (this.projectBorings || []).find(b => BoringModel.fromFlat(b).guid === guid);
    if (!boring || !boring.id) return;
    this._optimistic(
      (list) => list.map(b => (b === boring ? { ...b, x, y } : b)),
      () => api.updateBoring(boring.id, { x, y }),
      'Locatie opslaan mislukt',
    );
  }

  /**
   * Optionele kolommen voor de boringenlijst. Uit te breiden zonder aan de tabel te komen:
   * `render` bepaalt de inhoud, de rest regelt mv-table.
   */
  static BORING_COLUMN_OPTIONS = [
    { id: 'name',       label: 'Naam' },
    // Alleen te kiezen als het project deellocaties heeft (zie _boringColumnOptions). Standaard
    // uit, dus geen nieuwe voorkeursleutel nodig: wie hem nooit aanzette ziet niets veranderen.
    { id: 'sublocation', label: 'Deellocatie', default: false },
    { id: 'profiel',    label: 'Profiel' },
    { id: 'details',    label: 'Details' },
    { id: 'created',    label: 'Datum/tijd aangemaakt' },
    { id: 'boringType', label: 'Type boring' },
    // Standaard uit; aan te zetten via het tandwiel en, net als de rest, in de tabel te wijzigen.
    { id: 'boormeester', label: 'Boormeester', default: false },
    { id: 'groundLevelType', label: 'Maaiveldtype', default: false },
    // Standaard UIT: "Verwerkt" staat bij vrijwel elke rij, want vrijwel elke opname haalt
    // het. Wie wil zien hoe de verwerking ervoor staat zet 'm aan; voor de rest is het een
    // kolom die overal hetzelfde zegt.
    { id: 'aiStatus',   label: 'Status AI', default: false },
  ];

  /** Standaard aan, tenzij een kolom zichzelf op `default: false` zet. */
  static DEFAULT_BORING_COLUMNS = this.BORING_COLUMN_OPTIONS
    .filter((c) => c.default !== false)
    .map((c) => c.id);

  /**
   * Sleutel van de voorkeur; met een punt genest zodat latere tabellen ernaast passen.
   *
   * De `.v2` was nodig omdat de eerste versie iets ANDERS betekende: toen stond er de lijst
   * met EXTRA kolommen bovenop een vaste basis, nu de lijst met ALLE zichtbare kolommen.
   * Wie destijds de enige extra kolom uitvinkte had `[]` opgeslagen, en dat leest nu als
   * 'verberg alles' -- een lege tabel. Bij een betekeniswissel hoort een nieuwe sleutel.
   *
   * En `.v3` omdat 'Type boring' en 'Status AI' erbij kwamen. Een v2-lijst noemt die twee
   * niet, en omdat we onbekende ids wegfilteren zou 'Type boring' bij iedereen die ooit iets
   * aan de kolommen veranderde UIT staan -- precies andersom dan bedoeld. Een nieuwe sleutel
   * geeft iedereen de nieuwe standaard; de prijs is dat een eerdere kolomkeuze eenmalig
   * terugvalt op die standaard.
   */
  static BORING_COLUMNS_PREF = 'columns.projectBorings.v3';

  /**
   * Extra kolommen in de peilbuistabel. De zeven basiskolommen staan er altijd -- die
   * beantwoorden de vraag 'wat staat er waar en wat mat je bij plaatsing'. De rest is
   * detail dat je zelden allemaal tegelijk wilt zien, dus dat zet je er zelf bij.
   */
  static PEILBUIS_COLUMN_OPTIONS = [
    { id: 'referencePointType', label: 'Referentievlak' },
    { id: 'material', label: 'Materiaal' },
    { id: 'innerDiameterMm', label: 'Binnendiameter' },
    { id: 'diameter', label: 'Buitendiameter' },
    { id: 'filterSieveUsed', label: 'Filterkous' },
    { id: 'unwatered', label: 'Leeggezogen' },
    { id: 'rinsed', label: 'Gespoeld' },
    { id: 'flowType', label: 'Doorloop' },
    { id: 'ecWorkWater', label: 'EGV werkwater' },
    { id: 'waterUsageL', label: 'Werkwater (l)' },
    { id: 'remark', label: 'Opmerking' },
  ];

  static PEILBUIS_COLUMNS_PREF = 'columns.projectPeilbuizen';

  /** Haal de kolomkeuze van deze gebruiker op. Faalt dit, dan tonen we simpelweg de standaard. */
  async _loadColumnPrefs() {
    try {
      const prefs = await api.getPreferences();
      const saved = prefs?.[ProjectDetail.BORING_COLUMNS_PREF];
      // Een LEGE lijst is een geldige keuze (alles uitgevinkt) en mag niet als 'niets
      // ingesteld' worden gelezen — vandaar de check op het type, niet op de lengte.
      // Onbekende ids negeren: hernoemen we later een kolom, dan verdwijnt die uit de
      // voorkeur in plaats van als spookkolom te blijven hangen.
      if (Array.isArray(saved)) {
        const known = new Set(ProjectDetail.BORING_COLUMN_OPTIONS.map((c) => c.id));
        this._visibleColumns = saved.filter((id) => known.has(id));
      }
      const pb = prefs?.[ProjectDetail.PEILBUIS_COLUMNS_PREF];
      if (Array.isArray(pb)) {
        const known = new Set(ProjectDetail.PEILBUIS_COLUMN_OPTIONS.map((c) => c.id));
        this._peilbuisColumns = pb.filter((id) => known.has(id));
      }
    } catch (_) { /* voorkeuren zijn comfort, geen voorwaarde */ }
  }

  async _savePeilbuisColumns(visible) {
    this._peilbuisColumns = visible;
    try { await api.setPreference(ProjectDetail.PEILBUIS_COLUMNS_PREF, visible); }
    catch (e) { console.error('kolomvoorkeur opslaan mislukt:', e); }
  }

  /** Bewaar direct bij het aanvinken — een aparte opslaan-knop zou hier alleen in de weg zitten. */
  async _saveColumnPrefs(visible) {
    // In een project zonder deellocaties staat 'Deellocatie' niet in de kiezer; een andere
    // kolom aan- of uitzetten mag hem dan niet stilletjes uit de voorkeur halen.
    if (!this._sublocations.length && this._visibleColumns.includes('sublocation') && !visible.includes('sublocation')) {
      visible = [...visible, 'sublocation'];
    }
    this._visibleColumns = visible;
    try {
      await api.setPreference(ProjectDetail.BORING_COLUMNS_PREF, visible);
    } catch (e) {
      console.error('kolomvoorkeur opslaan mislukt:', e);
    }
  }

  _openBoringFromMap(guid) {
    this.activeTab = 'borings';
    const boring = (this.projectBorings || []).find(b => BoringModel.fromFlat(b).guid === guid);
    if (boring) {
      const row = boring.recordingId ? (this.recordings || []).find(r => r.id === boring.recordingId) : boring;
      if (row) row.expanded = true; // mv-table leest row.expanded
    }
    this.requestUpdate();
    this.updateComplete.then(() => {
      const el = this.renderRoot.querySelector('mv-table')?.renderRoot?.querySelector('tr.data-row.expanded');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  get _exportTotals() {
    const selectedBoringen = this.selectedBoringGuids;
    
    let meetpunten = 0;
    let lagen = 0;
    let monsters = 0;
    let peilbuizen = 0;

    const allB = this._allBoringen;

    for (const b of allB) {
      if (selectedBoringen && selectedBoringen.size > 0 && b.guid && !selectedBoringen.has(b.guid)) continue;

      meetpunten++;
      lagen += b.layers.length;
      monsters += b.monsters.length;
      peilbuizen += b.peilbuizenCount || 0;
    }

    return { meetpunten, lagen, monsters, peilbuizen };
  }

  _handleFilterChange() {
    this.generateAndPreviewXML();
  }

  toggleBoringSelection(guid) {
    if (!guid) return;
    if (this.selectedBoringGuids.has(guid)) {
      this.selectedBoringGuids.delete(guid);
    } else {
      this.selectedBoringGuids.add(guid);
    }
    this.requestUpdate();
    this._scheduleXmlPreview();
  }

  toggleBoringAccordion(guid, event) {
    if (!guid) return;
    if (event) {
       event.stopPropagation();
    }
    if (this.expandedBoringGuids.has(guid)) {
      this.expandedBoringGuids.delete(guid);
    } else {
      this.expandedBoringGuids.add(guid);
    }
    this.requestUpdate();
  }

  toggleAllBoringen() {
    const all = this._allBoringen.filter(b => b.guid);
    if (this.selectedBoringGuids.size === all.length && all.length > 0) {
       this.selectedBoringGuids.clear();
    } else {
       all.forEach(b => this.selectedBoringGuids.add(b.guid));
    }
    this.requestUpdate();
    this._scheduleXmlPreview();
  }

  toggleProjectSelection(id) {
    if (this.selectedProjectIds.includes(id)) {
      this.selectedProjectIds = this.selectedProjectIds.filter(pid => pid !== id);
    } else {
      this.selectedProjectIds = [...this.selectedProjectIds, id];
    }
    this._handleFilterChange();
  }

  // ─── Column definitions ──────────────────────────────────────────────────

  /** Subproject name a recording/boring belongs to, or null for the parent's own. */
  _subprojectName(projectId) {
    if (!projectId || projectId === this.projectId) return null;
    const dp = (this.deelprojects || []).find(p => p.id === projectId);
    return dp ? (dp.project_name || dp.name || dp.project_code || 'Deelproject') : null;
  }

  /** Badge marking a recording/boring as belonging to a subproject (hoofdproject view). */
  _subprojectBadge(name) {
    if (!name) return '';
    return html`<span title="Onderdeel van deelproject: ${name}"
      style="display:inline-flex; align-items:center; gap:4px; max-width:100%; font-size:0.68rem; font-weight:700; color:var(--mv-primary); background:rgba(16,185,129,0.1); border:1px solid rgba(16,185,129,0.28); padding:2px 8px; border-radius:10px; white-space:nowrap; overflow:hidden;">
      ${unsafeSVG(lucideIcon(Folder, 11))}<span style="overflow:hidden; text-overflow:ellipsis;">${name}</span>
    </span>`;
  }

  get recordingColumns() {
    return [
      {
        id: 'filename',
        title: 'Opname',
        width: '220px',
        hideOnMobile: true,
        render: (row) => html`
          <div class="memo-name" style="font-size:0.82rem; color:var(--mv-text-muted, #7E8299);">${row.filename || `Opname ${row.id.slice(0, 8)}`}</div>
          <div class="memo-date" style="font-size:0.75rem; color:var(--mv-text-muted, #A1A5B7);">${this.formatDate(row.created_at)}</div>
          ${row.subprojectName ? html`<div style="margin-top:4px;">${this._subprojectBadge(row.subprojectName)}</div>` : ''}
        `
      },
      {
        id: 'results',
        title: 'Resultaten',
        render: (row) => {
          const rowWarnings = row.status === 'processed' ? validateRecordingData(row) : [];

          if (row.status === 'processed') {
            const recordingBorings = (this.projectBorings || []).filter(b => b.recordingId === row.id);
            for (const b of recordingBorings) {
              rowWarnings.push(...this._getBoringWarnings(b));
            }
          }

          return html`
            <div style="display:flex; flex-direction:column; gap:6px;">
              ${this._getMemoSummary(row)}
              ${this._renderWarningBadges(rowWarnings)}
            </div>
          `;
        }
      },
      {
        id: 'actions',
        title: '',
        width: '240px',
        render: (row) => {
          const isProcessing = ['transcribing', 'processing', 'syncing'].includes(row.status);
          let statusBadge;

          if (isProcessing) {
            const perc = row.progress || 0;
            statusBadge = html`
              <div style="display:flex; flex-direction:column; align-items:flex-end; gap:4px; min-width:120px;">
                <style>
                  @keyframes rotate { 100% { transform: rotate(360deg); } }
                  .spin-icon svg { animation: rotate 2s linear infinite; }
                </style>
                <div style="display:flex; align-items:center; gap:6px; font-size:0.75rem; font-weight:700; color:#FFA800;">
                  <span class="spin-icon">${unsafeSVG(lucideIcon(Loader2, 12))}</span>
                  ${this.getStatusLabel(row.status)} ${perc}%
                </div>
                <div style="width:100%; height:4px; background:var(--mv-warning-panel-bg, #FFF4DE); border-radius:4px; overflow:hidden;">
                  <div style="height:100%; background:#FFA800; border-radius:4px; width:${perc}%; transition:width 0.3s ease;"></div>
                </div>
              </div>
            `;
          } else if (row.status === 'processed' || row.status === 'complete') {
            statusBadge = html`
              <div style="display:flex; align-items:center; gap:6px; font-size:0.8rem; font-weight:700; color:var(--mv-primary); background:var(--mv-primary-tint); padding:4px 10px; border-radius:6px;">
                ${unsafeSVG(lucideIcon(CheckCircle2, 14))} <span class="status-badge-label">Verwerkt</span>
              </div>
            `;
          } else if (row.status === 'error') {
            statusBadge = html`
              <div style="display:flex; align-items:center; gap:6px; font-size:0.8rem; font-weight:700; color:var(--mv-danger); background:var(--mv-danger-bg); padding:4px 10px; border-radius:6px;" title="${row.error || 'Fout opgetreden'}">
                ${unsafeSVG(lucideIcon(XCircle, 14))} <span class="status-badge-label">Fout</span>
              </div>
            `;
          } else {
            statusBadge = html`
              <div style="display:flex; align-items:center; gap:6px; font-size:0.8rem; font-weight:600; color:var(--mv-text-sub); background:var(--mv-surface-alt); padding:4px 10px; border-radius:6px;">
                ${unsafeSVG(lucideIcon(Clock, 14))} <span class="status-badge-label">Wachten</span>
              </div>
            `;
          }

          return html`
            <div style="display:flex; align-items:center; gap:16px; justify-content:flex-end;">
              ${statusBadge}
              <button class="action-btn delete"
                @click=${(e) => { e.stopPropagation(); this.deleteRecording(row); }}
                title="Verwijderen" style="flex-shrink:0;">
                ${unsafeSVG(lucideIcon(Trash2, 16))}
              </button>
            </div>
          `;
        }
      }
    ];
  }

  // ─── Constructor & lifecycle ─────────────────────────────────────────────

  constructor() {
    super();
    // Laatst bekende licentie en bètafuncties: de tabbladen staan er meteen, ook in het skelet.
    { const c = profielUitOpslag(); if (c?.license) { this._companyLicense = c.license; this._betaFeatures = c.betaFeatures || []; } }
    this._visibleColumns = ProjectDetail.DEFAULT_BORING_COLUMNS;
    this._sublocations = [];
    this._peilbuisColumns = [];   // extra kolommen staan standaard uit
    this.project = null;
    this.recordings = [];
    this.aggregatedRecordings = [];
    this.loading = false;
    this.transcribing = false;
    this.processing = false;
    this.reprocessing = false;
    this.showReprocessWarning = false;
    this._reprocessWarning = null;
    this.syncing = false;
    this.showUploadModal = false;
    this._inspreekOpen = false;
    this._nieuwKeuzeOpen = false;
    this._autoEditRowId = null;
    this.showDeleteModal = false;
    this.showDeleteRecordingModal = false;
    this._deleteRecordingTarget = null;
    this.error = null;
    // XML export
    this.xmlContent = '';
    this.exportGenerating = false;
    this.exportWarnings = [];
    this.exportError = null;
    this._exportFormat = 'xml';
    this._exportModalOpen = false;
    this._exportMenuOpen = false;
    this._exportFotos = false;
    this._exportVoortgang = '';
    this._fotoTelling = null;
    this._companyLogo = null; // data-URI uit het bedrijfsprofiel, voor de PDF-exports
    this._pdfPages = [];
    this._pdfPage = 0;
    this._pdfBusy = false;
    this._pdfDirty = false;
    this._pdfError = null;
    this._pdfPagesFor = null; // voor welk formaat de huidige _pdfPages gerenderd zijn
    // Progress overlay
    this.showLoader = false;
    this.progressData = null;
    this.currentMessage = '';
    // WebSocket
    this._ws = null;
    // Pagination
    this.recordingsPage = 1;
    this.recordingsPageSize = 10;
    this.recordingsTotal = 0;
    // Filtering
    this.showFilterModal = false;
    this.exportFormat = 'xml';
    this.selectedMeetpuntIds = [];
    this.selectedProjectIds = [];
    this.selectedBoringGuids = new Set();
    this.expandedBoringGuids = new Set();
    this.activeTab = 'overzicht';
    this.parentProject = null;
    this.deelprojects = [];
    this._templates = [];
    this._memberNames = [];
    this._currentUserName = '';
    this.templateValue = '';
    this.projectBorings = [];
    this._duplicateSource = null;   // boring die gedupliceerd wordt; null = venster dicht
    this.showBoringModal = false;
    this._createFormVal = {};
    this._createFormSaving = false;
    this._inlineFormVals = {};
    this._loadingRecordings = true;
    this._loadingBorings = true;
    this._recordingsSort = 'created';
    this._recordingsQuery = '';
    this._boringsSort = 'created';
    this._boringsQuery = '';
    this.showCreateModal = false;
    this.projectNameValue = '';
    this.projectCodeValue = '';
    this.instructionsValue = '';
    this.createError = null;
    this.isCreating = false;
  }

  connectedCallback() {
    super.connectedCallback();
    // Een deellocatie aangemaakt, hernoemd of weggegooid (kaart, monsterplan): de kolom volgt.
    this._onSublocations = (e) => { if (e.detail?.projectId === this.projectId) this._sublocations = e.detail.sublocations || []; };
    window.addEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    this.loadProject();
    this._loadMembers();
    this._loadColumnPrefs();
    this._onEsc = (e) => {
      if (e.key !== 'Escape') return;
      // Close whichever modal is open (topmost-first).
      if (this._monstersOpen) this._monstersOpen = false;
      else if (this._peilbuizenOpen) this._peilbuizenOpen = false;
      else if (this.showCreateModal) this.showCreateModal = false;
      else if (this.showBoringModal) this.showBoringModal = false;
      else if (this.showFilterModal) this.showFilterModal = false;
      else if (this.showUploadModal) this.showUploadModal = false;
      else if (this.showDeleteModal) this.showDeleteModal = false;
      else if (this.showDeleteRecordingModal) { this.showDeleteRecordingModal = false; this._deleteRecordingTarget = null; }
    };
    document.addEventListener('keydown', this._onEsc);
    // Klik ergens anders → menu dicht; de knop zelf stopt de bubbling.
    this._closeProjectMenu = () => { if (this._projectMenuOpen) this._projectMenuOpen = false; };
    document.addEventListener('click', this._closeProjectMenu);
  }

  updated(changedProperties) {
    // Een bètatabblad via een link of de adresbalk, bij een bedrijf waar hij niet aanstaat:
    // terug naar het overzicht in plaats van een leeg scherm. Pas als het profiel er is.
    // Pas met het echte profiel: een verouderde bewaarde waarde mag je niet wegsturen.
    if (this._profielEcht && !this._tabAllowed(this.activeTab)) this._goToTab('overzicht');
    // Opdracht is opgegaan in Vooronderzoek: een oude link /opdracht wordt Vooronderzoek → Veldwerkopdracht.
    if (this.activeTab === 'opdracht' && this.projectId) this._kiesVooDeel('veldwerkopdracht');
    if (changedProperties.has('projectId') && this.projectId) {
      this.resetState();
      this.loadProject();
      this._focusDone = null;   // ander project = de vorige diepe link telt niet meer
    }
    // Een NIEUWE link moet altijd uitgevoerd worden, ook naar dezelfde boring: klik je in de
    // zoekbalk twee keer op hetzelfde resultaat, dan verwacht je dat hij weer openspringt.
    if (changedProperties.has('deepLink')) this._focusDone = null;
    // Elke render proberen: de boringen komen asynchroon binnen, dus de eerste poging
    // valt meestal in het niets.
    this._applyFocus();
  }

  resetState() {
    this.project = null;
    this.recordings = [];
    this.deelprojects = [];
    this.parentProject = null;
    this.error = null;
    this.loading = true;
    this._loadingRecordings = true;
    this._loadingBorings = true;
  }

  /** De deellocaties van het project; een fout is geen reden om de pagina te laten haperen. */
  _loadProjectSublocations() {
    const id = this.projectId;
    loadSublocations(id).then((list) => { if (this.projectId === id) this._sublocations = list; }).catch(() => {});
  }

  /** Kolomkeuze van de boringenlijst: 'Deellocatie' alleen als het project er heeft. */
  get _boringColumnOptions() {
    return ProjectDetail.BORING_COLUMN_OPTIONS.filter((c) => c.id !== 'sublocation' || this._sublocations.length);
  }

  /** De deellocaties van een rij (boring, of de boring van een opname), op naam. */
  _rowSublocations(row) {
    const boring = row._kind === 'boring' ? row : (this.projectBorings || []).find((b) => b.recordingId === row.id);
    const ids = boring?.sublocationIds || [];
    return this._sublocations.filter((s) => ids.includes(s.id));
  }

  disconnectedCallback() {
    window.removeEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    if (this._cellOutside) document.removeEventListener('mousedown', this._cellOutside, true);
    document.removeEventListener('click', this._closeProjectMenu);
    super.disconnectedCallback();
    this.stopEvents();
    // Een geplande herlaadactie hoort niet af te gaan nadat het scherm weg is.
    clearTimeout(this._boringsTimer);
    document.removeEventListener('keydown', this._onEsc);
  }

  // ─── Data loading ────────────────────────────────────────────────────────

  async loadProject() {
    this.loading = true;
    this._loadingRecordings = true;
    this.error = null;
    try {
      // Load project metadata + all projects in parallel — both needed for header/breadcrumb
      const [project, allProjects] = await Promise.all([
        api.getProject(this.projectId),
        api.getProjects(),
      ]);
      this.project = project;
      // Komt mee met het project zelf (zie ProjectRepository.findByIdWithCounts); de lijst in
      // het tabblad werkt hem later bij als je er een aanmaakt of weggooit.
      this._labOrderCount = Number(project?.lab_order_count) || 0;
      this._loadProjectSublocations();

      // Resolve deelprojects and parent from the already-fetched list
      const currentId = String(this.projectId).toLowerCase();
      if (Array.isArray(allProjects)) {
        const deelprojectSummaries = allProjects.filter(p => p.parent_id && String(p.parent_id).toLowerCase() === currentId);
        const fullDeelprojects = await Promise.all(deelprojectSummaries.map(dp => api.getProject(dp.id)));
        // Samenvatting ONDER het detail houden: alleen de lijst-query levert boring_count, en
        // het detail overschreef dat veld met niets -- vandaar 0 boringen bij elk deelproject.
        this.deelprojects = fullDeelprojects.map((full, i) => ({ ...deelprojectSummaries[i], ...full }));
        if (project.parent_id) {
          const pid = String(project.parent_id).toLowerCase();
          this.parentProject = allProjects.find(p => String(p.id).toLowerCase() === pid);
        }
      }

      // Sjabloon-defaults laden (voor o.a. olie-water/geur verbergen bij bewerken).
      this._loadTemplateDefaults();

      // Show the page header immediately — content will fill in below
      this.loading = false;

      // Load recordings and borings in parallel
      await Promise.all([
        this.loadRecordings(),
        this._loadBorings(),
        this._loadMapData(),
      ]);

      // Auto-select all projects and boringen for the export tab
      this.selectedProjectIds = [this.projectId, ...this.deelprojects.map(p => p.id)];
      this.selectedBoringGuids.clear();
      this._allBoringen.forEach(b => { if (b.guid) this.selectedBoringGuids.add(b.guid); });

      this.triggerXmlGenerationIfNeeded();
    } catch (error) {
      console.error('Failed to load project details:', error);
      this.error = `Fout bij laden project: ${error.message}`;
    } finally {
      this.loading = false;
      this._loadingRecordings = false;
      this._loadingBorings = false;
    }
  }

  /**
   * Optimistic update helper: applies `applyFn` to `this.projectBorings` immediately,
   * fires `apiFn()` in the background, reverts + sets error on failure.
   */
  _optimistic(applyFn, apiFn, errorMsg = 'Opslaan mislukt') {
    const prev = this.projectBorings;
    this.projectBorings = applyFn([...(this.projectBorings || [])]);
    apiFn()
      .then(() => this._loadBorings())
      .catch(e => {
        this.projectBorings = prev;
        /*
         * Een popup, en niet alleen de foutregel bovenaan het scherm.
         *
         * Bij een optimistische update is de wijziging al in beeld geweest en meteen weer
         * teruggedraaid. Zie je daar niets over -- en die foutregel staat bovenaan, terwijl je
         * onderin een boring zit te bewerken -- dan lijkt het alsof er gewoon iets is
         * opgeslagen. Dat bleek precies te gebeuren in een meekijksessie, waar élke
         * schrijfactie een 403 oplevert.
         *
         * Zelfde gedrag als de tweelinghelper in recording-row-detail, die dit al deed.
         */
        showAlert(`${errorMsg}: ${e.message}`, 'Niet opgeslagen');
        this._loadBorings();
      });
  }

  /**
   * Boringen herladen, maar hoogstens één keer per vlaag meldingen.
   *
   * `_loadBorings` haalt alle boringen van dit project én al zijn deelprojecten op, elk met hun
   * lagen, monsters, peilbuizen en foto's. Eén keer na een bewerking is dat prima. Tien keer
   * omdat er tien opnamen tegelijk klaar zijn is het niet: dan vraagt één openstaand tabblad
   * tien keer dezelfde lijst op, precies op het moment dat de backend die opnamen zit te
   * verwerken.
   *
   * Alleen voor meldingen van de hub. Na een handeling van de gebruiker zelf blijft het directe
   * `_loadBorings()` staan -- daar hoort de lijst meteen te kloppen, zonder wachttijd.
   */
  _scheduleLoadBorings() {
    clearTimeout(this._boringsTimer);
    this._boringsTimer = setTimeout(() => { this._loadBorings(); }, 600);
  }

  async _loadBorings() {
    // Build a map of existing boring objects by ID so we can mutate them in place.
    // recording-row-detail holds references to these objects, so mutating avoids
    // passing a new .borings value → no re-render → no layout shift.
    const existingById = new Map((this.projectBorings || []).map(b => [b.id, b]));
    // Only show loading state on first load (no existing borings) to avoid
    // flickering the skeleton on every borings-changed refresh.
    const showSkeleton = existingById.size === 0;
    if (showSkeleton) this._loadingBorings = true;
    try {
      const projectIds = [this.projectId, ...(this.deelprojects || []).map(p => p.id)];
      // Eén keer per scherm: de licentie bepaalt of de tab Labopdrachten iets te doen heeft.
      // Bewust niet in de Promise.all hieronder: het scherm hoeft er niet op te wachten.
      // De bewaarde waarden (profielUitOpslag) tonen de tabbladen al; dit ververst ze.
      if (!this._profielGeladen) {
        this._profielGeladen = true;
        api.getCompanyProfile()
          .then(p => { this._companyLicense = p?.license || 'geovoice'; this._betaFeatures = p?.betaFeatures || []; this._companyLogo = p?.logo || null; this._profielEcht = true; profielOnthouden(p); })
          .catch(() => { this._companyLicense = 'geovoice'; this._betaFeatures = []; this._profielEcht = true; });
      }
      const allBorings = (await Promise.all(
        projectIds.map(pid => api.getProjectBorings(pid).then(r => r?.borings ?? []))
      )).flat();
      // Filter out soft-deleted borings — clients use deletedAt to track removals,
      // but we only display active ones.
      const activeBorings = allBorings.filter(b => !b.deletedAt);

      // Mutate existing objects in place; only add/remove entries that changed
      const merged = activeBorings.map(fresh => {
        const existing = existingById.get(fresh.id);
        if (existing) {
          Object.assign(existing, fresh); // update data, preserve expanded/UI state
          return existing;
        }
        return fresh;
      });
      this.projectBorings = merged;
      // Direct proberen: een diepe link wacht op deze lijst, en vertrouwen op de volgende
      // render-cyclus maakte het afhankelijk van toevallige volgorde.
      this._applyFocus();
    } catch (e) {
      console.error('Failed to load borings:', e);
      this.projectBorings = [];
    } finally {
      if (showSkeleton) this._loadingBorings = false;
    }
  }

  _mapRecording(r, projectId = this.projectId, subprojectName = null) {
    if (!r || r.deleted_at) return null;
    return {
      id: r.id,
      recordingId: r.id,
      filename: r.filename,
      type: r.file_type,
      status: r.status,
      transcription: r.transcription_text,
      result_data: r.result_data || null,
      soil_standard: r.soil_standard || null,
      progress: r.progress_percentage || 0,
      currentStage: r.current_stage || '',
      error: r.error_message,
      created_at: r.created_at,
      parent_recording_id: r.parent_recording_id || null,
      domain: r.domain || null, // doel van de opname (lagen/peilbuis/watermonster/afwerking)
      projectId,
      // Set when this recording belongs to a subproject (shown as a badge in the
      // hoofdproject view); null for the parent project's own recordings.
      subprojectName,
    };
  }

  // Group extra opnamen (children) under their primary recording: the primary gets an
  // `extraRecordings` array, children are removed from the top-level list. So one meting
  // shows as one row with all its audio + the combined transcript.
  _groupRecordings(rawList, projectId = this.projectId, subprojectName = null) {
    const mapped = (rawList || []).map(r => this._mapRecording(r, projectId, subprojectName)).filter(Boolean);
    const childrenByParent = {};
    for (const m of mapped) {
      if (m.parent_recording_id) {
        if (!childrenByParent[m.parent_recording_id]) childrenByParent[m.parent_recording_id] = [];
        childrenByParent[m.parent_recording_id].push(m);
      }
    }
    const primaries = mapped.filter(m => !m.parent_recording_id);
    for (const p of primaries) p.extraRecordings = childrenByParent[p.id] || [];
    return primaries;
  }

  async loadRecordings() {
    try {
      // A hoofdproject shows the recordings of itself AND all its deelprojecten
      // (mirrors the Boringen tab). Each recording keeps its own projectId and a
      // subprojectName label (null for the parent's own recordings).
      const sources = [
        { id: this.projectId, name: null },
        ...(this.deelprojects || []).map(p => ({ id: p.id, name: p.project_name || p.name || p.id })),
      ];
      const all = [];
      let total = 0;
      for (const src of sources) {
        try {
          const res = await api.getProjectRecordings(src.id);
          const recs = res.results || res.recordings || [];
          total += res.total || recs.length;
          all.push(...this._groupRecordings(recs, src.id, src.name));
        } catch (e) { console.warn('Failed to load recordings for', src.id, e); }
      }
      this.recordings = all;
      this.aggregatedRecordings = all;
      this.recordingsTotal = total;

      // Initial selection: main project and all subprojects
      if (this.selectedProjectIds.length === 0) {
        this.selectedProjectIds = sources.map(s => s.id);
      }

      this._loadingRecordings = false;
      this.startEvents();
    } catch (error) {
      console.error('Failed to load recordings:', error);
      this.recordings = [];
      this._loadingRecordings = false;
    }
  }

  // ─── WebSocket events ────────────────────────────────────────────────────

  startEvents() {
    // Skip if already connected or connecting
    if (this._ws && (this._ws.readyState === WebSocket.CONNECTING || this._ws.readyState === WebSocket.OPEN)) {
      console.log('[WS] already active (readyState:', this._ws.readyState, '), skipping reconnect');
      return;
    }
    this.stopEvents();
    let ws;
    try {
      ws = api.connectWebSocket(this.projectId);
      this._ws = ws;
    } catch (e) {
      console.warn('WebSocket unavailable, falling back to manual refresh:', e);
      return;
    }

    console.log('[WS] connecting to project', this.projectId);
    this._ws.onopen = () => console.log('[WS] connected');
    this._ws.onmessage = async (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      console.log('[WS] message received:', msg);

      if (msg.type === 'recording-added') {
        // Reload the full (parent + subprojects) recordings list so the new entry appears.
        try { await this.loadRecordings(); } catch { /* ignore */ }
        this.requestUpdate();
        return;
      }

      if (msg.type === 'recording-deleted') {
        this.recordings = this.recordings.filter(r => r.id !== msg.recordingId);
        this.recordings = [...this.recordings];
        this.xmlContent = '';
        this.exportWarnings = [];
        this.triggerXmlGenerationIfNeeded();
        this.requestUpdate();
        return;
      }

      // Geplande punten of tekeningen gewijzigd (import, verplaatst, aan/uit).
      if (msg.type === 'map-update') {
        this._loadMapData();
        return;
      }

      // Veldwerkopdracht goedgekeurd of gewijzigd (ook vanuit een ander venster).
      if (msg.type === 'field-order-update') {
        this._fieldOrderSignal = (this._fieldOrderSignal || 0) + 1;
        this.requestUpdate();
        return;
      }

      if (msg.type === 'boring-update') {
        this._scheduleLoadBorings();
        // Of een gepland punt 'uitgevoerd' is hangt van de boringen af.
        if ((this._plannedPoints || []).length) this._loadMapData();
        // Ook het activiteitenlog is nu verouderd (de tegel op Overzicht luistert hierop).
        this._activitySignal = (this._activitySignal || 0) + 1;
        return;
      }

      if (msg.type !== 'recording-update') return;

      const { recordingId, status } = msg;
      let idx = this.recordings.findIndex(r => r.id === recordingId);
      if (idx === -1) {
        // Recording not in list yet (race with recording-added) — reload full list
        try {
          await this.loadRecordings();
          this.requestUpdate();
        } catch { /* ignore */ }
        return;
      }

      const wasProcessed = this.recordings[idx].status !== 'processed' && status === 'processed';
      const wasTranscribed = this.recordings[idx].status !== 'transcribed' && status === 'transcribed';

      if (wasProcessed || wasTranscribed) {
        // Fetch full recording to get updated transcription_text / result_data
        try {
          const updated = await api.getRecording(recordingId);
          const mapped = this._mapRecording({ ...updated, status });
          if (mapped) Object.assign(this.recordings[idx], mapped);
        } catch {
          this.recordings[idx].status = status;
        }
        this.recordings = [...this.recordings];
        if (wasProcessed) {
          this.xmlContent = '';
          this.exportWarnings = [];
          this.triggerXmlGenerationIfNeeded();
          this._scheduleLoadBorings();
        }
      } else {
        // Status-only change (transcribing, error, manual edit notification) —
        // just update the status badge, no extra API call needed.
        this.recordings[idx].status = status;
        this.recordings = [...this.recordings];
      }

      this.requestUpdate();
    };

    this._ws.onerror = (e) => console.warn('[WS] error:', e);

    this._ws.onclose = (e) => {
      console.log('[WS] closed — code:', e.code, 'reason:', e.reason, 'wasClean:', e.wasClean);
      // Only reconnect if this specific connection is still the active one
      if (this._ws === ws) {
        this._ws = null;
        console.log('[WS] unexpected close, reconnecting in 3s...');
        setTimeout(() => this.startEvents(), 3000);
      } else {
        console.log('[WS] old socket closed (replaced), no reconnect');
      }
    };
  }

  stopEvents() {
    if (this._ws) {
      const ws = this._ws;
      this._ws = null; // clear first so onclose doesn't auto-reconnect
      ws.close();
    }
  }

  // ─── Upload & file handling ───────────────────────────────────────────────

  toggleUploadModal() { this.showUploadModal = !this.showUploadModal; }
  openUploadModal()   { this.showUploadModal = true; }
  closeUploadModal()  { this.showUploadModal = false; }
  closeDeleteModal()  { this.showDeleteModal = false; }

  handleDragOver(event)  { event.preventDefault(); event.currentTarget.classList.add('dragover'); }
  handleDragLeave(event) { event.preventDefault(); event.currentTarget.classList.remove('dragover'); }

  handleDrop(event) {
    event.preventDefault();
    event.currentTarget.classList.remove('dragover');
    this.uploadAdditionalFiles(Array.from(event.dataTransfer.files));
  }

  handleFileSelect(event) {
    this.uploadAdditionalFiles(Array.from(event.target.files));
  }

  async uploadAdditionalFiles(files, { locatie = null } = {}) {
    if (!files.length) return;

    const SUPPORTED_AUDIO = /\.(mp3|m4a|mp4|wav|webm|ogg|opus|flac|aac)$/i;
    const SUPPORTED_DOC   = /\.(txt|pdf|docx)$/i;
    const errors = [];
    const accepted = files.filter(file => {
      const isAudio = file.type.startsWith('audio/') || SUPPORTED_AUDIO.test(file.name);
      const isDoc   = file.type === 'text/plain' || SUPPORTED_DOC.test(file.name);
      if (!isAudio && !isDoc) {
        errors.push(`"${file.name}" wordt niet ondersteund.`);
        return false;
      }
      if (file.size > CONFIG.MAX_FILE_SIZE) {
        errors.push(`"${file.name}" is te groot (max ${CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB).`);
        return false;
      }
      return true;
    });

    if (errors.length) {
      alertDialog({
        title: 'Bestand niet ondersteund',
        message: `${errors.join('\n')}\n\nOndersteunde formaten:\nAudio: MP3, M4A, MP4, WAV, WEBM, OGG, OPUS\nDocument: TXT, PDF, DOCX`,
      });
    }

    if (!accepted.length) return;

    this.transcribing = true;
    this.error = null;
    try {
      // Documents (txt/pdf/docx) → extract their text in the browser and upload that
      // as the transcript; audio is uploaded as-is for server-side transcription.
      const toUpload = [];
      const docErrors = [];
      for (const file of accepted) {
        if (isExtractableDoc(file.name)) {
          let text = '';
          try { text = await extractDocumentText(file); }
          catch (e) { console.error('Tekst-extractie mislukt:', file.name, e); }
          if (!text || !text.trim()) { docErrors.push(`Geen tekst gevonden in "${file.name}".`); continue; }
          // Keep the original name so the user recognises it; content is the text.
          toUpload.push(new File([text], file.name, { type: 'text/plain' }));
        } else {
          toUpload.push(file);
        }
      }

      if (docErrors.length) {
        alertDialog({
          title: 'Geen tekst gevonden',
          message: `${docErrors.join('\n')}\n\nBevat het document leesbare tekst? Een gescande PDF (alleen afbeeldingen) heeft geen tekstlaag.`,
        });
      }
      if (!toUpload.length) return;

      // In porties en na elkaar, niet alles in één request: zie utils/upload-porties.js. Gaat
      // er halverwege een portie mis, dan staan de eerdere er al; die tonen we dan ook.
      const recordings = [];
      let gelukt = 0;
      try {
        for (const portie of inPorties(toUpload)) {
          const r = await api.uploadAdditionalFiles(this.projectId, portie, null, null, locatie);
          recordings.push(...(r?.recordings || []));
          gelukt += portie.length;
        }
      } catch (e) {
        if (gelukt) {
          await this.loadRecordings();
          throw new Error(`${gelukt} van ${toUpload.length} bestanden zijn geüpload; daarna ging het mis: ${e.message}`);
        }
        throw e;
      }
      const result = { recordings };

      // De backend slaat een upload over als er in dit project al een opname met DEZELFDE
      // BESTANDSNAAM staat, en antwoordt dan alsnog met 200 en `duplicate: true`. Dat is er
      // voor de veldapp, die een mislukte upload opnieuw aanbiedt en geen tweede boring mag
      // opleveren. In de backoffice kies je het bestand zelf, en dan las hetzelfde antwoord
      // als "gelukt": het venster sloot, de lijst laadde opnieuw, en er kwam niets bij.
      const dubbel = (result?.recordings || []).filter((r) => r.duplicate);
      if (dubbel.length === toUpload.length) {
        alertDialog({
          title: dubbel.length === 1 ? 'Deze opname staat er al' : 'Deze opnamen staan er al',
          message: this._duplicateMessage(dubbel),
        });
        return; // venster open laten: je bent nog niet klaar
      }
      if (dubbel.length) {
        alertDialog({ title: 'Een deel stond er al', message: this._duplicateMessage(dubbel) });
      }

      await this.loadRecordings();
      this.showUploadModal = false;
    } catch (error) {
      console.error('Upload failed:', error);
      this.error = `Upload gefaald: ${error.message}`;
    } finally {
      this.transcribing = false;
    }
  }

  /**
   * De boringen die uit een opname zijn voortgekomen, op naam.
   *
   * De bestandsnaam van een opname zegt de gebruiker niets: de veldapp verzint hem
   * ("memo_1787319877981.m4a") en na het verwerken zie je hem nergens meer. Wat hij wél kent is
   * de boring die eruit kwam. Die staat al in `projectBorings`, dus dit kost geen extra verzoek.
   */
  _boringNamesForRecording(recordingId) {
    return (this.projectBorings || [])
      .filter((b) => b.recordingId === recordingId)
      .map((b) => b.name)
      .filter(Boolean);
  }

  /**
   * De uitleg bij een overgeslagen upload.
   *
   * Noemt de boring waar dit bestand al toe verwerkt is, want dát is waar de gebruiker aan
   * denkt. Is die er niet -- de opname staat er wel maar is nog niet verwerkt, of het verwerken
   * mislukte -- dan zeggen we dat in plaats van een naam te verzinnen.
   */
  _duplicateMessage(dubbel) {
    const regels = dubbel.map((r) => {
      const namen = this._boringNamesForRecording(r.recordingId);
      const boring = namen.length === 1
        ? html`<strong>boring ${namen[0]}</strong>`
        : namen.length > 1
          ? html`de boringen <strong>${namen.join(', ')}</strong>`
          : null;
      return html`
        <div style="margin-bottom:.9rem;">
          <div style="font-size:.82rem; color:var(--mv-text-muted); word-break:break-all; margin-bottom:.25rem;">${r.filename}</div>
          ${boring
            ? html`<div>In dit project is deze opname al verwerkt tot ${boring}. We hebben niets toegevoegd.</div>`
            : html`<div>In dit project staat deze opname al; hij is alleen nog niet tot een boring verwerkt. We hebben niets toegevoegd.</div>`}
        </div>`;
    });
    const enkel = dubbel.length === 1;
    const namen = enkel ? this._boringNamesForRecording(dubbel[0].recordingId) : [];
    return html`<div style="white-space:normal;">
      ${regels}
      <div>Wil je dit audiobestand opnieuw laten verwerken tot boring, geef het dan een unieke naam.
      ${enkel && namen.length === 1
        ? html`Gebruik <strong>Extra opname</strong> als je boring ${namen[0]} wilt aanvullen.`
        : html`Gebruik <strong>Extra opname</strong> op de bestaande boring als je die wilt aanvullen.`}</div>
    </div>`;
  }

  // ─── Recording actions ────────────────────────────────────────────────────

  deleteRecording(recording) {
    if (!recording) return;
    const connectedBorings = (this.projectBorings || []).filter(b => b.recordingId === recording.id);
    this._deleteRecordingTarget = { recording, connectedBorings };
    this.showDeleteRecordingModal = true;
  }

  async _confirmDeleteRecording() {
    const { recording } = this._deleteRecordingTarget;
    this.showDeleteRecordingModal = false;
    this._deleteRecordingTarget = null;

    try {
      const result = await api.deleteRecording(recording.recordingId);
      if (result.status === 'deleted') {
        this.recordings = this.recordings.filter(r => r.id !== recording.id);
        this.xmlContent = '';
        this.exportWarnings = [];
        this.triggerXmlGenerationIfNeeded();
        await this._loadBorings();
      } else {
        this.error = `Verwijderen gefaald: ${result.error || 'Onbekende fout'}`;
      }
    } catch (error) {
      console.error(`Failed to delete recording ${recording.filename}:`, error);
      this.error = `Verwijderen gefaald voor ${recording.filename}: ${error.message}`;
    }
  }

  // ─── Transcription save (event from recording-row-detail) ─────────────────

  // Standaard-wissel vanaf de tegel: zet de standaard op de opname en verwerk opnieuw.
  async _onReprocessStandard(event) {
    const { recording, standard } = event.detail;
    const label = SOIL_STANDARDS[normalizeSoilStandard(standard)].label;
    const metingId = recording.parent_recording_id || recording.id;
    const affected = (this.projectBorings || []).filter(b => b.recordingId === metingId);
    const extra = affected.length ? ' Eventuele handmatige aanpassingen aan het boorprofiel gaan verloren.' : '';
    const ok = await confirmDialog({
      title: `Verwerken volgens ${label}?`,
      message: `Deze meting wordt opnieuw verwerkt volgens ${label}.${extra} Doorgaan?`,
      confirmLabel: `Ja, ${label}`,
      danger: standard === 'nen5104',
    });
    if (!ok) return;
    const idx = this.recordings.findIndex(r => r.id === recording.id);
    if (idx !== -1) {
      this.recordings[idx] = { ...this.recordings[idx], soil_standard: standard, status: 'processing', progress: 0 };
      this.recordings = [...this.recordings];
    }
    try {
      await api.reprocessRecording(recording.recordingId || recording.id, standard);
      // De standaard is nu op de opname vastgelegd; herlaad zodat de tegel de verse waarde toont
      // (los van de WS-status-updates die tijdens het opnieuw verwerken binnenkomen).
      await this.loadRecordings();
    } catch (e) {
      alertDialog({ title: 'Fout', message: `Opnieuw verwerken mislukt: ${e.message || e}` });
    }
  }

  async _onTranscriptionSave(event) {
    const { recording, text } = event.detail;
    // Editing an extra opname rebuilds the whole meting on its primary recording, so
    // check the primary's borings for the "edits will be lost" warning.
    const metingId = recording.parent_recording_id || recording.id;
    const affectedBorings = (this.projectBorings || []).filter(b => b.recordingId === metingId);
    if (affectedBorings.length > 0) {
      this._reprocessWarning = { recording, text, borings: affectedBorings };
      this.showReprocessWarning = true;
      return;
    }
    await this._doTranscriptionSave(recording, text);
  }

  async _doTranscriptionSave(recording, text) {
    const idx = this.recordings.findIndex(r => r.id === recording.id);
    try {
      // Optimistic update: show loader immediately, update transcription text
      if (idx !== -1) {
        this.recordings[idx] = { ...this.recordings[idx], transcription: text, status: 'processing', progress: 0 };
        this.recordings = [...this.recordings];
      }

      // Clear XML so it regenerates once reprocessing finishes
      this.xmlContent = '';
      this.exportWarnings = [];
      this.requestUpdate();

      await api.updateRecordingTranscription(recording.recordingId, text);

      // Let the polling pick up the real status from the backend
      this.startEvents();
    } catch (e) {
      console.error('Failed to update transcription:', e);
      // Roll back optimistic update on error
      if (idx !== -1) {
        this.recordings[idx] = { ...this.recordings[idx], status: 'processed' };
        this.recordings = [...this.recordings];
      }
      this.error = `Transcriptie opslaan gefaald: ${e.message}`;
    }
  }

  // ─── Scan jar update ────────────────────────────────────────────────────

  async _onDeleteScanJar(event) {
    // Handled inline via update-scan-jar in recording-row-detail
  }

  _onUpdateScanJar(event) {
    const { boringId, jars } = event.detail;
    this.xmlContent = '';
    this.exportWarnings = [];
    this.triggerXmlGenerationIfNeeded();
    this._optimistic(
      bs => bs.map(b => {
        if (b.id !== boringId) return b;
        const nonScanMonsters = (b.monsters || []).filter(m => m.source !== 'scan' && m.type !== 'Jar');
        return {
          ...b,
          monsters: [
            ...nonScanMonsters,
            ...jars.map(j => ({
              id: j.id,
              name: j.name,
              identificatie: j.barcode,
              upperDepth: j.layerDepthFrom,
              lowerDepth: j.layerDepthTo,
              geroerd: j.geroerd,
              packingType: j.packingType,
              source: 'scan',
              type: 'Jar',
              created_at: typeof j.scannedAt === 'number' ? new Date(j.scannedAt).toISOString() : (j.scannedAt || new Date().toISOString()),
            })),
          ],
        };
      }),
      () => api.updateBoringScanData(boringId, jars),
      'Potjes bijwerken gefaald',
    );
  }



  /** Ververs (debounced) het XML-voorbeeld op de Export-tab bij selectiewijzigingen. */
  _scheduleXmlPreview() {
    if (this.activeTab !== 'export') return;
    // Elke selectie/instelling-wijziging maakt een bestaand PDF-voorbeeld verouderd.
    if (this._pdfPages.length) this._pdfDirty = true;
    const fmt = this._exportFormat || 'xml';
    // PDF-formaten niet auto-genereren (te zwaar) — de gebruiker klikt op 'Vernieuwen'
    // (net als het TerraBase-afdrukvenster).
    // Excel heeft geen te genereren voorbeeld: de tabbladtelling rekent bij elke render mee.
    if (fmt === 'profiles' || fmt === 'report' || fmt === 'excel') { this.requestUpdate(); return; }
    clearTimeout(this._xmlPreviewTimer);
    this._xmlPreviewTimer = setTimeout(() => this.generateAndPreviewXML(), 600);
  }

  /** Open het full-page export-venster voor een gekozen formaat + start het voorbeeld. */
  _openExportModal(id) {
    this._exportFormat = id;
    this._exportMenuOpen = false;
    this._exportModalOpen = true;
    this.requestUpdate();
    if (id === 'xml') { if (!this.xmlContent) this.generateAndPreviewXML(); this._laadFotoTelling(); }
    else if (id === 'excel') { /* geen voorbeeld te genereren */ }
    else if (this._pdfPagesFor !== id || this._pdfDirty) { this._refreshPdfPreview(); }
  }
  /**
   * Hoeveel foto's (en hoeveel MB) elke boring heeft, voor de fototegel in het exportvenster.
   * Eén lijst-verzoek per boring, een paar tegelijk; de foto's zelf worden pas bij het
   * exporteren opgehaald.
   */
  async _laadFotoTelling() {
    if (this._fotoTellingBezig) return;
    const ids = (this._allBoringen || []).map((b) => b.guid).filter(Boolean);
    const telling = { ...(this._fotoTelling || {}) };
    const nog = ids.filter((id) => !telling[id]);
    // Ook zonder iets te tellen (een project zonder boringen) een uitkomst vastleggen, anders
    // bleef de tegel op 'Foto's tellen…' staan (14-10).
    if (!nog.length) { this._fotoTelling = telling; return; }
    this._fotoTellingBezig = true;
    let i = 0;
    const werker = async () => {
      while (i < nog.length) {
        const id = nog[i++];
        try {
          const fotos = (await api.getBoringPhotos(id))?.photos || [];
          telling[id] = { aantal: fotos.length, bytes: fotos.reduce((t, f) => t + (Number(f.sizeBytes) || 0), 0) };
        } catch { telling[id] = { aantal: 0, bytes: 0, fout: true }; }
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(6, nog.length) }, werker));
    } finally {
      this._fotoTellingBezig = false;
      this._fotoTelling = telling;
    }
  }

  _closeExportModal() { this._exportModalOpen = false; this._exportMenuOpen = false; this.requestUpdate(); }

  /** Bouwt het PDF-bestand voor het huidige formaat en rendert het via pdf.js naar
   *  voorbeeldpagina's (echt 'doorkijkluik' naar de PDF, inclusief pagina-stepper). */
  async _refreshPdfPreview() {
    const fmt = this._exportFormat;
    if (fmt !== 'profiles' && fmt !== 'report') return;
    this._pdfBusy = true; this._pdfError = null; this.requestUpdate();
    try {
      const selectedIds = this.selectedBoringGuids;
      const borings = this._withStandard(selectedIds && selectedIds.size > 0
        ? this.projectBorings.filter(b => selectedIds.has(b.id))
        : this.projectBorings);
      let doc;
      if (fmt === 'profiles') {
        doc = await buildBoorprofielenDoc(borings, this._boorprofielPdfOpts());
      } else {
        const pm = ProjectModel.fromFlat({ borings, projectName: this.project?.project_name || null, projectCode: this.project?.project_code || null });
        doc = await buildProjectReportDoc(pm, borings, { logo: this._companyLogo });
      }
      const data = doc.output('arraybuffer');
      const pdfjs = await import('pdfjs-dist');
      if (!pdfjs.GlobalWorkerOptions.workerSrc) {
        pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      }
      const pdf = await pdfjs.getDocument({ data: new Uint8Array(data) }).promise;
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.6 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width; canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        pages.push(canvas.toDataURL('image/png'));
      }
      this._pdfPages = pages;
      this._pdfPage = 0;
      this._pdfPagesFor = fmt;
      this._pdfDirty = false;
    } catch (e) {
      console.error('PDF preview error:', e);
      this._pdfError = e.message || 'Voorbeeld genereren mislukt';
      this._pdfPages = [];
    } finally {
      this._pdfBusy = false;
      this.requestUpdate();
    }
  }

  /** Projectcode inline opslaan vanaf de Export-tab (leeg = weggehaald). */
  async _saveProjectCode(value) {
    const code = (value ?? '').trim();
    if (code === (this.project?.project_code || '')) return;
    const prev = this.project?.project_code || '';
    this.project = { ...this.project, project_code: code || null };
    this._scheduleXmlPreview(); // ververst XML of markeert PDF-voorbeeld als verouderd
    try {
      await api.updateProject(this.projectId, { project_code: code || null });
    } catch (error) {
      this.project = { ...this.project, project_code: prev };
      showAlert(`Projectcode opslaan mislukt: ${error.message}`);
    }
  }

  /** Coordinate system to display a recording's points in (its own project's, or the main one). */
  _coordSystemFor(pid) {
    if (pid && this.deelprojects) {
      const dp = this.deelprojects.find(p => String(p.id) === String(pid));
      if (dp?.coordinate_system) return dp.coordinate_system;
    }
    return this.project?.coordinate_system || DEFAULT_COORDINATE_SYSTEM;
  }

  /** Height datum to display a recording's Z in (its own project's, or the main one). */
  _heightDatumFor(pid) {
    if (pid && this.deelprojects) {
      const dp = this.deelprojects.find(p => String(p.id) === String(pid));
      if (dp?.height_datum) return dp.height_datum;
    }
    return this.project?.height_datum || DEFAULT_HEIGHT_DATUM;
  }

  /** Projectdefault-standaard voor een (deel)project (opname-override telt zwaarder, zie _standardForRow). */
  _projectStandardFor(pid) {
    if (pid && this.deelprojects) {
      const dp = this.deelprojects.find(p => String(p.id) === String(pid));
      if (dp?.soil_standard) return normalizeSoilStandard(dp.soil_standard);
    }
    return normalizeSoilStandard(this.project?.soil_standard || DEFAULT_SOIL_STANDARD);
  }

  /**
   * Plak de effectieve standaard op elke boring die naar een export gaat.
   *
   * De tekening kiest zijn patronen op de naam van de grondsoort, en die naam hangt af van
   * de standaard: 'S' is silt in NEN 6693 en slib in de SIKB-lijst. Zonder deze vlag zou een
   * 5104-boring in de PDF slib als silt tonen. Dezelfde afspraak als in de backend-export,
   * die `boring._standard` ook per boring meegeeft.
   */
  _withStandard(borings) {
    return (borings || []).map(b => ({ ...b, _standard: this._standardForRow(b) }));
  }

  /**
   * Vult dit (deel)project de korrelverdeling in bij een laag?
   *
   * Per deelproject instelbaar, net als de coördinaten en de standaard: een deelproject kan
   * een ander soort onderzoek zijn dan het hoofdproject.
   */
  _grainSizeInputFor(pid) {
    if (pid && this.deelprojects) {
      const dp = this.deelprojects.find(p => String(p.id) === String(pid));
      if (dp) return !!dp.grain_size_input;
    }
    return !!this.project?.grain_size_input;
  }

  /** Effectieve standaard van een opname: eigen keuze > projectdefault. */
  _standardForRow(row) {
    return row?.soil_standard ? normalizeSoilStandard(row.soil_standard) : this._projectStandardFor(row?.projectId);
  }

  /** Haal de sjabloon-defaults op voor het project + alle deelprojecten (per template_id gecachet). */
  async _loadTemplateDefaults() {
    const ids = new Set();
    if (this.project?.template_id) ids.add(this.project.template_id);
    for (const dp of this.deelprojects || []) if (dp?.template_id) ids.add(dp.template_id);
    const map = {};
    await Promise.all([...ids].map(async (id) => {
      try { const tpl = await api.getTemplate(id); map[id] = tpl?.defaults || null; } catch { /* geen toegang/offline */ }
    }));
    this._templateDefaultsById = map;
    this.requestUpdate();
  }

  /** Sjabloon-defaults voor een (deel)project (val terug op het hoofdproject). */
  _templateDefaultsFor(pid) {
    let tplId = this.project?.template_id;
    if (pid && this.deelprojects) {
      const dp = this.deelprojects.find(p => String(p.id) === String(pid));
      if (dp) tplId = dp.template_id;
    }
    return (tplId && (this._templateDefaultsById || {})[tplId]) || null;
  }

  async saveCoordinateSystem(sys) {
    const prev = this.project?.coordinate_system;
    this.project = { ...this.project, coordinate_system: sys };
    this.requestUpdate();
    try {
      await api.updateProject(this.projectId, { coordinate_system: sys });
    } catch (error) {
      this.project = { ...this.project, coordinate_system: prev };
      this.requestUpdate();
      showAlert(`Opslaan mislukt: ${error.message}`);
    }
  }

  // ─── Project actions ──────────────────────────────────────────────────────

  async confirmDeleteProject() {
    try {
      await api.deleteProject(this.projectId);
      this.dispatchEvent(new CustomEvent('back-to-projects'));
    } catch (error) {
      console.error('Delete project failed:', error);
      this.error = `Project verwijderen gefaald: ${error.message}`;
    }
    this.closeDeleteModal();
  }

  goBack() {
    this.dispatchEvent(new CustomEvent('back-to-projects'));
  }

  // ─── Processing ───────────────────────────────────────────────────────────

  async reprocessProjectAi() {
    const confirmed = await confirmDialog({
      title: 'Opnieuw analyseren',
      message: 'Weet je zeker dat je alle opnamen in dit project opnieuw wilt laten analyseren?',
      confirmLabel: 'Opnieuw analyseren',
    });
    if (!confirmed) return;

    this.reprocessing = true;
    this.showLoader = true;
    this.currentMessage = 'AI herverwerking gestart...';
    this.requestUpdate();

    try {
      await api.reprocessProjectAi(this.projectId);
      this.currentMessage = 'Herverwerking gestart — updates komen via WebSocket...';
      setTimeout(async () => {
        this.showLoader = false;
        this.reprocessing = false;
        await this.loadProject();
      }, 1500);
    } catch (error) {
      console.error('Reprocessing failed:', error);
      this.currentMessage = `Fout bij herverwerken: ${error.message}`;
      setTimeout(() => {
        this.showLoader = false;
        this.reprocessing = false;
        this.requestUpdate();
      }, 3000);
    }
  }

  handleProgressUpdate(progressUpdate) {
    if (progressUpdate.showLoader !== undefined) this.showLoader = progressUpdate.showLoader;
    if (progressUpdate.progress) this.progressData = progressUpdate.progress;
    if (progressUpdate.message) this.currentMessage = progressUpdate.message;
    if (['complete', 'error'].includes(progressUpdate.type || progressUpdate.status)) {
      this.showLoader = false;
      this.progressData = null;
      this.currentMessage = '';
    }
    this.requestUpdate();
  }

  // ─── XML export ───────────────────────────────────────────────────────────

  get deelprojectenTableColumns() {
    return [
      {
        id: 'name',
        title: 'Projectnaam',
        render: (row) => html`
          <div style="display:flex; align-items:center; gap:8px;">
            <div style="width: 24px; height: 24px; border-radius: 6px; background: var(--mv-surface-alt); display: flex; align-items: center; justify-content: center; color: var(--mv-text-sub);">
              ${unsafeSVG(lucideIcon(Folder, 14))}
            </div>
            <strong>${row.project_name || row.name || row.id}</strong>
          </div>
        `
      },
      {
        id: 'code',
        title: 'Projectcode',
        render: (row) => row.project_code ? row.project_code : html`<span style="color:var(--mv-text-muted)">-</span>`
      },
      {
        id: 'boring_count',
        title: 'Boringen',
        render: (row) => html`<span style="color:var(--mv-text-muted); font-weight: 500">${row.boring_count || 0}</span>`
      },
      {
        id: 'actions',
        title: '',
        width: '80px',
        render: (row) => html`
          <div style="display:flex; gap:0.5rem; justify-content: flex-end;">
            <button class="action-btn" @click=${(e) => { e.stopPropagation(); this.editDeelproject(row); }} title="Bewerken">
              ${unsafeSVG(lucideIcon(Pencil, 15))}
            </button>
            <button class="action-btn delete" @click=${(e) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('delete-project', { detail: { projectId: row.id }, bubbles: true, composed: true })); }} title="Verwijderen">
              ${unsafeSVG(lucideIcon(Trash2, 15))}
            </button>
          </div>
        `
      }
    ];
  }

  triggerXmlGenerationIfNeeded() {
    const allProcessed = this.recordings.length > 0 &&
      this.recordings.every(r => r.status === 'processed');
    if (!allProcessed || this.exportGenerating || this.xmlContent) return;
    this.generateAndPreviewXML();
  }

  createNewDeelproject() {
    this.editingDeelproject = null;
    this.projectNameValue = '';
    this.projectCodeValue = '';
    this.instructionsValue = '';
    this.coordinateSystemValue = this.project?.coordinate_system || DEFAULT_COORDINATE_SYSTEM;
    this.heightDatumValue = this.project?.height_datum || DEFAULT_HEIGHT_DATUM;
    // Nieuw deelproject erft de standaard + werksoort van het hoofdproject.
    this.soilStandardValue = normalizeSoilStandard(this.project?.soil_standard || DEFAULT_SOIL_STANDARD);
    this.grainSizeInputValue = !!this.project?.grain_size_input;
    this.timezoneValue = this.project?.timezone || DEFAULT_TIMEZONE;
    this.werksoortValue = this.project?.werksoort || '';
    // Nieuw deelproject: geen sjabloon voorgeselecteerd.
    this.templateValue = '';
    this._templateInitial = '';
    this.createError = null;
    this.showCreateModal = true;
    this._ensureTemplates();
  }

  async _onSoilStandardChange(e) {
    const next = e.target.value;
    if (next === 'nen5104' && this.soilStandardValue !== 'nen5104') {
      const ok = await confirmDialog({
        title: 'Naar NEN 5104 wisselen?',
        message: 'De standaard bepaalt hoe grondsoorten en bijmengingen gecodeerd worden. Lagen die al beschreven zijn, worden daarna anders gelezen. Weet je zeker dat je wilt wisselen?',
        confirmLabel: 'Ja, NEN 5104', cancelLabel: 'Annuleren', danger: true,
      });
      if (!ok) { e.target.value = this.soilStandardValue || DEFAULT_SOIL_STANDARD; return; }
    }
    this.soilStandardValue = next;
  }

  editDeelproject(dp) {
    this.editingDeelproject = dp;
    this.projectNameValue = dp.project_name || dp.name || '';
    this.projectCodeValue = dp.project_code || dp.code || '';
    this.instructionsValue = dp.instructions || '';
    this.coordinateSystemValue = dp.coordinate_system || DEFAULT_COORDINATE_SYSTEM;
    this.heightDatumValue = dp.height_datum || DEFAULT_HEIGHT_DATUM;
    this.soilStandardValue = normalizeSoilStandard(dp.soil_standard);
    this.grainSizeInputValue = !!dp.grain_size_input;
    this.timezoneValue = dp.timezone || DEFAULT_TIMEZONE;
    this.werksoortValue = dp.werksoort || '';
    // Bestaande projecten: leeg tenzij er al een sjabloon aan hangt.
    this.templateValue = dp.template_id || '';
    this._templateInitial = dp.template_id || '';
    this.createError = null;
    this.showCreateModal = true;
    this._ensureTemplates();
  }

  async _ensureTemplates() {
    if (this._templates && this._templates.length) return;
    try { this._templates = await api.getTemplates(); } catch { this._templates = []; }
  }

  /**
   * Eén veld uit het gedeelde projectformulier terug in de eigen toestand. Zie de gelijknamige
   * methode in project-dashboard.js: de namen in dat formulier zijn de domeinnamen, hier heten
   * ze historisch anders.
   */
  _zetProjectVeld(sleutel, waarde) {
    const naar = {
      name: 'projectNameValue', code: 'projectCodeValue',
      coordinateSystem: 'coordinateSystemValue', heightDatum: 'heightDatumValue',
      soilStandard: 'soilStandardValue', grainSizeInput: 'grainSizeInputValue',
      timezone: 'timezoneValue', werksoort: 'werksoortValue', templateId: 'templateValue',
      instructions: 'instructionsValue',
    }[sleutel];
    if (naar) this[naar] = waarde;
  }

  async saveDeelproject(e) {
    e.preventDefault();
    this.isCreating = true;
    this.createError = null;

    try {
      const payload = {
        name: this.projectNameValue,
        code: this.projectCodeValue || null,
        instructions: this.instructionsValue || null,
        coordinate_system: this.coordinateSystemValue || DEFAULT_COORDINATE_SYSTEM,
        height_datum: this.heightDatumValue || DEFAULT_HEIGHT_DATUM,
        soil_standard: this.soilStandardValue || DEFAULT_SOIL_STANDARD,
        grain_size_input: this.grainSizeInputValue ? 1 : 0,
        timezone: this.timezoneValue || DEFAULT_TIMEZONE,
        werksoort: this.werksoortValue || null,
        template_id: this.templateValue || null
      };

      if (this.editingDeelproject) {
        await api.updateProject(this.editingDeelproject.id, payload);
      } else {
        await api.createProject({ ...payload, parent_id: this.projectId });
      }
      this.showCreateModal = false;
      this.editingDeelproject = null;
      await this.loadProject();
    } catch (error) {
      this.createError = `${this.editingDeelproject ? 'Opslaan' : 'Aanmaken'} mislukt: ${error.message}`;
    } finally {
      this.isCreating = false;
    }
  }

  _setForm(key, val) {
    this._createFormVal = { ...this._createFormVal, [key]: val };
  }

  _modalShell(title, onClose, body) {
    return html`
      <div class="modal-overlay" @click=${(e) => e.target === e.currentTarget && onClose()}>
        <div class="modal-content">
          <h3 style="margin:0 0 1.25rem 0; color:var(--mv-text);">${title}</h3>
          ${body}
        </div>
      </div>
    `;
  }

  /**
   * Teamleden voeden de boormeester-dropdown; het huidige lid is de standaard voor
   * een nieuwe boring zonder sjabloon-boormeester. Zelfde bron als de boring-editor.
   */
  async _loadMembers() {
    try {
      const data = await api.getTeamMembers();
      const members = data?.members || [];
      this._memberNames = members.map(m => displayName(m)).filter(Boolean);
      const me = members.find(m => m.id === data?.myUserId);
      this._currentUserName = me ? displayName(me) : '';
    } catch (_) {
      // Geen teamleden (bv. rechten) → dropdown valt terug op vrije tekst.
    }
  }

  /** Boormeester-opties: teamleden, met de huidige waarde vooraan als die ontbreekt. */
  _boormeesterOptions(current) {
    const opts = [...(this._memberNames || [])];
    const c = (current || '').trim();
    if (c && !opts.includes(c)) opts.unshift(c);
    return opts;
  }

  _openBoringModal() {
    // Sjabloon-defaults van de boring (indien aan het project gekoppeld).
    const defs = this._templateDefaultsFor(this.projectId)?.boring || {};
    // Boormeester: eerst uit het sjabloon, anders het huidige teamlid.
    const boormeester = (defs.boormeester && String(defs.boormeester).trim()) || this._currentUserName || '';
    // Referentievlak: uit het sjabloon, anders maaiveld (standaard).
    const referencePlane = defs.reference_plane || DEFAULT_REFERENCE_PLANE;
    // Type boring: uit het sjabloon, anders leeg (zoals voorheen).
    const type = normalizeBoringType(defs.type) || '';
    const today = new Date().toISOString().slice(0, 10); // yyyy-mm-dd voor <input type=date>
    this._createFormVal = { name: '', type, boormeester, datum: today, referencePlane, groundLevelType: '' };
    this.showBoringModal = true;
  }

  _renderBoringModal() {
    if (!this.showBoringModal) return '';
    const v = this._createFormVal;
    const close = () => this.showBoringModal = false;
    return this._modalShell('Nieuw Boring', close, html`
      <form @submit=${async (e) => {
        e.preventDefault();
        this._createFormSaving = true;
        try {
          await api.createBoring(this.projectId, {
            name: v.name, type: v.type || null, boormeester: v.boormeester || null, datum: v.datum || null,
            referencePlane: v.referencePlane || null, groundLevelType: v.groundLevelType || null,
          });
          await this._loadBorings();
          close();
        } catch (err) { showAlert('Aanmaken mislukt: ' + err.message); }
        finally { this._createFormSaving = false; }
      }}>
        <div class="form-group">
          <label>Naam *</label>
          <input type="text" required .value=${v.name || ''} @input=${e => this._setForm('name', e.target.value)} placeholder="bijv. B-01">
        </div>
        <div class="form-group">
          <label>Type boring</label>
          <select class="native-select" .value=${v.type || ''} @change=${e => this._setForm('type', e.target.value)}>
            <option value="">— kies (optioneel) —</option>
            ${BORING_TYPE.map(t => html`<option value=${t.code} ?selected=${t.code === v.type}>${t.label}</option>`)}
          </select>
        </div>
        <div class="form-group">
          <label>Boormeester</label>
          <searchable-select
            allowCustom
            placeholder="— kies of typ —"
            .options=${this._boormeesterOptions(v.boormeester)}
            .value=${v.boormeester || ''}
            @change=${e => this._setForm('boormeester', e.detail.value)}>
          </searchable-select>
        </div>
        <div class="form-group">
          <label>Datum</label>
          <input type="date" .value=${v.datum || ''} @input=${e => this._setForm('datum', e.target.value)}>
        </div>
        <div class="form-group">
          <label>Referentievlak</label>
          <select class="native-select" .value=${v.referencePlane || DEFAULT_REFERENCE_PLANE} @change=${e => this._setForm('referencePlane', e.target.value)}>
            ${Object.values(REFERENCE_PLANES).map(r => html`<option value=${r.id} ?selected=${r.id === (v.referencePlane || DEFAULT_REFERENCE_PLANE)}>${r.label}</option>`)}
          </select>
        </div>
        <div class="form-group">
          <label>Maaiveldtype</label>
          <select class="native-select" .value=${v.groundLevelType || ''} @change=${e => this._setForm('groundLevelType', e.target.value)}>
            <option value="">— kies (optioneel) —</option>
            ${nen6693.MAAIVELDTYPE.map(m => html`<option value=${m.code} ?selected=${m.code === v.groundLevelType}>${m.label}</option>`)}
          </select>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click=${close}>Annuleren</button>
          <button type="submit" class="btn btn-primary" ?disabled=${this._createFormSaving}>
            ${this._createFormSaving ? 'Aanmaken...' : 'Boring Aanmaken'}
          </button>
        </div>
      </form>
    `);
  }

  _renderCreateModal() {
    if (!this.showCreateModal) return '';

    return html`
      <div class="modal-overlay" @click=${(e) => e.target === e.currentTarget ? this.showCreateModal = false : null}>
        <div class="modal-content">
          <h3 style="margin: 0 0 ${this.editingDeelproject ? '1.5rem' : '0'}; color: var(--mv-text);">${!this.editingDeelproject
            ? 'Nieuw deelproject'
            : (this.editingDeelproject.parent_id ? 'Deelproject bewerken' : 'Project bewerken')}</h3>
          ${this.editingDeelproject ? '' : html`
            <p style="font-size: 0.9rem; color: var(--mv-text-muted); margin-bottom: 1.5rem;">
              Dit project wordt aangemaakt als onderdeel van <strong>${this.project.project_name || this.project.name}</strong>.
            </p>`}
          
          <form @submit=${this.saveDeelproject}>
            ${projectVelden({
              waarden: {
                name: this.projectNameValue,
                code: this.projectCodeValue,
                coordinateSystem: this.coordinateSystemValue,
                heightDatum: this.heightDatumValue,
                soilStandard: this.soilStandardValue,
                grainSizeInput: this.grainSizeInputValue,
                timezone: this.timezoneValue,
                werksoort: this.werksoortValue,
                templateId: this.templateValue,
                instructions: this.instructionsValue,
              },
              zet: (k, v) => this._zetProjectVeld(k, v),
              // Geen hoofdprojectkeuze: je zit hier al in een project, en een deelproject hangt
              // per definitie onder dit project.
              sjablonen: this._templates || [],
              sjabloonInitieel: this._templateInitial,
            })}

            ${this.createError ? html`<div class="modal-error">${this.createError}</div>` : ''}
            
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click=${() => this.showCreateModal = false}>Annuleren</button>
              <button type="submit" class="btn btn-primary" ?disabled=${this.isCreating}>
                ${this.isCreating
                  ? (this.editingDeelproject ? 'Opslaan...' : 'Aanmaken...')
                  : (this.editingDeelproject ? 'Opslaan' : 'Project Aanmaken')
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  goBackToProject(projectId) {
    this.dispatchEvent(new CustomEvent('project-selected', {
      detail: { projectId },
      bubbles: true,
      composed: true
    }));
  }

  async generateAndPreviewXML() {
    try {
      this.exportGenerating = true;
      this.exportError = null;
      this.xmlContent = '';
      this.exportWarnings = [];
      this.requestUpdate();

      let queryParams = '?preview=1';
      if (this.selectedProjectIds && this.selectedProjectIds.length > 0) {
        queryParams += `&projectIds=${this.selectedProjectIds.join(',')}`;
      }
      if (this.selectedBoringGuids && this.selectedBoringGuids.size > 0) {
        queryParams += `&meetpuntIds=${Array.from(this.selectedBoringGuids).join(',')}`;
      }
      const response = await api.downloadXML(this.projectId, queryParams);
      const text = await response.text();

      this.xmlContent = text;

      // Combine XML-comment warnings with data-validation warnings
      const xmlWarnings = this._parseWarningsFromXml(text);
      const dataWarnings = validateProjectData(this.aggregatedRecordings.filter(r => this.selectedProjectIds.includes(r.projectId)));
      this.exportWarnings = [...dataWarnings, ...xmlWarnings];

    } catch (error) {
      console.error('XML generation failed:', error);
      this.exportError = `XML genereren gefaald: ${error.message}`;
    } finally {
      this.exportGenerating = false;
      this.requestUpdate();
    }
  }

  async handleExport() {
    if (this.exportFormat === 'xml') {
      this.downloadCurrentXML(true);
    } else if (this.exportFormat === 'pdf') {
      await this.handlePdfExport();
    }
  }

  /** Opties voor de boorprofielen-PDF: hoogtestelsel, bedrijfslogo en project in de kop. */
  _boorprofielPdfOpts() {
    const label = [this.project?.project_name, this.project?.project_code].filter(Boolean).join('  ·  ');
    return { heightDatum: this.project?.height_datum, logo: this._companyLogo, label };
  }

  /** Export: alléén de boorprofielen (SVG → PDF, vier boringen per pagina). */
  async handleBoorprofielenExport() {
    try {
      const selectedIds = this.selectedBoringGuids;
      const borings = this._withStandard(selectedIds && selectedIds.size > 0
        ? this.projectBorings.filter(b => selectedIds.has(b.id))
        : this.projectBorings);
      const code = this.project?.project_code || 'project';
      await exportBoorprofielenPdf(borings, this._boorprofielPdfOpts(), `boorprofielen-${code}.pdf`);
    } catch (e) {
      console.error('Boorprofielen Export Error:', e);
      showAlert('Fout bij genereren boorprofielen: ' + e.message);
    }
  }

  async handlePdfExport() {
    try {
      const selectedIds = this.selectedBoringGuids;
      const borings = this._withStandard(selectedIds && selectedIds.size > 0
        ? this.projectBorings.filter(b => selectedIds.has(b.id))
        : this.projectBorings);
      const pm = ProjectModel.fromFlat({
        borings,
        projectName: this.project?.project_name || null,
        projectCode: this.project?.project_code || null,
      });
      const code = this.project?.project_code || 'ONB';

      await generateProjectPDF(pm, borings, `geovoice-rapport-${code}.pdf`, { logo: this._companyLogo });
      this.showFilterModal = false;
    } catch (e) {
      console.error('PDF Export Error:', e);
      showAlert('Fout bij genereren PDF: ' + e.message);
    }
  }

  /** De boringen voor de Excel-export: de selectie, of zonder selectie het hele project. */
  _excelBorings() {
    const selectedIds = this.selectedBoringGuids;
    return this._withStandard(selectedIds && selectedIds.size > 0
      ? this.projectBorings.filter(b => selectedIds.has(b.id))
      : this.projectBorings);
  }

  /** Kolomnamen noemen het coördinatenstelsel en het hoogtestelsel van het project. */
  _excelOptions() {
    const sys = this.project?.coordinate_system || DEFAULT_COORDINATE_SYSTEM;
    const datum = this.project?.height_datum || DEFAULT_HEIGHT_DATUM;
    return {
      coordinateSystemLabel: (COORDINATE_SYSTEMS[sys] || {}).label || sys,
      heightDatumLabel: (HEIGHT_DATUMS[datum] || {}).shortLabel || datum,
    };
  }

  async handleExcelExport() {
    try {
      const code = this.project?.project_code || 'project';
      await exportProjectExcel(this._excelBorings(), `${code}.xlsx`, this._excelOptions());
    } catch (e) {
      console.error('Excel Export Error:', e);
      showAlert('Fout bij genereren Excel: ' + e.message);
    }
  }

  _parseWarningsFromXml(xmlText) {
    if (!xmlText) return [];
    const match = xmlText.match(/<!--([\s\S]*?)-->/);
    if (!match) return [];

    return (match[1] || '').split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.startsWith('* WARNING:'))
      .map(l => l.replace('* WARNING:', '').trim())
      .map(msg => ({ message: msg, recordingId: null, boringName: null }));
  }

  // ─── Navigation to recording row ────────────────────────────────────────

  _scrollToRecording(recordingId) {
    if (!recordingId) return;
    const row = this.recordings.find(r => r.id === recordingId || r.recordingId === recordingId);
    if (!row) return;

    row.expanded = true;
    this.requestUpdate();

    setTimeout(() => {
      const table = this.shadowRoot.querySelector('mv-table');
      if (!table) return;
      const rows = table.shadowRoot?.querySelectorAll('tr.data-row');
      if (!rows) return;
      const idx = this.recordings.indexOf(row);
      const tr = rows[idx];
      if (tr) {
        tr.scrollIntoView({ behavior: 'smooth', block: 'center' });
        tr.style.transition = 'background 0.3s';
        tr.style.background = '#FFFBEB';
        setTimeout(() => { tr.style.background = ''; }, 1500);
      }
    }, 80);
  }

  async downloadCurrentXML(useFilters = false) {
    try {
      let queryParams = '';
      if (useFilters) {
        queryParams = '?format=xml';
        if (this.selectedProjectIds && this.selectedProjectIds.length > 0) {
          queryParams += `&projectIds=${this.selectedProjectIds.join(',')}`;
        }
        if (this.selectedBoringGuids && this.selectedBoringGuids.size > 0) {
          queryParams += `&meetpuntIds=${Array.from(this.selectedBoringGuids).join(',')}`;
        }
      }

      const metFotos = useFilters && this._exportFotos;
      if (metFotos) { this.exportGenerating = true; this._exportVoortgang = 'Export maken…'; }
      const response = await api.downloadXML(this.projectId, queryParams);
      let blob = await response.blob();
      const projectName = this.project?.project_name || this.project?.name || this.projectId;
      const safe = projectName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const date = new Date().toISOString().split('T')[0];
      const filterSuffix = useFilters ? '-filtered' : '';
      const basis = `geovoice-${safe}-${date}${filterSuffix}`;
      let bestandsnaam = `${basis}.xml`;
      if (metFotos) {
        // Eén zip met een map: het exportbestand en een submap 'fotos' met de foto's van
        // precies de boringen die in de export zitten. Zie utils/export-met-fotos.js.
        const sel = this.selectedBoringGuids;
        const boringen = (this._allBoringen || [])
          .filter((b) => b.guid && (!sel || sel.size === 0 || sel.has(b.guid)))
          .map((b) => ({ id: b.guid, naam: b.name }));
        const { fotos, mislukt } = await haalFotosOp(boringen, {
          lijst: async (id) => (await api.getBoringPhotos(id))?.photos || [],
          blob: (id) => api.fetchPhotoBlob(id),
        }, (klaar, totaal) => { this._exportVoortgang = totaal ? `Foto's ophalen ${klaar}/${totaal}…` : 'Export maken…'; });
        this._exportVoortgang = 'Zip maken…';
        const zip = bouwExportZip({
          map: basis,
          exportNaam: `${basis}.xml`,
          exportBytes: new Uint8Array(await blob.arrayBuffer()),
          fotos,
        });
        blob = new Blob([zip], { type: 'application/zip' });
        bestandsnaam = `${basis}.zip`;
        if (mislukt) showAlert(`${mislukt} foto${mislukt === 1 ? '' : "'s"} kon${mislukt === 1 ? '' : 'den'} niet worden opgehaald en ${mislukt === 1 ? 'zit' : 'zitten'} niet in de export.`);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = bestandsnaam;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { window.URL.revokeObjectURL(url); document.body.removeChild(a); }, 100);
      
      if (useFilters) this.showFilterModal = false;
    } catch (error) {
      console.error('Error downloading XML file:', error);
      showAlert('Fout bij downloaden van XML bestand' + (error.message ? `: ${error.message}` : ''));
    } finally {
      if (this._exportVoortgang) { this.exportGenerating = false; this._exportVoortgang = ''; }
    }
  }


  // ─── Misc helpers ─────────────────────────────────────────────────────────

  formatDate(dateString) {
    if (!dateString) return 'Onbekend';
    // DB timestamps are UTC; show them in the Dutch timezone.
    const out = formatDateTime(dateString, { second: '2-digit' });
    return out === '—' ? 'Onbekend' : out;
  }

  getStatusLabel(status) {
    return { new: 'Nieuw', processing: 'Verwerken', complete: 'Voltooid', error: 'Fout' }[status] || status;
  }

  handlePageChange(event) {
    const { page, pageSize } = event.detail;
    this.recordingsPage = page;
    this.recordingsPageSize = pageSize;
    this.loadRecordings();
  }

  // ─── Memo summary (mini chart in table) ───────────────────────────────────

  _getMemoSummary(recording) {
    const recordingBorings = (this.projectBorings || []).filter(b => b.recordingId === recording.id);

    if (!recordingBorings.length) {
      if (['transcribing', 'processing'].includes(recording.status))
        return html`<span style="color:var(--mv-text-muted); font-size:0.8rem; font-style:italic;">Bezig met analyseren...</span>`;
      return html`<span style="color:var(--mv-text-muted); font-size:0.85rem;">Geen data</span>`;
    }

    const meetpunten = recordingBorings.map(b => BoringModel.fromFlat(b));

    return html`
      <div style="display:flex; flex-direction:column; gap:8px; width:100%;">
        ${meetpunten.map((mp) => {
          if (!mp.layers || !mp.layers.length) return '';

          // Determine max depth in cm for proportional rendering
          let maxDepth = Math.max(mp.maxDepth * 100 || 100, 100);

          return html`
            <div style="display:flex; align-items:center; gap:8px; height:16px;">
              <span style="font-size:0.72rem; font-weight:700; color:var(--mv-text-sub); min-width:30px;">${mp.name}</span>
              <div style="flex:1 1 0; min-width:60px; height:10px; border-radius:3px; overflow:hidden; display:flex; background:var(--mv-bg); border:1px solid var(--mv-border);">
                ${mp.layers.map(l => {
                  const from = l.upperDepth;
                  const to = l.lowerDepth;
                  const widthPct = ((to - from) / maxDepth) * 100;
                  return html`<div style="width:${widthPct}%; height:100%; background:${l.colorHex}; border-right:1px solid rgba(0,0,0,0.05);" title="${from}-${to} cm: ${this._soilTerm(l, this._standardForRow(mp))}"></div>`;
                })}
              </div>
              <span style="font-size:0.65rem; color:var(--mv-text-muted); font-weight:600; min-width:35px; text-align:right;">${Math.round(maxDepth)} cm</span>
            </div>
          `;
        })}
      </div>
    `;
  }


  // ─── Render ───────────────────────────────────────────────────────────────

  _renderSkeleton() {
    return html`
      <!-- Header skeleton -->
      <div class="dashboard-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem;">
        <div style="display:flex; align-items:center; gap:0.5rem;">
          <div style="width:36px; height:36px; border-radius:50%;" class="skeleton"></div>
          <div style="width:220px; height:28px;" class="skeleton"></div>
        </div>
        <div style="width:140px; height:32px; border-radius:8px;" class="skeleton"></div>
      </div>

      <!-- Instructions skeleton -->
      <div class="card" style="padding:1.25rem; margin-bottom:2rem;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0.875rem;">
          <div style="width:120px; height:16px;" class="skeleton"></div>
          <div style="width:72px; height:28px; border-radius:6px;" class="skeleton"></div>
        </div>
        <div style="width:100%; height:14px; margin-bottom:8px;" class="skeleton"></div>
        <div style="width:75%; height:14px;" class="skeleton"></div>
      </div>

      <!-- Tab bar skeleton -->
      <div style="display:flex; gap:0; border-bottom:1px solid var(--mv-border); margin-bottom:1.5rem;">
        ${this._tabDefs.map(t => html`
          <div style="width:${Math.round(t.label.length * 8.5 + 32)}px; height:40px; margin-right:4px; border-radius:0;" class="skeleton"></div>
        `)}
      </div>

      <!-- Table rows skeleton -->
      <div class="card" style="padding:0;">
        ${[1,2,3,4,5].map(() => html`
          <div class="skeleton-row">
            <div style="width:36px; height:36px; border-radius:8px; flex-shrink:0;" class="skeleton"></div>
            <div style="flex:1; min-width:0; display:flex; flex-direction:column; gap:6px;">
              <div style="width:55%; height:13px;" class="skeleton"></div>
              <div style="width:35%; height:11px;" class="skeleton"></div>
            </div>
            <div style="width:70px; height:22px; border-radius:20px;" class="skeleton"></div>
          </div>
        `)}
      </div>
    `;
  }

  render() {
    if (this.loading && !this.project) {
      return html`${this._renderSkeleton()}`;
    }

    if (!this.project) {
      return html`
        <div class="error">
          Project niet gevonden<br><br>
          <button class="processing-button" @click=${this.goBack}>Terug naar dashboard</button>
        </div>
      `;
    }

    const projectName = this.project.project_name || this.project.name || this.projectId;
    const processedCount = this.recordings.filter(r => r.status === 'processed').length;
    /*
     * Wanneer mag je exporteren? Niet "als alles gelukt is", maar "als er niets meer onderweg
     * is". Wachten tot élke opname 'processed' was, liet de exportpagina voorgoed op "opnamen
     * worden verwerkt" hangen zodra er één was mislukt.
     *
     * 'error' is een eindstation, net als 'processed'. En een opname kan ook blijven hangen
     * zónder foutstatus: in productie staan opnamen al maanden op 'transcribing' of
     * 'transcribed'. Die blokkeerden de export net zo hard. Wat na zes uur nog niet klaar is,
     * komt niet meer — verwerken duurt minuten, niet uren.
     *
     * Zulke opnamen leveren geen boring op en vallen dus vanzelf buiten de export. Er is niets
     * van te exporteren, alleen iets om te melden.
     */
    const STALE_MS = 6 * 60 * 60 * 1000;
    const settled = (r) => {
      if (r.status === 'processed' || r.status === 'error') return true;
      const t = parseTimestamp(r.created_at);
      return t ? (Date.now() - t.getTime()) > STALE_MS : false;
    };
    const failedCount = this.recordings.filter(r => r.status !== 'processed' && settled(r)).length;
    const canDownloadXML = this.recordings.every(settled);

    return html`
      ${this._teamOpen ? this._renderTeamPanel() : ''}

      <map-import-dialog
        .open=${!!this._mapImportOpen}
        .projectId=${this.projectId}
        .plannedPointsAllowed=${this._companyLicense === 'geodesk'}
        .coordSystem=${this.project?.coordinate_system || DEFAULT_COORDINATE_SYSTEM}
        .existingPoints=${this._plannedPoints || []}
        .borings=${this._mapImportOpen ? this._mapBorings() : []}
        @close=${() => { this._mapImportOpen = false; }}
        @imported=${(e) => this._onMapImported(e)}
        .plannerAvailable=${this._vooronderzoekAllowed}
        @open-monsterplan-import=${() => this._openMonsterplanImport()}
      ></map-import-dialog>

      <boring-duplicate-dialog
        .boring=${this._duplicateSource}
        .coordSystem=${this._duplicateSource ? this._coordSystemFor(this._duplicateSource.projectId) : 'WGS84'}
        @close=${() => { this._duplicateSource = null; }}
        @duplicated=${async () => {
          this._duplicateSource = null;
          await this._loadBorings();
          // Het activiteitenlog is nu verouderd (de tegel op Overzicht luistert hierop).
          this._activitySignal = (this._activitySignal || 0) + 1;
        }}
      ></boring-duplicate-dialog>
      ${this._renderBulkUpdate()}

      ${this.showReprocessWarning && this._reprocessWarning ? html`
        <div style="position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:var(--mv-z-modal); display:flex; align-items:center; justify-content:center; padding:1rem;"
             @click=${() => { this.showReprocessWarning = false; this._reprocessWarning = null; }}>
          <div style="background:var(--mv-surface); border-radius:14px; padding:2rem; max-width:460px; width:100%; box-shadow:0 24px 48px rgba(0,0,0,0.2);"
               @click=${(e) => e.stopPropagation()}>
            <div style="display:flex; align-items:center; gap:12px; margin-bottom:1rem;">
              <span style="color:#D97706; flex-shrink:0;">${unsafeSVG(lucideIcon(AlertTriangle, 22))}</span>
              <h2 style="margin:0; font-size:1.05rem; font-weight:700; color:var(--mv-text);">Bestaande boringen worden overschreven</h2>
            </div>
            <p style="color:var(--mv-text-sub); font-size:0.875rem; line-height:1.6; margin:0 0 0.875rem;">
              De AI analyseert de transcriptie opnieuw. De volgende boringen en hun lagen worden vervangen:
            </p>
            <ul style="margin:0 0 0.875rem; padding-left:1.25rem; display:flex; flex-direction:column; gap:3px;">
              ${this._reprocessWarning.borings.map(b => html`
                <li style="font-size:0.875rem; font-weight:600; color:var(--mv-text);">${b.name || 'Naamloos'}</li>
              `)}
            </ul>
            <p style="color:var(--mv-text-muted); font-size:0.8rem; margin:0 0 1.5rem; line-height:1.5;">
              Handmatig toegevoegde lagen en bewerkingen gaan verloren.
            </p>
            <div style="display:flex; justify-content:flex-end; gap:0.75rem;">
              <button class="btn-secondary" @click=${() => { this.showReprocessWarning = false; this._reprocessWarning = null; }}>
                Annuleren
              </button>
              <button style="display:inline-flex; align-items:center; gap:6px; background:#DC2626; color:#fff; border:none; border-radius:8px; padding:0.5rem 1.1rem; font-size:0.875rem; font-weight:600; cursor:pointer;"
                      @click=${async () => {
                        const { recording, text } = this._reprocessWarning;
                        this.showReprocessWarning = false;
                        this._reprocessWarning = null;
                        await this._doTranscriptionSave(recording, text);
                      }}>
                Toch herverwerken
              </button>
            </div>
          </div>
        </div>
      ` : ''}

      ${this.showDeleteRecordingModal && this._deleteRecordingTarget ? html`
        <div style="position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:var(--mv-z-modal); display:flex; align-items:center; justify-content:center; padding:1rem;"
             @click=${() => { this.showDeleteRecordingModal = false; this._deleteRecordingTarget = null; }}>
          <div style="background:var(--mv-surface); border-radius:14px; padding:2rem; max-width:460px; width:100%; box-shadow:0 24px 48px rgba(0,0,0,0.2);"
               @click=${(e) => e.stopPropagation()}>
            <div style="display:flex; align-items:center; gap:12px; margin-bottom:1rem;">
              <span style="color:#D97706; flex-shrink:0;">${unsafeSVG(lucideIcon(AlertTriangle, 22))}</span>
              <h2 style="margin:0; font-size:1.05rem; font-weight:700; color:var(--mv-text);">Opname verwijderen</h2>
            </div>
            <p style="color:var(--mv-text-sub); font-size:0.875rem; line-height:1.6; margin:0 0 0.875rem;">
              Weet je zeker dat je <strong>${this._deleteRecordingTarget.recording.filename}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </p>
            ${this._deleteRecordingTarget.connectedBorings.length > 0 ? html`
              <p style="color:var(--mv-text-sub); font-size:0.875rem; line-height:1.6; margin:0 0 0.5rem;">
                De volgende ${this._deleteRecordingTarget.connectedBorings.length === 1 ? 'boring wordt' : 'boringen worden'} ook verwijderd:
              </p>
              <ul style="margin:0 0 0.875rem; padding-left:1.25rem; display:flex; flex-direction:column; gap:3px;">
                ${this._deleteRecordingTarget.connectedBorings.map(b => html`
                  <li style="font-size:0.875rem; font-weight:600; color:var(--mv-text);">${b.name || 'Naamloos'}</li>
                `)}
              </ul>
            ` : ''}
            <div style="display:flex; justify-content:flex-end; gap:0.75rem;">
              <button class="btn-secondary" @click=${() => { this.showDeleteRecordingModal = false; this._deleteRecordingTarget = null; }}>
                Annuleren
              </button>
              <button style="display:inline-flex; align-items:center; gap:6px; background:#DC2626; color:#fff; border:none; border-radius:8px; padding:0.5rem 1.1rem; font-size:0.875rem; font-weight:600; cursor:pointer;"
                      @click=${this._confirmDeleteRecording}>
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      ` : ''}

      ${this.showLoader ? html`
        <div class="processing-overlay">
          <div class="processing-loader">
            <div class="loader-spinner"></div>
            <div class="progress-text">${this.reprocessing ? 'Herverwerken...' : 'Verwerken...'}</div>
            <div class="progress-message">${this.currentMessage || 'Data wordt verwerkt...'}</div>
            ${this.progressData ? html`
              <div class="progress-bar">
                <div class="progress-fill" style="width:${this.progressData.percentage || 0}%"></div>
              </div>
              <div class="progress-details">
                ${this.progressData.percentage || 0}% voltooid
                ${this.progressData.current && this.progressData.total
                  ? html` (${this.progressData.current}/${this.progressData.total})` : ''}
              </div>
            ` : ''}
          </div>
        </div>
      ` : ''}

      <!-- Header -->
      <!-- Minder lucht onder de kop: de tabs zijn de eerste bediening op deze pagina en
           hoorden niet zo laag te beginnen. -->
      <div class="dashboard-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:${this.parentProject ? '0.7rem' : '0.25rem'}; flex-wrap:wrap; gap:0.75rem;">
        <div style="display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap;">
          <button class="back-btn" @click=${this.goBack} title="Terug naar projecten" style="margin-right:0.5rem; background:none; border:none; cursor:pointer; color:var(--mv-text-muted); display:flex; align-items:center; justify-content:center; padding:8px; border-radius:50%; transition:all 0.2s;">
            ${unsafeSVG(lucideIcon(ArrowLeft, 20))}
          </button>
          
          <nav class="breadcrumb-nav" style="display:flex; align-items:center; gap:10px; font-size:1.1rem; color:var(--mv-text-muted);">
            <!-- Geen eigen kruimelpad meer: dat staat nu volledig in de balk bovenaan, met
                 het hoofdproject en de mogelijkheid van deelproject te wisselen. Twee paden
                 boven elkaar die hetzelfde zeggen is verwarrend. Wel de badge houden -- die
                 zegt WAT dit is, niet waar het hangt. -->
            <div style="display:flex; align-items:center; gap:${this.parentProject ? '14px' : '10px'};">
              ${this.parentProject ? html`
                <span style="font-size:0.8rem; background:var(--mv-primary-light); color:var(--mv-primary); padding:4px 10px; border-radius:5px; font-weight:700; letter-spacing:0.02em;">Deelproject</span>
              ` : ''}
              <h1 style="margin:0; font-size:1.5rem; font-weight:700; color:var(--mv-text); letter-spacing:-0.01em; line-height:1;">
                ${this.project.project_name || this.project.name}
              </h1>
              ${this.project.project_code ? html`
                <span style="padding:4px 10px; background:#F1F3F5; border-radius:6px; font-size:0.75rem; font-weight:600; color:var(--mv-text-muted); letter-spacing:0.02em;">${this.project.project_code}</span>
              ` : ''}
              <button @click=${() => this.editDeelproject(this.project)} title="Project bewerken"
                style="background:none; border:none; cursor:pointer; color:var(--mv-text-muted); display:flex; align-items:center; padding:4px; border-radius:6px;">
                ${unsafeSVG(lucideIcon(Pencil, 16))}
              </button>
            </div>
          </nav>
        </div>

        <div style="display:flex; gap:0.75rem; align-items:center;" @click=${(e) => e.stopPropagation()}>
          <!-- Hier stond een keuzelijst 'Status' (Nieuw, Bezig, …). Er hing niets aan: geen logica,
               geen filter dat ergens op stuurde. Weg sinds 30-10-2026; de kolom in de database blijft. -->

          <!-- Alles wat je zelden doet maar wél moet kunnen, achter een enkele knop. Los
               naast de statuskeuze stonden dat drie knoppen die om aandacht vroegen terwijl
               je hier komt om boringen te bekijken. -->
          <div style="position:relative;">
            <button title="Meer acties" @click=${() => { this._projectMenuOpen = !this._projectMenuOpen; }}
              style="display:flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:9px; border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text-sub,#5e6278); cursor:pointer;">
              ${unsafeSVG(lucideIcon(MoreHorizontal, 18))}
            </button>
            ${this._projectMenuOpen ? html`
              <div style="position:absolute; right:0; top:calc(100% + 6px); z-index:var(--mv-z-overlay); min-width:250px;
                          background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:.7rem;
                          box-shadow:0 10px 28px rgba(0,0,0,.14); padding:6px;">
                <!-- Een gast krijgt alleen 'Team op dit project' (daar zit zijn uitstapknop).
                     Deelprojecten en instellingen zijn schrijfacties op het project, en een knop
                     die een 403 oplevert is verwarrender dan geen knop. -->
                ${!this.project.parent_id && !this._iAmGuest ? html`
                  <button style=${PROJECT_MENU_ITEM} @click=${() => { this._projectMenuOpen = false; this.createNewDeelproject(); }}>
                    ${unsafeSVG(lucideIcon(FolderPlus, 16))} Nieuw deelproject
                  </button>` : ''}
                <button style=${PROJECT_MENU_ITEM} @click=${() => { this._projectMenuOpen = false; this._openProjectTeam(); }}>
                  ${unsafeSVG(lucideIcon(UsersRound, 16))} Team op dit project
                </button>
                ${!this._iAmGuest ? html`
                  <button style=${PROJECT_MENU_ITEM} @click=${() => { this._projectMenuOpen = false; this.editDeelproject(this.project); }}>
                    ${unsafeSVG(lucideIcon(Settings2, 16))} Projectinstellingen
                  </button>` : ''}
              </div>` : ''}
          </div>
        </div>
      </div>

      <!-- Main content -->
      <div class="detail-container"
           @transcription-save=${this._onTranscriptionSave}
           @reprocess-standard=${this._onReprocessStandard}
           @borings-changed=${() => this._loadBorings()}
           @delete-scan-jar=${(e) => this._onDeleteScanJar(e)}
           @update-scan-jar=${(e) => this._onUpdateScanJar(e)}>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}

        <!-- Deelprojecten (outside tabs, root projects only) -->
        ${!this.project.parent_id && this.deelprojects.length > 0 ? html`
          <div class="page-section" style="margin-top: 1.25rem; margin-bottom: 2rem;">
            <div class="page-section-header">
              <div class="page-section-title">
                Deelprojecten
                <span class="page-section-badge">${this.deelprojects.length}</span>
              </div>
            </div>
            <div class="card" style="padding: 0;">
              <mv-table
                .columns=${this.deelprojectenTableColumns}
                .data=${this.deelprojects}
                @row-click=${(e) => this.goBackToProject(e.detail.row.id)}
              ></mv-table>
            </div>
          </div>
        ` : ''}

        <!-- Tab header with action button -->
        <div style="display:flex; align-items:center; justify-content:space-between; border-bottom: 1px solid var(--mv-border); margin-bottom: 1.15rem; overflow-x: auto;">
          <div style="display:flex; gap:0; flex-shrink:0;">
            ${this._tabDefs.map(t => t.disabled ? html`
              <button disabled style="background:none; border:none; border-bottom:2px solid transparent; padding:0.65rem 1rem; font-size:0.9rem; font-weight:600; cursor:not-allowed; color:var(--mv-text-muted); opacity:0.4; white-space:nowrap; display:flex; align-items:center; gap:5px;">
                ${t.label}
                <span style="font-size:0.6rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; background:var(--mv-surface-alt); border:1px solid var(--mv-border); border-radius:4px; padding:1px 5px;">Binnenkort</span>
              </button>
            ` : html`
              <button style="background:none; border:none; border-bottom: 2px solid ${this.activeTab === t.id ? 'var(--mv-primary)' : 'transparent'}; padding: 0.65rem 1rem; font-size: 0.9rem; font-weight: 600; cursor: pointer; color: ${this.activeTab === t.id ? 'var(--mv-primary)' : 'var(--mv-text-muted)'}; white-space: nowrap; display:flex; align-items:center; gap:5px;"
                @click=${() => this._goToTab(t.id)}>
                ${t.label}
                ${t.count != null ? html`<span style="background:var(--mv-surface-alt,#F3F4F6); color:var(--mv-text-muted); font-size:0.7rem; font-weight:700; border-radius:99px; padding:1px 6px; min-width:18px; text-align:center;">${t.count}</span>` : ''}
                ${t.beta ? html`<span style="font-size:0.6rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--mv-info,#7c3aed); background:var(--mv-surface-alt); border:1px solid var(--mv-border); border-radius:4px; padding:1px 5px;">bèta</span>` : ''}
              </button>
            `)}
          </div>
          <div style="padding-bottom:0.5rem; flex-shrink:0;"></div>
        </div>

        ${this.activeTab === 'overzicht' ? html`
          ${this._renderOverzichtTab()}
        ` : (this.activeTab === 'vooronderzoek' || this.activeTab === 'opdracht') && this._vooronderzoekAllowed && this._vooDeel === 'veldwerkopdracht' ? html`
          ${this._renderVooronderzoekTabs()}
          <field-order-panel .projectId=${this.projectId} .refreshSignal=${this._fieldOrderSignal || 0}
            @field-order-changed=${() => { this._activitySignal = (this._activitySignal || 0) + 1; }}></field-order-panel>
        ` : this.activeTab === 'borings' ? html`
          ${this._renderCombinedTab()}
        ` : this.activeTab === 'vooronderzoek' && this._vooronderzoekAllowed ? html`
          ${this._renderVooronderzoekTabs()}
          <vooronderzoek-panel .projectId=${this.projectId} .borings=${this._mapBorings()}
            .focusPointId=${this._focusPlannedPoint || null}
            @focus-handled=${() => { this._focusPlannedPoint = null; }}
            .plannedPoints=${this._plannedPoints || []} .mapLayers=${this._mapLayers || []}
            @planned-points-saved=${() => this._loadMapData()}
            @planned-point-update=${e => this._updatePlannedPoint(e.detail)}
            @map-layer-update=${e => this._updateMapLayer(e.detail)}
            @map-layer-delete=${e => this._deleteMapLayer(e.detail)}
            @map-layer-realign=${e => this._realignMapLayer(e.detail.layer)}
            @open-import=${e => this._openMapImport(e.detail?.files)}></vooronderzoek-panel>
        ` : this.activeTab === 'map' ? html`
          <project-map .projectId=${this.projectId} .borings=${this._mapBorings()}
            .plannedPoints=${this._plannedPoints || []} .mapLayers=${this._mapLayers || []}
            .coordSystem=${this.project?.coordinate_system || DEFAULT_COORDINATE_SYSTEM}
            @open-boring=${e => this._openBoringFromMap(e.detail.guid)}
            @set-boring-location=${e => this._setBoringLocation(e.detail)}
            @sublocations-assigned=${() => this._loadBorings()}
            @open-import=${() => { this._mapImportOpen = true; }}
            @planned-point-update=${e => this._updatePlannedPoint(e.detail)}
            @planned-point-delete=${e => this._deletePlannedPoint(e.detail)}
            @planned-point-link=${e => this._linkPlannedPoint(e.detail)}
            @map-layer-update=${e => this._updateMapLayer(e.detail)}
            @map-layer-delete=${e => this._deleteMapLayer(e.detail)}
            @map-layer-realign=${e => this._realignMapLayer(e.detail.layer)}
            .plannerAvailable=${this._vooronderzoekAllowed}
            @open-vooronderzoek=${e => { this._focusPlannedPoint = e.detail.pointId; this._kiesVooDeel('monsterplan'); }}></project-map>
        ` : this.activeTab === 'rapportage' && this._reportAllowed ? html`
          <report-list .projectId=${this.projectId} .projectBorings=${this.projectBorings || []}
            @open-map=${() => this._goToTab('map')}></report-list>
        ` : this.activeTab === 'labopdrachten' ? html`
          ${this._companyLicense === 'geovoice' ? html`
            <div style="display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:14px; padding:72px 24px;">
              <h3 style="margin:0; color:var(--mv-text); font-size:1.05rem;">Hiervoor is een GeoDesk-licentie nodig</h3>
              <p style="margin:0; max-width:360px; color:var(--mv-text-sub,#6c757d); line-height:1.5;">
                Met GeoDesk stuur je monsters rechtstreeks naar het laboratorium.
              </p>
              <a href="mailto:sales@geovoice.nl?subject=Upgrade%20naar%20GeoDesk"
                style="padding:10px 18px; border-radius:8px; background:var(--mv-primary); color:#fff; font-weight:600; text-decoration:none;">
                Contact voor upgrade
              </a>
            </div>
          ` : html`
            <!-- Alleen de lijst. Een labopdracht openen gaat naar /labopdrachten/:id, een
                 eigen pagina: het detailvenster stond hier eerst ín, met de projectkop en de
                 projecttabs eromheen, en dan kijk je naar twee menu's tegelijk. -->
            <lab-koppeling-page
              .projectId=${this.projectId}
              .project=${this.project}
              @orders-changed=${(e) => { this._labOrderCount = e.detail.count; }}
            ></lab-koppeling-page>
          `}
        ` : this.activeTab === 'export' ? html`
          ${this._renderExport(canDownloadXML, processedCount, failedCount)}
        ` : ''}
      </div>

      <!-- Upload modal -->
      ${this.showUploadModal ? html`
        <div class="modal" @click=${(e) => e.target === e.currentTarget && this.closeUploadModal()}>
          <div class="modal-content">
            <div class="modal-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.5rem;">
              <h3 style="margin:0; color:var(--mv-text);">Opname Toevoegen</h3>
              <button class="icon-btn close-modal-btn" @click=${this.closeUploadModal}>
                ${unsafeSVG(lucideIcon(X, 20))}
              </button>
            </div>
            <div class="upload-area"
                 @click=${() => this.shadowRoot.querySelector('#file-input').click()}
                 @dragover=${this.handleDragOver}
                 @dragleave=${this.handleDragLeave}
                 @drop=${this.handleDrop}
                 style="border:2px dashed var(--mv-border); border-radius:0.5rem; padding:3rem; text-align:center; cursor:pointer; background:var(--mv-surface-alt); transition:border-color 0.2s;">
              <div style="margin-bottom:1rem; color:var(--mv-primary);">${unsafeSVG(lucideIcon(Plus, 32))}</div>
              <p style="font-weight:600; color:var(--mv-text-sub); margin-bottom:0.5rem;">Sleep bestanden hierheen of klik om te selecteren</p>
              <small style="color:var(--mv-text-muted);">Ondersteunde formaten: MP3, M4A, MP4, WAV, WEBM, OGG, OPUS, TXT, PDF, DOCX</small>
            </div>
            <input id="file-input" type="file" multiple hidden @change=${this.handleFileSelect} accept="audio/*,text/plain,.txt,.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document">
          </div>
        </div>
      ` : ''}

      <!-- Delete modal -->
      ${this.showDeleteModal ? html`
        <div class="modal" @click=${(e) => e.target === e.currentTarget && this.closeDeleteModal()}>
          <div class="modal-content">
            <h3 style="margin-top:0;">Project Verwijderen</h3>
            <p>Weet je zeker dat je "${projectName}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.</p>
            <div style="display:flex; gap:1rem; justify-content:flex-end; margin-top:2rem;">
              <button class="cancel-button" @click=${this.closeDeleteModal}>Annuleren</button>
              <button class="processing-button" style="background:#F1416C;" @click=${this.confirmDeleteProject}>Verwijderen</button>
            </div>
          </div>
        </div>
      ` : ''}

      ${this._renderCreateModal()}
      ${this._renderBoringModal()}
      ${this._renderNieuweBoringKeuze()}

      <boring-inspreken-dialog
        .open=${this._inspreekOpen}
        .bezig=${this.transcribing}
        .profiel=${this._projectStandardFor(this.projectId)}
        @sluit=${() => { this._inspreekOpen = false; }}
        @verwerk=${async (e) => {
          // Exact dezelfde weg als 'Via audio': uploaden, transcriberen, extraheren.
          this.error = null;
          await this.uploadAdditionalFiles([e.detail.file], { locatie: e.detail.locatie });
          if (!this.error) this._inspreekOpen = false;
          e.detail.klaar?.(this.error ? String(this.error).replace(/^Upload gefaald: /, '') : null);
        }}></boring-inspreken-dialog>
    `;
  }

  _getBoringName(recording) {
    const first = (this.projectBorings || []).find(b => b.recordingId === recording.id);
    return first?.name || '';
  }

  /** Returns all warnings for a flat boring object (model warnings + duplicate name check). */
  /**
   * Meldingen van één boring, met hun niveau. Zelfde bron als het overzicht, zodat een
   * boring in de lijst niet iets anders zegt dan de tegel op de overzichtspagina.
   */
  _getBoringWarnings(boring) {
    const dupes = new Set([...(this._duplicateBoringNames || [])]);
    return boringIssues(boring, {
      duplicateNames: dupes,
      recordingStatus: (this.recordings || []).find((r) => r.id === boring?.recordingId)?.status ?? null,
    });
  }

  /**
   * Meldingen als badges. Blokkerend is rood, aandacht amber -- anders leest een gat in het
   * profiel net zo dringend als een ontbrekende boormeester, en kijkt niemand er meer naar.
   */
  /**
   * Meldingen als badges. Blokkerend is rood, aandacht amber -- anders leest een gat in het
   * profiel net zo dringend als een ontbrekende boormeester.
   *
   * Hooguit twee in beeld, de rest achter een '+N'. Een boring met zes meldingen liet de
   * kolom over twee regels uitlopen en duwde de hele tabel scheef; de bedoeling van deze
   * badges is signaleren, niet opsommen. Wie alles wil zien opent de boring of kijkt op het
   * overzicht, waar ze onder elkaar staan.
   */
  _renderWarningBadges(issues) {
    if (!issues?.length) return '';
    const MAX = 2;
    const shown = issues.slice(0, MAX);
    const rest = issues.slice(MAX);
    const chip = (style, extra = '') =>
      `display:inline-flex; align-items:center; gap:4px; font-size:0.7rem; font-weight:600; border:1px solid; padding:2px 7px; border-radius:10px; white-space:nowrap; ${style} ${extra}`;
    const styleFor = (blocking) => blocking
      ? 'color:var(--mv-danger,#B91C1C); background:var(--mv-danger-bg,#FEE2E2); border-color:#FCA5A5;'
      : 'color:var(--mv-warning-text,#92400E); background:var(--mv-warning-panel-bg,#FEF3C7); border-color:var(--mv-warning-panel-border,#FDE68A);';

    return html`
      <div style="display:flex; align-items:center; gap:4px; margin-top:2px; min-width:0;"
        @mousemove=${tipMove} @mouseleave=${tipHide}>
        ${shown.map((i) => {
          const blocking = i.severity === BLOCKING;
          // Geen hovertekst op een badge zelf: het label staat er al, en de kleur zegt of
          // het blokkeert. Alleen de '+N' verbergt informatie en heeft er dus een nodig.
          return html`
            <span style=${chip(styleFor(blocking))}>
              ${blocking ? '●' : '⚠'} ${i.label}
            </span>`;
        })}
        ${rest.length ? html`
          <span data-tip=${rest.map((i) => i.label).join('\n')}
            style=${chip(styleFor(rest.some((i) => i.severity === BLOCKING)), 'cursor:default;')}>
            +${rest.length}
          </span>` : ''}
      </div>`;
  }


  /**
   * Search + sort toolbar shared by the Opnamen and Boringen tables — mirrors the
   * field-recorder: a search box and a toggle between 'Aangemaakt' (creation order)
   * and 'Naam' (alphabetical, numeric-aware).
   */
  /** Ben ik gast in dit bedrijf? Uit het token, alleen voor wat het scherm wel en niet aanbiedt. */
  get _iAmGuest() {
    return auth.getActiveCompany()?.role === 'guest';
  }

  async _openProjectTeam() {
    this._teamOpen = true;
    this._team = null;
    this._guestFormOpen = false;
    this._guestForm = { email: '', firstName: '', lastName: '', accessLevel: 'read' };
    this._guestResult = null;
    try {
      this._team = await api.getProjectTeam(this.projectId);
    } catch (e) {
      this._team = { error: e.message };
    }
  }

  /** Een gast uitnodigen voor dit project. Bij succes wordt de lijst opnieuw geladen. */
  async _inviteGuest(e) {
    e?.preventDefault?.();
    const f = this._guestForm || {};
    if (!f.email?.trim() || !f.firstName?.trim() || !f.lastName?.trim()) {
      this._guestResult = { error: 'Vul e-mailadres, voornaam en achternaam in.' };
      return;
    }
    this._guestBusy = true;
    this._guestResult = null;
    try {
      const res = await api.inviteProjectGuest(this.projectId, {
        email: f.email.trim(), firstName: f.firstName.trim(), lastName: f.lastName.trim(), accessLevel: f.accessLevel,
      });
      this._guestResult = res;
      this._guestForm = { email: '', firstName: '', lastName: '', accessLevel: 'read' };
      this._guestFormOpen = false;
      this._team = await api.getProjectTeam(this.projectId);
    } catch (err) {
      this._guestResult = { error: err.message };
    } finally {
      this._guestBusy = false;
    }
  }

  /** Niveau van een gast wijzigen, of hem van het project halen (accessLevel null). */
  async _setGuestAccess(member, accessLevel) {
    if (accessLevel === null) {
      const ok = await confirmDialog({
        title: 'Gast van project halen?',
        message: `${[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email} verliest de toegang tot dit project. Was dit zijn of haar laatste project bij dit bedrijf, dan verdwijnt de gast ook van de Team-pagina.`,
        confirmLabel: 'Ja, toegang intrekken',
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await api.setProjectGuestAccess(this.projectId, member.id, accessLevel);
      this._team = await api.getProjectTeam(this.projectId);
    } catch (err) {
      showAlert(`Aanpassen mislukt: ${err.message}`);
    }
  }

  /** Als gast zelf uit dit project stappen. Daarna terug naar de projectlijst: dit project bestaat voor jou niet meer. */
  async _leaveProjectAsGuest() {
    const ok = await confirmDialog({
      title: 'Uit dit project stappen?',
      message: 'Je verliest de toegang tot dit project. Wil je er later weer bij, dan moet het bedrijf je opnieuw uitnodigen.',
      confirmLabel: 'Ja, uitstappen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.leaveProjectAsGuest(this.projectId);
      window.location.href = '/projects';
    } catch (err) {
      showAlert(`Uitstappen mislukt: ${err.message}`);
    }
  }

  async _setTeamAccess(member, hasAccess) {
    // Optimistisch: de lijst springt meteen, want een halve seconde wachten op een vinkje
    // voelt als een knop die het niet doet. Mislukt het, dan zetten we hem terug.
    const before = this._team;
    this._team = {
      ...before,
      members: before.members.map(m => (m.id === member.id ? { ...m, hasAccess } : m)),
    };
    try {
      await api.setProjectTeamAccess(this.projectId, member.id, hasAccess);
    } catch (e) {
      this._team = before;
      showAlert(`Aanpassen mislukt: ${e.message}`);
    }
  }

  /**
   * Team op dit project. Voor LEDEN is het model open: iedereen in het bedrijf kan erbij,
   * tenzij je iemand uitsluit. Voor GASTEN is het omgekeerd: die staan hier alleen als ze
   * expliciet zijn uitgenodigd, en dat is dan ook het enige wat je hier 'toevoegt'.
   */
  _renderTeamPanel() {
    const t = this._team;
    const canEdit = t && ['owner', 'manager'].includes(t.myRole);
    const iAmGuest = t?.myRole === 'guest';
    const name = (m) => [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    const ROLE = { owner: 'Eigenaar', manager: 'Manager', member: 'Lid', guest: 'Gast' };
    const LEVEL = { read: 'kijkt mee', write: 'werkt mee' };
    const btn = 'padding:.35rem .7rem; border-radius:.5rem; border:1px solid var(--mv-border); background:var(--mv-surface); font:inherit; font-size:.8rem; font-weight:700; cursor:pointer; white-space:nowrap;';
    const field = 'width:100%; box-sizing:border-box; padding:.5rem .65rem; border:1px solid var(--mv-border); border-radius:.5rem; background:var(--mv-bg); color:var(--mv-text); font:inherit; font-size:.85rem;';

    /** Rechterkant van een gastrij: niveau omzetten of eraf halen (beheerder), of alleen het niveau (rest). */
    const guestControls = (m) => !canEdit
      ? html`<span style="font-size:.75rem; color:var(--mv-text-muted); white-space:nowrap;">${LEVEL[m.accessLevel] || 'kijkt mee'}</span>`
      : html`
        <select .value=${m.accessLevel || 'read'} @change=${(e) => this._setGuestAccess(m, e.target.value)}
          class="mv-field mv-select" style="padding:.35rem 1.9rem .35rem .6rem; font-size:.8rem; font-weight:700; cursor:pointer;">
          <option value="read" ?selected=${m.accessLevel === 'read'}>Kijkt mee</option>
          <option value="write" ?selected=${m.accessLevel === 'write'}>Werkt mee</option>
        </select>
        <button @click=${() => this._setGuestAccess(m, null)} title="Van dit project halen"
          style="${btn} color:var(--mv-danger,#f1416c);">Verwijderen</button>`;
    return html`
      <div style="position:fixed; inset:0; z-index:var(--mv-z-modal,1200); background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:5vh 1rem;"
        @click=${(e) => { if (e.target === e.currentTarget) this._teamOpen = false; }}>
        <div style="width:min(560px,100%); max-height:88vh; display:flex; flex-direction:column; background:var(--mv-surface,#fff); border-radius:.9rem; box-shadow:0 24px 60px rgba(0,0,0,.25);">
          <div style="display:flex; align-items:flex-start; gap:12px; padding:1.15rem 1.35rem .85rem; border-bottom:1px solid var(--mv-border);">
            <div style="flex:1;">
              <h3 style="margin:0; font-size:1rem; font-weight:800; color:var(--mv-text);">Team op dit project</h3>
              <span style="font-size:.82rem; color:var(--mv-text-muted);">${this.project?.project_name || this.project?.name || ''}</span>
            </div>
            <button @click=${() => { this._teamOpen = false; }} style="background:none; border:none; cursor:pointer; color:var(--mv-text-sub,#5e6278); display:flex; padding:4px;">
              ${unsafeSVG(lucideIcon(XIcon, 18))}
            </button>
          </div>

          <div style="padding:1rem 1.35rem; overflow-y:auto;">
            <p style="margin:0 0 1rem; font-size:.85rem; line-height:1.5; color:var(--mv-text-sub,#5e6278);">
              ${iAmGuest
                ? html`Je bent gast op dit project. Je ziet wie er verder bij kan.`
                : html`Iedereen in dit bedrijf heeft standaard toegang tot dit project. Haal je iemand
                  eraf, dan verdwijnt het project uit zijn of haar lijst. Gasten zien alleen de
                  projecten waarvoor ze zijn uitgenodigd.`}
            </p>

            <!-- Zonder deze uitleg lijkt het venster stuk in een bedrijf waar iedereen
                 eigenaar of manager is: je ziet dan alleen 'altijd toegang' en geen knop. -->
            ${t && !t.error && t.members.length && !t.members.some(m => m.restrictable) ? html`
              <div style="margin:0 0 1rem; padding:.7rem .85rem; border-radius:.55rem; background:var(--mv-surface-alt,#f8f9fa); border:1px solid var(--mv-border); font-size:.82rem; line-height:1.5; color:var(--mv-text-sub,#5e6278);">
                Er is hier niemand om te verwijderen: eigenaren en managers houden altijd toegang
                tot alle projecten. Alleen leden met de rol <b>Lid</b> kun je per project uitsluiten.
              </div>` : ''}

            ${!t ? html`<p style="color:var(--mv-text-muted);">Laden…</p>`
              : t.error ? html`<p style="color:var(--mv-danger,#f1416c);">${t.error}</p>`
              : html`
                <div style="display:flex; flex-direction:column; gap:6px;">
                  ${t.members.map(m => html`
                    <div style="display:flex; align-items:center; gap:11px; padding:.6rem .7rem; border:1px solid var(--mv-border); border-radius:.6rem; opacity:${m.hasAccess ? '1' : '.55'};">
                      <span style="flex-shrink:0; width:32px; height:32px; border-radius:9px; background:var(--mv-primary); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:.8rem;">
                        ${(name(m) || '?').charAt(0).toUpperCase()}
                      </span>
                      <div style="flex:1; min-width:0;">
                        <div style="font-weight:700; font-size:.88rem; color:var(--mv-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${name(m)}</div>
                        <div style="font-size:.76rem; color:var(--mv-text-muted); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${m.email} · ${ROLE[m.role] || m.role}</div>
                      </div>
                      ${m.role === 'guest'
                        ? guestControls(m)
                        : !m.restrictable
                        ? html`<span style="font-size:.75rem; color:var(--mv-text-muted); white-space:nowrap;" title="Eigenaren en managers hebben altijd toegang">altijd toegang</span>`
                        : !canEdit
                          ? html`<span style="font-size:.75rem; color:var(--mv-text-muted);">${m.hasAccess ? 'toegang' : 'geen toegang'}</span>`
                          : m.hasAccess
                            ? html`<button @click=${() => this._setTeamAccess(m, false)}
                                style="padding:.35rem .7rem; border-radius:.5rem; border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-danger,#f1416c); font:inherit; font-size:.8rem; font-weight:700; cursor:pointer; white-space:nowrap;">Verwijderen</button>`
                            : html`<button @click=${() => this._setTeamAccess(m, true)}
                                style="padding:.35rem .7rem; border-radius:.5rem; border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-primary); font:inherit; font-size:.8rem; font-weight:700; cursor:pointer; white-space:nowrap;">Terugzetten</button>`}
                    </div>`)}
                </div>

                <!-- Gasten: externen die alleen dít project zien. Alleen een beheerder nodigt ze
                     uit; het formulier staat ingeklapt omdat het de uitzondering is. -->
                ${canEdit ? html`
                  <div style="margin-top:1.1rem; padding-top:1rem; border-top:1px solid var(--mv-border);">
                    ${!this._guestFormOpen ? html`
                      <button @click=${() => { this._guestFormOpen = true; this._guestResult = null; }}
                        style="${btn} color:var(--mv-primary); display:inline-flex; align-items:center; gap:6px;">
                        ${unsafeSVG(lucideIcon(Plus, 14))} Gast uitnodigen
                      </button>
                      <div style="margin-top:.4rem; font-size:.78rem; color:var(--mv-text-muted); line-height:1.45;">
                        Een opdrachtgever die meekijkt, of een onderaannemer die meewerkt. Ziet alleen dit project.
                      </div>
                    ` : html`
                      <form @submit=${(e) => this._inviteGuest(e)} style="display:flex; flex-direction:column; gap:.6rem;">
                        <div style="font-weight:700; font-size:.88rem; color:var(--mv-text);">Gast uitnodigen voor dit project</div>
                        <div style="display:grid; grid-template-columns:1fr 1fr; gap:.6rem;">
                          <input placeholder="Voornaam" .value=${this._guestForm.firstName}
                            @input=${(e) => { this._guestForm = { ...this._guestForm, firstName: e.target.value }; }} style=${field}>
                          <input placeholder="Achternaam" .value=${this._guestForm.lastName}
                            @input=${(e) => { this._guestForm = { ...this._guestForm, lastName: e.target.value }; }} style=${field}>
                        </div>
                        <input type="email" placeholder="E-mailadres" .value=${this._guestForm.email}
                          @input=${(e) => { this._guestForm = { ...this._guestForm, email: e.target.value }; }} style=${field}>
                        <!-- Twee knoppen en geen dropdown: dit is dé keuze van het formulier, en
                             met twee opties leest een knoppenpaar sneller dan een lijst die je
                             eerst moet openen. -->
                        <div style="display:flex; gap:.5rem;">
                          ${[['read', 'Kijkt mee', 'Kan alles bekijken, niets aanpassen'], ['write', 'Werkt mee', 'Kan boringen en lagen bewerken, net als een lid']].map(([v, label, sub]) => html`
                            <button type="button" @click=${() => { this._guestForm = { ...this._guestForm, accessLevel: v }; }}
                              style="flex:1; text-align:left; padding:.6rem .75rem; border-radius:.6rem; cursor:pointer; font:inherit;
                                     border:2px solid ${this._guestForm.accessLevel === v ? 'var(--mv-primary)' : 'var(--mv-border)'};
                                     background:${this._guestForm.accessLevel === v ? 'var(--mv-primary-light,#f4faf2)' : 'var(--mv-surface)'};">
                              <div style="font-weight:700; font-size:.85rem; color:var(--mv-text);">${label}</div>
                              <div style="font-size:.75rem; color:var(--mv-text-muted); line-height:1.4;">${sub}</div>
                            </button>`)}
                        </div>
                        <div style="display:flex; gap:.5rem; justify-content:flex-end;">
                          <button type="button" @click=${() => { this._guestFormOpen = false; this._guestResult = null; }}
                            style="${btn} color:var(--mv-text-sub,#5e6278);">Annuleren</button>
                          <button type="submit" ?disabled=${this._guestBusy}
                            style="${btn} background:var(--mv-primary); border-color:var(--mv-primary); color:#fff;">
                            ${this._guestBusy ? 'Bezig…' : 'Uitnodigen'}
                          </button>
                        </div>
                      </form>
                    `}
                    ${this._guestResult?.error ? html`
                      <div style="margin-top:.6rem; padding:.55rem .75rem; border-radius:.5rem; background:var(--mv-danger-bg,#fff5f8); color:var(--mv-danger,#f1416c); font-size:.82rem;">${this._guestResult.error}</div>` : ''}
                    ${this._guestResult && !this._guestResult.error ? html`
                      <div style="margin-top:.6rem; padding:.55rem .75rem; border-radius:.5rem; background:var(--mv-primary-light,#f4faf2); border:1px solid var(--mv-border); font-size:.82rem; line-height:1.5; color:var(--mv-text);">
                        ${this._guestResult.added
                          ? html`<b>${this._guestResult.email}</b> had al een account en heeft nu toegang tot dit project.`
                          : html`Uitnodiging voor <b>${this._guestResult.email}</b> ${this._guestResult.emailed ? 'is verstuurd.' : 'is aangemaakt.'}
                              ${!this._guestResult.emailed && this._guestResult.token ? html`<br>Deel deze link: <code style="font-size:.78rem; word-break:break-all;">${window.location.origin}/accept-invite?token=${this._guestResult.token}</code>` : ''}`}
                      </div>` : ''}
                  </div>` : ''}

                <!-- Een gast stapt zelf uit: zijn eigen overzicht, niet dat van het bedrijf. -->
                ${iAmGuest ? html`
                  <div style="margin-top:1.1rem; padding-top:1rem; border-top:1px solid var(--mv-border); display:flex; align-items:center; justify-content:space-between; gap:1rem;">
                    <span style="font-size:.82rem; color:var(--mv-text-sub,#5e6278); line-height:1.45;">
                      Klaar met dit project? Dan kun je eruit stappen, zodat het uit je lijst verdwijnt.
                    </span>
                    <button @click=${() => this._leaveProjectAsGuest()} style="${btn} color:var(--mv-danger,#f1416c);">Uit dit project stappen</button>
                  </div>` : ''}
              `}
          </div>
        </div>
      </div>`;
  }

  /**
   * 'Nieuwe boring' hoort op één regel met de zoekbalk, niet in de tabbalk: de tabs zeggen
   * WAAR je bent, de werkbalk wat je met deze lijst kunt. Als methode, zodat er geen tweede
   * kopie van dit menu ontstaat.
   */
  _renderNewBoringButton() {
    // Geen uitklapmenu meer maar een keuzevenster met drie tegels (zie _renderNieuweBoringKeuze).
    return html`
      <button class="btn btn-primary" style="height:${TOOLBAR_H}; font-size:0.85rem; padding:0 14px; display:flex; align-items:center; gap:6px;"
        @click=${(e) => { e.stopPropagation(); this._nieuwKeuzeOpen = true; }}>
        ${unsafeSVG(lucideIcon(Plus, 14))} Nieuwe boring
      </button>`;
  }

  /** Boringen van dit project zelf, zonder die van deelprojecten: daar werkt bulk bijwerken op. */
  _ownBorings() {
    return (this.projectBorings || []).filter(b => b.projectId === this.projectId && !b.deletedAt);
  }

  /**
   * 'Bulk bijwerken' naast 'Nieuwe boring': coördinaten, maaiveld, datum en boormeester van
   * bestaande boringen uit een Excel/csv. Zonder boringen valt er niets bij te werken.
   */
  _renderBoringActions() {
    const bulk = this._ownBorings().length ? html`
      <button style="height:${TOOLBAR_H}; font-size:0.85rem; padding:0 14px; display:flex; align-items:center; gap:6px; border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface); color:var(--mv-text); font-weight:600; cursor:pointer; white-space:nowrap;"
        @click=${(e) => { e.stopPropagation(); this._bulkOpen = true; }}>
        ${unsafeSVG(lucideIcon(Upload, 14))} Bulk bijwerken
      </button>` : '';
    return html`<div style="display:flex; gap:8px;">${bulk}${this._renderNewBoringButton()}</div>`;
  }

  /** Na 'Bulk bijwerken': lijst verversen, badge per boring, en een korte melding. */
  async _onBulkUpdated(e) {
    const { ids = [], count = 0 } = e.detail || {};
    this._bulkOpen = false;
    this._bulkUpdatedIds = new Set([...(this._bulkUpdatedIds || []), ...ids]);
    await this._loadBorings();
    this._activitySignal = (this._activitySignal || 0) + 1;
    this._bulkNotice = `${count} ${count === 1 ? 'boring' : 'boringen'} bijgewerkt`;
    clearTimeout(this._bulkNoticeTimer);
    this._bulkNoticeTimer = setTimeout(() => { this._bulkNotice = null; }, 4000);
  }

  _renderBulkUpdate() {
    return html`
      <boring-bulk-update-dialog
        .open=${!!this._bulkOpen}
        .projectId=${this.projectId}
        .projectCode=${this.project?.project_code || ''}
        .borings=${this._bulkOpen ? this._ownBorings() : []}
        .coordSystem=${this._coordSystemFor(this.projectId)}
        @close=${() => { this._bulkOpen = false; }}
        @updated=${(e) => this._onBulkUpdated(e)}
      ></boring-bulk-update-dialog>
      ${this._bulkNotice ? html`
        <div role="status" style="position:fixed; left:50%; bottom:28px; transform:translateX(-50%); z-index:var(--mv-z-modal, 1200); display:flex; align-items:center; gap:8px; padding:10px 16px; border-radius:10px; background:#1e1e2d; color:#fff; font-size:0.85rem; font-weight:600; box-shadow:0 8px 24px rgba(0,0,0,.25);">
          ${unsafeSVG(lucideIcon(CheckCircle2, 15))} ${this._bulkNotice}
        </div>` : ''}`;
  }

  /**
   * 'Nieuwe boring': drie manieren naast elkaar, als tegels met icoon, titel en korte uitleg.
   * Elke tegel opent wat er vroeger in het uitklapmenu stond.
   */
  _renderNieuweBoringKeuze() {
    if (!this._nieuwKeuzeOpen) return '';
    const sluit = () => { this._nieuwKeuzeOpen = false; };
    const tegel = (icoon, titel, uitleg, actie) => html`
      <button class="nb-tegel" @click=${() => { sluit(); actie(); }}>
        <span class="nb-icoon">${unsafeSVG(lucideIcon(icoon, 30))}</span>
        <span class="nb-titel">${titel}</span>
        <span class="nb-uitleg">${uitleg}</span>
      </button>`;
    return html`
      <div class="modal" @click=${(e) => e.target === e.currentTarget && sluit()}
        @keydown=${(e) => e.key === 'Escape' && sluit()}>
        <div class="modal-content nb-keuze" role="dialog" aria-label="Nieuwe boring">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:1.75rem;">
            <div>
              <h3 style="margin:0; color:var(--mv-text);">Nieuwe boring</h3>
              <p class="nb-subtitel">Kies hoe je de boring wilt toevoegen.</p>
            </div>
            <button class="icon-btn close-modal-btn" @click=${sluit} aria-label="Sluiten">${unsafeSVG(lucideIcon(X, 20))}</button>
          </div>
          <div class="nb-tegels">
            ${tegel(FileAudio, 'Via audio', 'Een opname of tekstbestand uploaden', () => this.toggleUploadModal())}
            ${tegel(Mic, 'Inspreken', 'Opnemen met de microfoon van deze computer', () => { this._inspreekOpen = true; })}
            ${tegel(Pencil, 'Handmatig', 'Zelf de gegevens van de boring invullen', () => this._openBoringModal())}
          </div>
        </div>
      </div>`;
  }

  /**
   * De balk boven een lijst: zoeken, eventueel sorteren, en rechts de knoppen.
   *
   * `search: false` laat het zoekveld weg. Bedoeld voor een lege lijst: zoeken in niets is een
   * vakje dat niets doet, maar de knop ernaast hoort er wél te staan -- juist dan.
   */
  _renderListToolbar({ query, sort, placeholder, onQuery, onSort, actions, search = true }) {
    return html`
      <div style="display:flex; gap:8px; align-items:center; margin-bottom:10px; flex-wrap:wrap;">
        ${!search ? '' : html`
        <div style="position:relative; flex:1; min-width:180px; max-width:340px;">
          <span style="position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--mv-text-muted); display:flex;">${unsafeSVG(lucideIcon(Search, 15))}</span>
          <input type="text" .value=${query} @input=${(e) => onQuery(e.target.value)} placeholder=${placeholder}
            style="width:100%; box-sizing:border-box; height:${TOOLBAR_H}; padding:0 30px 0 34px; border:1px solid var(--mv-border); border-radius:8px; font-size:0.875rem; background:var(--mv-surface); color:var(--mv-text);">
          ${query ? html`<button @click=${() => onQuery('')} title="Wissen"
            style="position:absolute; right:6px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:var(--mv-text-muted); display:flex; padding:2px;">${unsafeSVG(lucideIcon(X, 14))}</button>` : ''}
        </div>`}
        ${!onSort ? '' : html`
        <button @click=${() => onSort(sort === 'name' ? 'created' : 'name')} title="Sorteervolgorde wisselen"
          style="display:flex; align-items:center; gap:6px; height:${TOOLBAR_H}; padding:0 14px; border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface); color:var(--mv-text); font-weight:600; font-size:0.85rem; cursor:pointer; white-space:nowrap;">
          ${unsafeSVG(lucideIcon(ArrowUpDown, 14))} ${sort === 'name' ? 'Naam' : 'Aangemaakt'}
        </button>`}
        ${actions ? html`<div style="margin-left:auto; flex-shrink:0;">${actions}</div>` : ''}
      </div>
    `;
  }

  _renderOpnamen() {
    if (this._loadingRecordings) {
      return html`
        <div class="card" style="padding:0;">
          ${[1,2,3].map(() => html`
            <div class="skeleton-row">
              <div style="width:36px; height:36px; border-radius:8px; flex-shrink:0;" class="skeleton"></div>
              <div style="flex:1; min-width:0; display:flex; flex-direction:column; gap:6px;">
                <div style="width:50%; height:13px;" class="skeleton"></div>
                <div style="width:30%; height:11px;" class="skeleton"></div>
              </div>
              <div style="width:80px; height:22px; border-radius:20px;" class="skeleton"></div>
            </div>
          `)}
        </div>
      `;
    }
    if (this.recordings.length === 0) {
      return html`
        <div style="padding:4rem 2rem; max-width:500px; margin:2rem auto; text-align:center; background:var(--mv-surface); border-radius:0.75rem; border:1px dashed var(--mv-border);">
          <div style="color:var(--mv-text-muted); margin-bottom:1.5rem; opacity:0.7; display:flex; justify-content:center;">
            ${unsafeSVG(lucideIcon(FileAudio, 40))}
          </div>
          <h3 style="margin:0 0 0.75rem 0; color:var(--mv-text-sub); font-size:1.25rem;">Geen opnames in dit project</h3>
          <p style="color:var(--mv-text-sub); font-size:0.95rem; line-height:1.6; margin:0 auto 1.5rem auto;">
            U kunt opnames toevoegen door audiobestanden handmatig te uploaden via de knop hierboven, of door de <a href="/recorder" style="color:#10B981; font-weight:600; text-decoration:none;" @click=${(e) => { e.preventDefault(); window.history.pushState({}, '', '/recorder'); window.dispatchEvent(new PopStateEvent('popstate')); }}>MV Field Recorder App</a> te gebruiken.
          </p>
          <button class="processing-button" @click=${this.openUploadModal}>
            ${unsafeSVG(lucideIcon(Plus, 16))} Handmatig Uploaden
          </button>
        </div>
      `;
    }

    const q = this._recordingsQuery.trim().toLowerCase();
    const filtered = q
      ? this.recordings.filter((r) =>
          this._getBoringName(r).toLowerCase().includes(q) ||
          (r.filename || '').toLowerCase().includes(q))
      : this.recordings;
    const sorted = this._recordingsSort === 'name'
      ? [...filtered].sort((a, b) => {
          const na = this._getBoringName(a), nb = this._getBoringName(b);
          // Recordings without a boring name fall to the bottom.
          if (!na && !nb) return 0;
          if (!na) return 1;
          if (!nb) return -1;
          return na.localeCompare(nb, 'nl', { numeric: true, sensitivity: 'base' });
        })
      : [...filtered].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));

    const toolbar = this._renderListToolbar({
      query: this._recordingsQuery,
      sort: this._recordingsSort,
      placeholder: 'Zoek op opname…',
      onQuery: (v) => { this._recordingsQuery = v; },
      onSort: (v) => { this._recordingsSort = v; },
    });

    return html`
      ${toolbar}
      ${sorted.length === 0
        ? html`<div style="padding:2rem; text-align:center; color:var(--mv-text-muted);">Geen opname gevonden voor "${this._recordingsQuery.trim()}".</div>`
        : html`
          <mv-table
            ?expandable=${true}
            .columns=${this.recordingColumns}
            .data=${sorted}
            .renderDetails=${(row) => html`
              <recording-row-detail
                .recording=${row}
                .extraRecordings=${row.extraRecordings || []}
                .projectId=${row.projectId || this.projectId}
                .coordinateSystem=${this._coordSystemFor(row.projectId)}
                .heightDatum=${this._heightDatumFor(row.projectId)}
                .standard=${this._standardForRow(row)}
                ?grainSizeInput=${this._grainSizeInputFor(row.projectId)}
                .templateDefaults=${this._templateDefaultsFor(row.projectId)}
                .borings=${(this.projectBorings || []).filter(b => b.recordingId === row.id)}
                @borings-changed=${() => this._loadBorings()}
              ></recording-row-detail>
            `}
          ></mv-table>
        `}
    `;
  }



  // ── Gecombineerde tab: opnames (met hun boring) + handmatige boringen ──────
  /** Rijen = primaire opnames + handmatig aangemaakte boringen (zonder opname). Stabiele objecten (voor expand-state). */
  _combinedRows() {
    const recs = (this.recordings || []).filter(r => !r.parent_recording_id);
    recs.forEach(r => { r._kind = 'recording'; });
    const manual = (this.projectBorings || []).filter(b => !b.recordingId);
    manual.forEach(b => { b._kind = 'boring'; });
    return [...recs, ...manual];
  }
  _combinedName(row) {
    return row._kind === 'recording' ? (this._getBoringName(row) || row.filename || '') : (row.name || '');
  }
  /** Laag-balkje van een boring (zoals in de oude Boringen-tab). */
  /** Grondsoort-code → leesbare term. Bij NEN 6693 resolven we de 6693-codes (grondsoort/
   *  laagtype/gesteente); bij 5104 is `soilLabel` al leesbaar. */
  _soilTerm(l, standard) {
    if (usesNen6693Codes(standard) && l?.soilType) {
      // ISO 14688 bewaart dezelfde codes, plus keitjes en 'teelaarde of humus' (iso14688 erft van nen6693).
      const N = standard === 'iso14688' ? iso14688 : nen6693;
      for (const m of [N.GRONDSOORT, N.LAAGTYPE_SPECIAAL, N.GESTEENTE]) {
        for (const [name, id] of m) if (id === l.soilType) return name;
      }
    }
    return l?.soilLabel || l?.soilType || '';
  }

  _layerBar(boring) {
    if (!boring) return html`<span style="color:var(--mv-text-muted); font-size:0.8rem;">—</span>`;
    const bm = BoringModel.fromFlat(boring);
    if (!bm.layers.length) return html`<span style="color:var(--mv-text-muted); font-size:0.8rem;">—</span>`;
    const maxDepth = Math.max(bm.maxDepth * 100 || 100, 100);
    const std = this._standardForRow(boring);
    return html`<div style="display:flex; align-items:center; gap:6px;">
      <div style="flex:1; min-width:80px; height:12px; border-radius:3px; overflow:hidden; display:flex; background:var(--mv-bg); border:1px solid var(--mv-border);"
        @mousemove=${tipMove} @mouseleave=${tipHide}>
        ${bm.layers.map(l => { const w = ((l.lowerDepth - l.upperDepth) / maxDepth) * 100; return html`<div style="width:${w}%; height:100%; background:${l.colorHex}; border-right:1px solid rgba(0,0,0,0.05);" data-tip="${l.upperDepth}–${l.lowerDepth} cm · ${this._soilTerm(l, std)}"></div>`; })}
      </div>
      <span style="font-size:0.65rem; color:var(--mv-text-muted); font-weight:600; white-space:nowrap;">${Math.round(maxDepth)} cm</span>
    </div>`;
  }
  /** 'Details'-kolom: icoontjes + tellingen per onderdeel (zoals de tab-badges). */
  /** Breedte van vier tellers met hun tussenruimte (icoon 13 px + getal tot twee cijfers). */
  get _detailsMinWidth() { return 4 * 32 + 3 * 11; }

  _detailsCell(row) {
    const boring = row._kind === 'boring' ? row : (this.projectBorings || []).find(b => b.recordingId === row.id);
    const audio = row._kind === 'recording' ? (1 + (row.extraRecordings?.length || 0)) : 0;
    const lagen = boring?.layers?.length || 0;
    const grond = boring?.monsters?.length || 0;
    const pb = boring?.peilbuizen?.length || 0;
    const water = boring?.watermonsters?.length || 0;
    const afw = boring?.afwerking?.fillings?.length || 0;
    const fotos = boring?.photos?.length || 0;
    const chip = (icon, n, title) => html`<span class="detail-chip" data-tip=${title} style="color:${n ? 'var(--mv-text-sub)' : 'var(--mv-text-muted)'}; opacity:${n ? 1 : 0.4};">${unsafeSVG(lucideIcon(icon, 13))}${n}</span>`;
    // Op één regel als er ruimte is; anders teruglopen, maar nooit naar drie regels: de
    // minimale breedte is die van vier tellers, dus hoogstens 4 + 3. Een smallere kolom dan
    // dat maakt de tabel niet, die rekt hem op tot deze breedte.
    return html`<div style="display:flex; flex-wrap:wrap; gap:7px 11px; align-items:center; min-width:${this._detailsMinWidth}px;" @mousemove=${tipMove} @mouseleave=${tipHide}>
      ${chip(FileAudio, audio, 'Audio-opnamen')}
      ${chip(Layers, lagen, 'Lagen')}
      ${chip(FlaskConical, grond, 'Grondmonsters')}
      ${chip(TestTube, pb, 'Peilbuizen')}
      ${chip(Droplets, water, 'Watermonsters')}
      ${chip(PaintBucket, afw, 'Afwerking')}
      ${chip(ImageIcon, fotos, "Foto's")}
    </div>`;
  }
  _recStatusBadge(r) {
    const s = r.status;
    if (['transcribing', 'processing', 'syncing', 'uploading', 'pending', 'queued'].includes(s)) {
      return html`<span style="display:inline-flex; align-items:center; gap:4px; font-size:0.72rem; font-weight:700; color:#FFA800;"><span class="spin-icon" style="display:inline-flex; animation:rotate 2s linear infinite;">${unsafeSVG(lucideIcon(Loader2, 12))}</span> ${this.getStatusLabel(s)}${r.progress ? ' ' + r.progress + '%' : ''}</span>`;
    }
    if (s === 'processed' || s === 'complete') return html`<span style="display:inline-flex; align-items:center; gap:4px; font-size:0.75rem; font-weight:700; color:var(--mv-primary); background:var(--mv-primary-tint); padding:2px 8px; border-radius:6px;">${unsafeSVG(lucideIcon(CheckCircle2, 13))} Verwerkt</span>`;
    if (s === 'error') return html`<span title="${r.error || ''}" style="display:inline-flex; align-items:center; gap:4px; font-size:0.75rem; font-weight:700; color:var(--mv-danger); background:var(--mv-danger-bg); padding:2px 8px; border-radius:6px;">${unsafeSVG(lucideIcon(XCircle, 13))} Fout</span>`;
    return html`<span style="display:inline-flex; align-items:center; gap:4px; font-size:0.75rem; color:var(--mv-text-sub);">${unsafeSVG(lucideIcon(Clock, 13))} Wachten</span>`;
  }
  /** De boring achter een rij: bij een opnamerij is dat de boring die eruit voortkwam. */
  _boringOf(row) {
    return row._kind === 'boring'
      ? row
      : (this.projectBorings || []).find((b) => b.recordingId === row.id) || null;
  }

  /** Het leesbare type van een rij, of null. Ook gebruikt om de kolom op te sorteren. */
  /** Het aanmaaktijdstip van de boring achter een rij (anders dat van de opname). */
  _createdOf(row) {
    const b = this._boringOf(row);
    return b?.createdAt || b?.created_at || row.created_at || row.createdAt || null;
  }

  _groundLevelLabel(row) {
    const code = this._boringOf(row)?.groundLevelType;
    return code ? (nen6693.MAAIVELDTYPE.find((m) => m.code === code)?.label || code) : '';
  }

  _boringTypeLabel(row) {
    return getBoringTypeLabel(this._boringOf(row)?.type) || null;
  }

  /**
   * Het type onderzoek van de boring.
   *
   * Deze kolom verving "Verwerkt", dat bij vrijwel elke rij hetzelfde zei. Het type verschilt
   * wél per boring en is waar je op zoekt zodra er sleuven en gewone boringen door elkaar
   * staan. De AI-status is nu een eigen kolom, standaard uit.
   *
   * Geen type gekozen → een waarschuwingsdriehoekje bij de tekst. Zonder dat teken leest
   * "Geen type" als een mededeling; met dat teken als iets dat je nog moet doen -- en dat is
   * het ook, want het type bepaalt de TerraIndex-code en of de sleufvelden verschijnen.
   *
   * Bij een opname die nog niet verwerkt is staat er nog geen boring, dus ook geen type. Daar
   * hoort een streepje en geen waarschuwing: er is niets vergeten, het moet nog komen.
   */
  _boringTypeCell(row) {
    if (row._kind === 'recording' && !['processed', 'complete'].includes(row.status)) {
      return html`<span title="Nog niet verwerkt" style="color:var(--mv-text-sub);">—</span>`;
    }
    const label = this._boringTypeLabel(row);
    if (!label) {
      return html`<span title="Geen type onderzoek gekozen — dit bepaalt de TerraIndex-code van het meetpunt"
        style="display:inline-flex; align-items:center; gap:4px; font-size:0.75rem; color:#D97706;">
        ${unsafeSVG(lucideIcon(AlertTriangle, 13))} Geen type</span>`;
    }
    return html`<span title=${label} style="display:inline-block; max-width:100%; font-size:0.75rem; font-weight:600; color:var(--mv-text-sub); background:var(--mv-surface-alt); padding:2px 8px; border-radius:6px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; vertical-align:middle;">${label}</span>`;
  }

  /** Potlood in de lijst: klap de rij uit én open de eerste boring in bewerk-modus.
   *  De autoEdit-flag wordt na één tick weer gewist (edge-trigger in de child), zodat
   *  een gewone rij-klik de bewerk-modus niet opnieuw opent. */
  _editBoringRow(row) {
    row.expanded = true;
    this._autoEditRowId = row.id;
    this.requestUpdate();
    setTimeout(() => { this._autoEditRowId = null; }, 0);
  }

  _renderCombinedDetail(row) {
    if (row._kind === 'recording') {
      const rb = (this.projectBorings || []).filter(b => b.recordingId === row.id);
      return html`
        <recording-row-detail
          .recording=${row}
          .extraRecordings=${row.extraRecordings || []}
          .projectId=${row.projectId || this.projectId}
          .coordinateSystem=${this._coordSystemFor(row.projectId)}
          .heightDatum=${this._heightDatumFor(row.projectId)}
          .standard=${this._standardForRow(row)}
          ?grainSizeInput=${this._grainSizeInputFor(row.projectId)}
          .templateDefaults=${this._templateDefaultsFor(row.projectId)}
          .borings=${rb}
          .initialTab=${this._openBoringTab}
          ?autoEdit=${this._autoEditRowId === row.id}
          @borings-changed=${() => this._loadBorings()}
        ></recording-row-detail>`;
    }
    return html`
      <recording-row-detail
        .recording=${{ id: row.id, projectId: row.projectId, filename: '', status: 'processed' }}
        .projectId=${row.projectId || this.projectId}
        .coordinateSystem=${this._coordSystemFor(row.projectId)}
        .heightDatum=${this._heightDatumFor(row.projectId)}
        .standard=${this._standardForRow(row)}
        ?grainSizeInput=${this._grainSizeInputFor(row.projectId)}
        .templateDefaults=${this._templateDefaultsFor(row.projectId)}
        .borings=${[row]}
        .initialTab=${this._openBoringTab}
        ?boringOnly=${true}
        ?autoEdit=${this._autoEditRowId === row.id}
        @borings-changed=${() => this._loadBorings()}
      ></recording-row-detail>`;
  }
  // ── Snel bewerken in de boringenlijst ─────────────────────────────────────
  //
  // Klik op een waarde → een invoerveld (naam, datum/tijd), een keuzelijst (type, boormeester,
  // maaiveldtype) of tegels om aan/uit te zetten (deellocatie). Enter of wegklikken slaat op,
  // Escape breekt af. Alleen voor rijen met een boring: een opname die nog verwerkt wordt heeft
  // er nog geen.

  /** Een cel die op klik bewerkbaar wordt. `editor` tekent de bewerkstand. */
  _editable(row, col, display, editor) {
    const boring = this._boringOf(row);
    if (!boring) return display;
    if (this._cell?.id === boring.id && this._cell.col === col) {
      return html`<div class="ie-edit" @click=${(e) => e.stopPropagation()}>${editor(boring)}</div>`;
    }
    return html`<span class="ie" title="Klik om te wijzigen"
      @click=${(e) => { e.stopPropagation(); this._startCell(boring, col); }}>${display}</span>`;
  }

  async _startCell(boring, col) {
    const value = {
      name: boring.name || '',
      boringType: normalizeBoringType(boring.type) || '',
      boormeester: boring.boormeester || '',
      groundLevelType: boring.groundLevelType || '',
      created: this._toLocalInput(boring.createdAt || boring.created_at),
      sublocation: [...(boring.sublocationIds || [])],
    }[col];
    this._cell = { id: boring.id, col, value };
    // Wegklikken = klaar. Pas na deze klik luisteren, anders sluit hij meteen weer.
    setTimeout(() => {
      this._cellOutside ||= (e) => {
        if (e.composedPath().some((el) => el.classList?.contains('ie-edit'))) return;
        this._commitCell();
      };
      document.addEventListener('mousedown', this._cellOutside, true);
    }, 0);
    await this.updateComplete;
    // De cel staat in de schaduw-DOM van mv-table.
    const table = this.renderRoot.querySelector('mv-table');
    await table?.updateComplete;
    const el = table?.renderRoot.querySelector('.ie-edit input, .ie-edit select');
    el?.focus();
    if (el?.select && el.type === 'text') el.select();
  }

  _closeCell() {
    this._cell = null;
    if (this._cellOutside) document.removeEventListener('mousedown', this._cellOutside, true);
  }

  /** 'JJJJ-MM-DDTUU:MM' in lokale tijd, voor een datetime-local-veld. */
  _toLocalInput(value) {
    const d = parseTimestamp(value);
    if (!d) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  /** De bewerkte cel opslaan (als er iets veranderde) en sluiten. */
  async _commitCell(value = this._cell?.value) {
    const c = this._cell;
    if (!c) return;
    this._closeCell();
    const boring = (this.projectBorings || []).find((b) => b.id === c.id);
    if (!boring) return;
    let patch = null;
    if (c.col === 'name') {
      const v = String(value || '').trim();
      if (!v) { showAlert('Een boring heeft een naam nodig.'); return; }
      if (v !== boring.name) patch = { name: v };
    } else if (c.col === 'boringType') {
      if ((value || '') !== (normalizeBoringType(boring.type) || '')) patch = { type: value || null };
    } else if (c.col === 'boormeester') {
      if ((value || '') !== (boring.boormeester || '')) patch = { boormeester: value || null };
    } else if (c.col === 'groundLevelType') {
      if ((value || '') !== (boring.groundLevelType || '')) patch = { groundLevelType: value || null };
    } else if (c.col === 'created') {
      const d = value ? new Date(value) : null;
      if (d && !Number.isNaN(d.getTime()) && value !== this._toLocalInput(boring.createdAt || boring.created_at)) {
        patch = { createdAt: d.toISOString() };
      }
    }
    if (!patch) return;
    // Meteen tonen; daarna opslaan en de lijst verversen. Mislukt het, dan zet het herladen
    // de oude waarde terug.
    const before = { ...boring };
    Object.assign(boring, patch, patch.createdAt ? { createdAt: patch.createdAt.replace('T', ' ').slice(0, 19) } : {});
    this.requestUpdate();
    try {
      await api.updateBoring(boring.id, patch);
    } catch (e) {
      Object.assign(boring, before);
      showAlert('Opslaan mislukt: ' + (e.message || e));
    }
    this._loadBorings();
  }

  /** Deellocatie aan- of uitzetten: meteen opslaan, de cel blijft open voor de volgende. */
  async _toggleCellSublocation(boring, id) {
    const now = boring.sublocationIds || [];
    const next = now.includes(id) ? now.filter((x) => x !== id) : [...now, id];
    boring.sublocationIds = next;
    if (this._cell) this._cell = { ...this._cell, value: next };
    this.requestUpdate();
    try {
      await api.setBoringSublocations(boring.id, next);
    } catch (e) {
      boring.sublocationIds = now;
      if (this._cell) this._cell = { ...this._cell, value: now };
      showAlert('Opslaan mislukt: ' + (e.message || e));
    }
  }

  /** Toetsen in een invoerveld van de lijst: Enter = opslaan, Escape = afbreken. */
  _cellKeys(e) {
    if (e.key === 'Enter') { e.preventDefault(); this._commitCell(e.target.value); }
    if (e.key === 'Escape') { e.preventDefault(); this._closeCell(); }
  }

  _renderCombinedTab() {
    if (this._loadingBorings || this._loadingRecordings) {
      return html`<div class="card" style="padding:0;">${[1, 2, 3].map(() => html`
        <div class="skeleton-row">
          <div style="width:36px; height:36px; border-radius:8px; flex-shrink:0;" class="skeleton"></div>
          <div style="flex:1; min-width:0; display:flex; flex-direction:column; gap:6px;">
            <div style="width:45%; height:13px;" class="skeleton"></div><div style="width:25%; height:11px;" class="skeleton"></div>
          </div>
          <div style="width:60px; height:12px;" class="skeleton"></div>
        </div>`)}</div>`;
    }
    const rows = this._combinedRows();
    // Ook zonder boringen staat 'Nieuwe boring' er. Hij hing eerst aan de lijst, dus in een
    // gloednieuw project -- precies waar je hem nodig hebt -- was er geen knop en verwees de
    // tekst naar iets wat nergens stond.
    if (!rows.length) {
      return html`
        ${this._renderListToolbar({ actions: this._renderNewBoringButton(), search: false })}
        <div style="padding:2rem; text-align:center; color:var(--mv-text-muted);">Nog geen boringen of opnamen. Voeg audio toe of maak handmatig een boring aan.</div>`;
    }

    const q = this._boringsQuery.trim().toLowerCase();
    const filtered = q ? rows.filter(row => this._combinedName(row).toLowerCase().includes(q)) : rows;
    const created = (row) => String(row.created_at || row.createdAt || '');
    const sorted = this._boringsSort === 'name'
      ? [...filtered].sort((a, b) => this._combinedName(a).localeCompare(this._combinedName(b), 'nl', { numeric: true, sensitivity: 'base' }))
      : [...filtered].sort((a, b) => created(b).localeCompare(created(a)));
    // Geen sorteerknop: in deze lijst sorteer je door op een kolomkop te klikken.
    const toolbar = this._renderListToolbar({
      actions: this._renderBoringActions(),
      query: this._boringsQuery, placeholder: 'Zoek op boring of opname…',
      onQuery: (v) => { this._boringsQuery = v; },
    });
    if (!sorted.length) return html`${toolbar}<div style="padding:2rem; text-align:center; color:var(--mv-text-muted);">Niets gevonden voor "${this._boringsQuery.trim()}".</div>`;

    return html`
      ${toolbar}
      <div class="card" style="padding:0;" @borings-changed=${() => this._loadBorings()}>
        <mv-table
          ?expandable=${true}
          .columns=${[
            ...([
            { id: 'name', title: 'Naam', width: '160px',
              sortValue: (row) => this._combinedName(row),
              render: (row) => {
                const sub = this._subprojectName(row.projectId);
                const boringId = row._kind === 'boring' ? row.id : (this.projectBorings || []).find(b => b.recordingId === row.id)?.id;
                const bulk = boringId && this._bulkUpdatedIds?.has(boringId);
                return html`<div style="display:flex; flex-direction:column; gap:5px; align-items:flex-start;">
                  <span style="font-weight:600; color:var(--mv-text); display:flex; align-items:center; gap:6px;">${this._editable(row, 'name', this._combinedName(row), () => html`
                    <input class="ie-input" type="text" .value=${this._cell.value} maxlength="60"
                      @input=${(e) => { this._cell.value = e.target.value; }}
                      @keydown=${(e) => this._cellKeys(e)}>`)}
                    ${bulk ? html`<span style="font-size:0.68rem; font-weight:700; color:var(--mv-primary); background:var(--mv-primary-tint); padding:1px 7px; border-radius:6px;">Bijgewerkt</span>` : ''}</span>
                  ${sub ? this._subprojectBadge(sub) : ''}
                </div>`;
              } },
            { id: 'sublocation', title: 'Deellocatie', width: '120px',
              sortValue: (row) => this._rowSublocations(row).map((s) => s.name).join(', '),
              render: (row) => {
                const subs = this._rowSublocations(row);
                const display = subs.length
                  ? html`<div style="display:flex; flex-wrap:wrap; gap:4px;">${subs.map((s) => html`
                      <span title=${s.description || s.name} style="font-size:0.72rem; font-weight:700; color:#c2410c; background:#fff7f2; border:1px solid #fed7c3; padding:1px 7px; border-radius:6px; white-space:nowrap;">${s.name}</span>`)}</div>`
                  : html`<span style="color:var(--mv-text-muted); font-size:0.8rem;">—</span>`;
                // Meer dan één mogelijk (overlappende vlakken): tegels aan/uit, elke tik slaat op.
                return this._editable(row, 'sublocation', display, (boring) => html`
                  <div style="display:flex; flex-wrap:wrap; gap:4px; align-items:center;">
                    ${this._sublocations.map((s) => {
                      const on = (boring.sublocationIds || []).includes(s.id);
                      return html`<button type="button" class="ie-chip ${on ? 'on' : ''}" @click=${() => this._toggleCellSublocation(boring, s.id)}>${s.name}</button>`;
                    })}
                    <button type="button" class="ie-done" @click=${() => this._closeCell()}>Klaar</button>
                  </div>`);
              } },
            { id: 'profiel', title: 'Profiel', render: (row) => {
                const boring = row._kind === 'boring' ? row : (this.projectBorings || []).find(b => b.recordingId === row.id);
                if (row._kind === 'recording' && !boring) {
                  if (['transcribing', 'processing', 'syncing'].includes(row.status)) return html`<span style="font-size:0.78rem; color:#B45309;">AI-analyse loopt…</span>`;
                  return html`<span style="color:var(--mv-text-muted); font-size:0.8rem;">${row.status === 'error' ? 'Geen data' : '—'}</span>`;
                }
                const warnings = boring ? this._getBoringWarnings(boring) : [];
                return html`<div style="display:flex; flex-direction:column; gap:4px;">${this._layerBar(boring)}${this._renderWarningBadges(warnings)}</div>`;
              } },
            { id: 'details', title: 'Details', render: (row) => this._detailsCell(row) },
            { id: 'created', title: 'Datum/tijd aangemaakt', width: '170px',
              // Als getal sorteren, niet als tekst: '9/08' hoort vóór '10/08'.
              // Het tijdstip van de boring (ook bij een opnamerij): dat is wat je hier aanpast.
              sortValue: (row) => parseTimestamp(this._createdOf(row))?.getTime() ?? null,
              render: (row) => this._editable(row, 'created', html`<span style="font-size:0.8rem; color:var(--mv-text-sub); white-space:nowrap;">
                ${formatDateTimeSlash(this._createdOf(row))}</span>`, () => html`
                <input class="ie-input" type="datetime-local" .value=${this._cell.value}
                  @input=${(e) => { this._cell.value = e.target.value; }}
                  @keydown=${(e) => this._cellKeys(e)}>`),
            },
            {
              id: 'boringType', title: 'Type', width: '170px',
              sortValue: (row) => this._boringTypeLabel(row) || '',
              render: (row) => this._editable(row, 'boringType', this._boringTypeCell(row), () => html`
                <select class="ie-input" @change=${(e) => this._commitCell(e.target.value)} @keydown=${(e) => this._cellKeys(e)}>
                  <option value="" ?selected=${!this._cell.value}>— geen type —</option>
                  ${BORING_TYPE.map((o) => html`<option value=${o.code} ?selected=${o.code === this._cell.value}>${o.label}</option>`)}
                </select>`),
            },
            {
              id: 'boormeester', title: 'Boormeester', width: '160px',
              sortValue: (row) => this._boringOf(row)?.boormeester || '',
              render: (row) => this._editable(row, 'boormeester',
                html`<span style="font-size:0.8rem; color:${this._boringOf(row)?.boormeester ? 'var(--mv-text)' : 'var(--mv-text-muted)'};">${this._boringOf(row)?.boormeester || '—'}</span>`,
                () => html`
                <select class="ie-input" @change=${(e) => this._commitCell(e.target.value)} @keydown=${(e) => this._cellKeys(e)}>
                  <option value="" ?selected=${!this._cell.value}>— geen —</option>
                  ${this._boormeesterOptions(this._cell.value).map((n) => html`<option value=${n} ?selected=${n === this._cell.value}>${n}</option>`)}
                </select>`),
            },
            {
              id: 'groundLevelType', title: 'Maaiveldtype', width: '150px',
              sortValue: (row) => this._groundLevelLabel(row),
              render: (row) => this._editable(row, 'groundLevelType',
                html`<span style="font-size:0.8rem; color:${this._groundLevelLabel(row) ? 'var(--mv-text)' : 'var(--mv-text-muted)'};">${this._groundLevelLabel(row) || '—'}</span>`,
                () => html`
                <select class="ie-input" @change=${(e) => this._commitCell(e.target.value)} @keydown=${(e) => this._cellKeys(e)}>
                  <option value="" ?selected=${!this._cell.value}>— geen —</option>
                  ${nen6693.MAAIVELDTYPE.map((m) => html`<option value=${m.code} ?selected=${m.code === this._cell.value}>${m.label}</option>`)}
                </select>`),
            },
            {
              id: 'aiStatus', title: 'Status AI', width: '130px',
              render: (row) => (row._kind === 'recording' ? this._recStatusBadge(row) : ''),
            },
            // Alleen de kolommen die de gebruiker aan heeft staan.
            ].filter((c) => this._visibleColumns.includes(c.id) && (c.id !== 'sublocation' || this._sublocations.length))),
            // Acties staan altijd in de tabel: geen inhoud maar bediening. Het tandwiel zit in
            // de laatste kolom, dus uitzetbaar maken zou de kiezer zelf kunnen laten
            // verdwijnen zonder weg terug.
            { id: '_actions', title: '', width: '92px', render: (row) => {
                const hasBoring = row._kind === 'boring' || (this.projectBorings || []).some(b => b.recordingId === row.id);
                // De boring achter deze rij: bij een opnamerij is dat de boring die eruit
                // voortkwam. Dupliceren werkt op de boring, niet op de opname.
                const dupSource = row._kind === 'boring' ? row : (this.projectBorings || []).find(b => b.recordingId === row.id);
                return html`<div style="display:flex; gap:2px; justify-content:flex-end; align-items:center;">
                  ${hasBoring ? html`<button @click=${(e) => { e.stopPropagation(); this._editBoringRow(row); }} style="background:none; border:none; cursor:pointer; color:var(--mv-text-muted); padding:4px;" title="Boring bewerken">
                    ${unsafeSVG(lucideIcon(Pencil, 14))}
                  </button>` : ''}
                  ${dupSource ? html`<button @click=${(e) => { e.stopPropagation(); this._duplicateSource = dupSource; }} style="background:none; border:none; cursor:pointer; color:var(--mv-text-muted); padding:4px;" title="Boring dupliceren">
                    ${unsafeSVG(lucideIcon(CopyPlus, 14))}
                  </button>` : ''}
                  <button @click=${async (e) => {
                    e.stopPropagation();
                    if (row._kind === 'recording') { this.deleteRecording(row); return; }
                    const ok = await confirmDialog({ title: 'Boring verwijderen', message: `Weet je zeker dat je boring "${row.name}" wilt verwijderen?`, confirmLabel: 'Verwijderen', danger: true });
                    if (!ok) return;
                    try { await api.deleteBoring(row.id); await this._loadBorings(); } catch (err) { showAlert('Verwijderen mislukt: ' + err.message); }
                  }} style="background:none; border:none; cursor:pointer; color:var(--mv-danger,#EF4444); padding:4px;" title="Verwijderen">
                    ${unsafeSVG(lucideIcon(Trash2, 14))}
                  </button>
                </div>`;
              } },
          ]}
          .data=${sorted}
          .columnOptions=${this._boringColumnOptions}
          .visibleColumns=${this._visibleColumns}
          @columns-changed=${(e) => this._saveColumnPrefs(e.detail.visible)}
          .renderDetails=${(row) => this._renderCombinedDetail(row)}
        ></mv-table>
      </div>
    `;
  }

  _renderBoringenTab() {
    const borings = this.projectBorings || [];
    if (this._loadingBorings) {
      return html`
        <div class="card" style="padding:0;">
          ${[1,2,3].map(() => html`
            <div class="skeleton-row">
              <div style="width:36px; height:36px; border-radius:8px; flex-shrink:0;" class="skeleton"></div>
              <div style="flex:1; min-width:0; display:flex; flex-direction:column; gap:6px;">
                <div style="width:45%; height:13px;" class="skeleton"></div>
                <div style="width:25%; height:11px;" class="skeleton"></div>
              </div>
              <div style="width:60px; height:12px;" class="skeleton"></div>
            </div>
          `)}
        </div>
      `;
    }
    if (borings.length === 0) return html`<div style="padding: 2rem; text-align: center; color: var(--mv-text-muted);">Nog geen boringen aangemaakt.</div>`;

    const q = this._boringsQuery.trim().toLowerCase();
    const filtered = q ? borings.filter((b) => (b.name || '').toLowerCase().includes(q)) : borings;
    // 'created' keeps the natural (creation) order; 'name' sorts alphabetically.
    const sorted = this._boringsSort === 'name'
      ? [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl', { numeric: true, sensitivity: 'base' }))
      : filtered;
    const toolbar = this._renderListToolbar({
      query: this._boringsQuery,
      sort: this._boringsSort,
      placeholder: 'Zoek op boring…',
      onQuery: (v) => { this._boringsQuery = v; },
      onSort: (v) => { this._boringsSort = v; },
    });

    if (sorted.length === 0) return html`${toolbar}<div style="padding:2rem; text-align:center; color:var(--mv-text-muted);">Geen boring gevonden voor "${this._boringsQuery.trim()}".</div>`;
    return html`
      ${toolbar}
      <div class="card" style="padding: 0;"
           @borings-changed=${() => this._loadBorings()}>
        <mv-table
          ?expandable=${true}
          .columns=${[
            { id: 'name', title: 'Naam', width: '140px', render: (row) => {
                const sub = this._subprojectName(row.projectId);
                return html`<div style="display:flex; flex-direction:column; gap:5px; align-items:flex-start;">
                  <span style="font-weight:600; color:var(--mv-text);">${row.name}</span>
                  ${sub ? this._subprojectBadge(sub) : ''}
                </div>`;
              } },
            {
              id: '_visual', title: 'Profiel',
              render: (row) => {
                const bm = BoringModel.fromFlat(row);
                const warnings = this._getBoringWarnings(row);
                const std = this._standardForRow(row);
                const profile = bm.layers.length ? (() => {
                  const maxDepth = Math.max(bm.maxDepth * 100 || 100, 100);
                  return html`
                    <div style="display:flex; align-items:center; gap:6px;">
                      <div style="flex:1; min-width:80px; height:12px; border-radius:3px; overflow:hidden; display:flex; background:var(--mv-bg); border:1px solid var(--mv-border);">
                        ${bm.layers.map(l => {
                          const w = ((l.lowerDepth - l.upperDepth) / maxDepth) * 100;
                          return html`<div style="width:${w}%; height:100%; background:${l.colorHex}; border-right:1px solid rgba(0,0,0,0.05);" title="${l.upperDepth}–${l.lowerDepth} cm: ${this._soilTerm(l, std)}"></div>`;
                        })}
                      </div>
                      <span style="font-size:0.65rem; color:var(--mv-text-muted); font-weight:600; white-space:nowrap;">${Math.round(maxDepth)} cm</span>
                    </div>
                  `;
                })() : html`<span style="color:var(--mv-text-muted); font-size:0.8rem;">—</span>`;
                return html`
                  <div style="display:flex; flex-direction:column; gap:4px;">
                    ${profile}
                    ${this._renderWarningBadges(warnings)}
                  </div>
                `;
              }
            },
            {
              id: '_recording', title: 'Opname',
              render: (row) => {
                const rec = (this.recordings || []).find(r => r.id === row.recordingId);
                if (!rec) return html`<span style="color:var(--mv-text-muted); font-size:0.8rem;">—</span>`;
                return html`<button @click=${async (e) => {
                  e.stopPropagation();
                  (this.recordings || []).forEach(r => { r.expanded = false; });
                  rec.expanded = true;
                  this.activeTab = 'recordings';
                  await this.updateComplete;
                  requestAnimationFrame(() => requestAnimationFrame(() => {
                    this.shadowRoot
                      ?.querySelector('mv-table')
                      ?.shadowRoot
                      ?.querySelector('tr.data-row.expanded')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }));
                }} style="background:none; border:none; padding:0; cursor:pointer; color:var(--mv-primary); font-size:0.85rem; text-decoration:underline; text-underline-offset:2px;">${rec.filename}</button>`;
              }
            },
            {
              id: '_actions', title: '',  width: '40px',
              render: (row) => html`
                <button @click=${async (e) => {
                  e.stopPropagation();
                  const confirmed = await confirmDialog({
                    title: 'Boring verwijderen',
                    message: `Weet je zeker dat je boring "${row.name}" wilt verwijderen?`,
                    confirmLabel: 'Verwijderen',
                    danger: true,
                  });
                  if (!confirmed) return;
                  try { await api.deleteBoring(row.id); await this._loadBorings(); }
                  catch (err) { showAlert('Verwijderen mislukt: ' + err.message); }
                }} style="background:none; border:none; cursor:pointer; color:var(--mv-danger,#EF4444); padding:4px;" title="Verwijderen">
                  ${unsafeSVG(lucideIcon(Trash2, 14))}
                </button>
              `,
            },
          ]}
          .data=${sorted}
          .renderDetails=${(row) => html`
            <recording-row-detail
              .recording=${{ id: row.id, projectId: row.projectId, filename: '', status: 'processed' }}
              .projectId=${row.projectId || this.projectId}
              .coordinateSystem=${this._coordSystemFor(row.projectId)}
              .heightDatum=${this._heightDatumFor(row.projectId)}
              .standard=${this._standardForRow(row)}
              ?grainSizeInput=${this._grainSizeInputFor(row.projectId)}
              .templateDefaults=${this._templateDefaultsFor(row.projectId)}
              .borings=${[row]}
              ?boringOnly=${true}
              @borings-changed=${() => this._loadBorings()}
            ></recording-row-detail>
          `}
        ></mv-table>
      </div>
    `;
  }

  /** Spring naar een boring in de Boringen-tab en klap 'm open. */
  /**
   * Spring naar een boring vanuit de activiteitentegel, eventueel op een specifieke subtab.
   *
   * Let op welke RIJ je uitklapt: een boring die uit een opname komt, wordt als kaart in de
   * opnamerij getekend. De boring zelf staat dan niet als eigen rij in de tabel, dus
   * `expanded` daarop zetten doet niets.
   */
  async _jumpToBoring(guid, tab = null) {
    (this.projectBorings || []).forEach(r => { r.expanded = false; });
    (this.recordings || []).forEach(r => { r.expanded = false; });
    const boring = (this.projectBorings || []).find(b => b.id === guid);
    const row = boring?.recordingId ? (this.recordings || []).find(r => r.id === boring.recordingId) : boring;
    if (row) row.expanded = true;
    // Edge-getriggerd in de child; daarna weer wissen zodat de gebruiker vrij kan klikken.
    this._openBoringTab = (boring && tab) ? { boringId: boring.id, tab } : null;
    // Adres bijwerken zonder geschiedenis-item: wie de pagina deelt, deelt wat hij ziet.
    // replaceState en geen pushState -- anders levert elk open- en dichtklappen een stap op
    // die je met de terugknop moet doorlopen.
    if (boring && this.projectId) {
      const q = new URLSearchParams({ boring: boring.id, ...(tab ? { tab } : {}) });
      window.history.replaceState({}, '', `/projects/${this.projectId}/borings?${q}`);
    }
    this.activeTab = 'borings';   // springen naar een boring hoort in de boringenlijst
    this.requestUpdate();
    await this.updateComplete;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.shadowRoot?.querySelector('mv-table')?.shadowRoot
        ?.querySelector('tr.data-row.expanded')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }));
  }

  /** De drie exportformaten (icoon/titel/omschrijving/bestandsnaam/actie). */
  _exportFormats() {
    const code = this.project?.project_code || 'project';
    return [
      // Heette "SIKB0101 XML", maar deze knop gaat naar /projects/:id/xml en dat is
      // ExportController.terraIndexXml. De SIKB0101-export zit achter een ander eindpunt
      // (/export/sikb) dat op dit scherm nergens wordt aangeroepen -- het label klopte dus niet.
      { id: 'xml', icon: FileJson, title: 'TerraIndex XML', desc: 'Om je project in TerraIndex te importeren.', file: `${code}.xml`, label: this._exportFotos ? "Exporteer XML + foto's" : 'Exporteer XML', action: () => this.downloadCurrentXML(true) },
      { id: 'profiles', icon: Layers, title: 'Boorprofielen', desc: 'Pdf met alleen de boorprofielen.', badge: 'bèta', file: `boorprofielen-${code}.pdf`, label: 'Maak boorprofielen', action: () => this.handleBoorprofielenExport() },
      { id: 'report', icon: FileText, title: 'Algemene rapportage', desc: 'Volledig rapport: kopgegevens, boorbeschrijving, monsters en peilbuizen.', badge: 'bèta', file: `geovoice-rapport-${code}.pdf`, label: 'Maak rapportage', action: () => this.handlePdfExport() },
      { id: 'excel', icon: FileSpreadsheet, title: 'Excel', desc: 'Alle gegevens in tabellen, om te filteren en na te rekenen.', badge: 'bèta', file: `${code}.xlsx`, label: 'Exporteer Excel', action: () => this.handleExcelExport() },
    ];
  }

  /** Kengetallen van het project, afgeleid uit wat er werkelijk in de boringen zit. */
  _projectStats() {
    const borings = this.projectBorings || [];
    const monsters = borings.flatMap((b) => b.monsters || []);
    const peilbuizen = borings.flatMap((b) => b.peilbuizen || []);
    const watermonsters = borings.flatMap((b) => b.watermonsters || []);
    const bottles = watermonsters.flatMap((w) => w.bottles || []);
    const issues = this._issuesByBoring();
    const metMelding = [...issues.values()].filter((list) => list.length).length;
    return {
      borings: borings.length,
      boringsSub: metMelding ? `${metMelding} met melding` : 'alles compleet',
      monsters: monsters.length,
      monstersSub: `${monsters.filter((m) => m.identificatie || m.name).length} met barcode`,
      peilbuizen: peilbuizen.length,
      peilbuizenSub: `${peilbuizen.filter((p) => p.filterTopCm != null && p.filterBottomCm != null).length} met filtertraject`,
      watermonsters: watermonsters.length,
      watermonstersSub: `${bottles.length} fles${bottles.length === 1 ? '' : 'sen'}`,
    };
  }

  /** Meldingen per boring-id, zodat tabel en overzicht dezelfde bron gebruiken. */
  _issuesByBoring() {
    const map = new Map();
    for (const i of this._projectIssues()) {
      if (!map.has(i.boringId)) map.set(i.boringId, []);
      map.get(i.boringId).push(i);
    }
    for (const b of this.projectBorings || []) if (!map.has(b.id)) map.set(b.id, []);
    return map;
  }

  /** Alle meldingen in dit project, blokkerend bovenaan. */
  _projectIssues() {
    return projectIssues(this.projectBorings || [], {
      recordingStatusFor: (b) => (this.recordings || []).find((r) => r.id === b.recordingId)?.status ?? null,
    });
  }

  _issueIcon(code) {
    const icon = {
      'no-coordinates': MapPin,
      'profile-gap': Layers,
      'profile-overlap': Layers,
      'layer-no-soil': Layers,
      'no-layers': Layers,
      'sample-no-barcode': FlaskConical,
      'bottle-no-barcode': FlaskConical,
      'recording-failed': Mic,
      'duplicate-name': Copy,
      'value-out-of-range': AlertTriangle,
    }[code] || AlertTriangle;
    return unsafeSVG(lucideIcon(icon, 14));
  }

  /**
   * Overzicht-tab: waar staat dit project, en wat houdt het tegen.
   *
   * De volgorde is bewust: eerst de omvang (kengetallen), dan wat er MOET gebeuren, dan
   * waar het ligt, dan wat er is gebeurd. Wie het project binnenkomt wil weten of hij kan
   * afronden -- niet wie er gisteren iets wijzigde.
   */
  /**
   * Voer een diepe link uit: open de boring uit de URL op de gevraagde subtab.
   *
   * Pas zodra de boringen binnen zijn -- bij het laden van de pagina is de lijst nog leeg en
   * zou er niets te openen zijn. De focus wordt daarna gewist, zodat je de rij weer dicht
   * kunt klappen zonder dat hij bij de volgende render terugspringt.
   */
  _applyFocus() {
    const f = this.deepLink;
    if (!f?.boringId || this._focusDone === f.boringId) return;
    // Pas opgeven als de boring er ECHT is. De lijst wordt in stappen gevuld -- eerst het
    // project zelf, daarna de deelprojecten -- dus 'lijst is niet leeg' betekent nog niet dat
    // deze boring erbij zit. Vlaggen op de eerste vulling liet een diepe link naar een boring
    // uit een deelproject stil mislukken.
    const found = (this.projectBorings || []).some((b) => b.id === f.boringId);
    if (!found) return;
    this._focusDone = f.boringId;
    this._jumpToBoring(f.boringId, f.tab || null);
  }

  /**
   * Wissel van tab via de URL in plaats van alleen de state.
   *
   * Zo krijgt elke subpagina een eigen adres: de terugknop werkt, je kunt een specifieke tab
   * delen of bookmarken, en het kruimelpad kan tonen waar je bent. Het overzicht is de
   * standaard en krijgt geen achtervoegsel, zodat bestaande links naar /projects/:id blijven
   * werken.
   */
  _goToTab(tab) {
    // Zelf van tab wisselen betekent dat de diepe link niet meer geldt.
    this._openBoringTab = null;
    if (!this.projectId) { this.activeTab = tab; this.requestUpdate(); return; }
    // Zonder query: een andere tab betekent dat de gefocuste boring niet meer van toepassing is.
    const path = tab === 'overzicht' ? `/projects/${this.projectId}` : `/projects/${this.projectId}/${tab}`;
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    this.activeTab = tab;
  }

  _renderOverzichtTab() {
    const st = this._projectStats();
    const issues = this._projectIssues();
    const blocking = issues.filter((i) => i.severity === BLOCKING);

    // `link` = het pijltje naar de bijbehorende pagina, `icon` = een eigen actie op de tegel.
    const kpi = (value, label, sub, link = null, action = null) => html`
      <div style="flex:1; min-width:150px; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:.75rem; padding:1rem 1.15rem; position:relative;">
        ${link ? html`
          <!-- Subtiel pijltje in plaats van een tekstlink: het label herhaalde alleen maar
               wat er onder het getal al staat, en trok meer aandacht dan het getal zelf. -->
          <button @click=${link.onClick} title=${link.label} aria-label=${link.label}
            style="position:absolute; top:.75rem; right:.75rem; background:none; border:none; padding:4px; cursor:pointer;
                   display:inline-flex; color:var(--mv-text-muted); border-radius:6px; transition:color .12s, background .12s;"
            @mouseenter=${(e) => { e.currentTarget.style.color = 'var(--mv-primary)'; e.currentTarget.style.background = 'var(--mv-surface-alt,#f3f4f6)'; }}
            @mouseleave=${(e) => { e.currentTarget.style.color = 'var(--mv-text-muted)'; e.currentTarget.style.background = 'none'; }}>
            ${unsafeSVG(lucideIcon(ChevronRight, 16))}
          </button>` : ''}
        ${action ? html`
          <button @click=${action.onClick} data-tip=${action.label} aria-label=${action.label}
            style="position:absolute; top:.75rem; right:.75rem; background:none; border:none; padding:4px; cursor:pointer;
                   display:inline-flex; color:var(--mv-text-muted); border-radius:6px; transition:color .12s, background .12s;"
            @mouseenter=${(e) => { e.currentTarget.style.color = 'var(--mv-primary)'; e.currentTarget.style.background = 'var(--mv-surface-alt,#f3f4f6)'; }}
            @mouseleave=${(e) => { e.currentTarget.style.color = 'var(--mv-text-muted)'; e.currentTarget.style.background = 'none'; }}>
            ${unsafeSVG(lucideIcon(action.icon, 16))}
          </button>` : ''}
        <div style="font-size:1.75rem; font-weight:700; color:var(--mv-text); line-height:1.1;">${value}</div>
        <div style="font-size:.85rem; color:var(--mv-text-sub); margin-top:2px;">${label}</div>
        <div style="font-size:.75rem; color:var(--mv-text-muted); margin-top:6px;">${sub}</div>
      </div>`;

    return html`
      <div style="display:flex; flex-direction:column; gap:1rem;">
        <div style="display:flex; gap:1rem; flex-wrap:wrap;">
          ${kpi(st.borings, st.borings === 1 ? 'boring' : 'boringen', st.boringsSub,
            { label: 'ga naar boringen', onClick: () => { this.activeTab = 'borings'; } })}
          ${kpi(st.monsters, st.monsters === 1 ? 'grondmonster' : 'grondmonsters', st.monstersSub, null,
            st.monsters ? { label: 'Toon alle grondmonsters', icon: Table, onClick: () => { this._monstersOpen = true; } } : null)}
          ${kpi(st.peilbuizen, st.peilbuizen === 1 ? 'peilbuis' : 'peilbuizen', st.peilbuizenSub, null,
            st.peilbuizen ? { label: 'Toon alle peilbuizen', icon: Table, onClick: () => { this._peilbuizenOpen = true; } } : null)}
          ${kpi(st.watermonsters, st.watermonsters === 1 ? 'watermonster' : 'watermonsters', st.watermonstersSub)}
        </div>

        <!-- Vaste, gelijke hoogte: bij één melding mag de tegel niet inzakken naast de kaart,
             en bij dertig meldingen mag hij de pagina niet uit groeien. De lijst schuift. -->
        <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(340px, 1fr)); gap:1rem; align-items:stretch;">
          <section style="display:flex; flex-direction:column; height:340px; box-sizing:border-box; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:.75rem; padding:1rem 1.15rem;">
            <div style="display:flex; align-items:center; justify-content:space-between;">
              <h3 style="margin:0; font-size:1rem; font-weight:700; color:var(--mv-text);">Vraagt om actie</h3>
              <span style="font-size:.78rem; color:var(--mv-text-muted); font-weight:600;">${issues.length}</span>
            </div>
            <div style="font-size:.75rem; color:var(--mv-text-muted); margin:2px 0 12px;">
              ${blocking.length
                ? 'wat het rapport tegenhoudt staat bovenaan'
                : 'niets blokkeert het rapport'}
            </div>
            ${!issues.length ? html`
              <div style="flex:1; display:flex; align-items:center; justify-content:center; font-size:.85rem; color:var(--mv-text-sub);">
                Alles compleet.
              </div>
            ` : html`
              <div style="flex:1; overflow-y:auto; display:flex; flex-direction:column; margin:0 -2px;">
                ${this._issuesGrouped(issues).map((g) => this._renderIssueGroup(g))}
              </div>`}
          </section>

          <section style="display:flex; flex-direction:column; height:340px; box-sizing:border-box; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:.75rem; padding:1rem 1.15rem;">
            <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:10px;">
              <h3 style="margin:0; font-size:1rem; font-weight:700; color:var(--mv-text);">Kaart</h3>
              <button @click=${() => { this.activeTab = 'map'; }}
                style="background:none; border:none; padding:0; cursor:pointer; font:inherit; font-size:.8rem; font-weight:600; color:var(--mv-primary);">vergroten</button>
            </div>
            <div style="flex:1; min-height:0; border-radius:.5rem; overflow:hidden;">
              <project-map ?compact=${true} .projectId=${this.projectId} .borings=${this._mapBorings()}
                .plannedPoints=${this._plannedPoints || []} .mapLayers=${this._mapLayers || []}
                @open-boring=${(e) => this._openBoringFromMap(e.detail.guid)}
                @planned-point-link=${(e) => this._linkPlannedPoint(e.detail)}></project-map>
            </div>
            ${this._boringsWithoutLocation().length ? html`
              <div style="display:flex; align-items:center; gap:6px; margin-top:10px; font-size:.78rem; color:var(--mv-danger,#EF4444);">
                ${unsafeSVG(lucideIcon(MapPin, 13))}
                ${this._boringsWithoutLocation().length === 1
                  ? html`${this._boringsWithoutLocation()[0].name} heeft geen locatie`
                  : html`${this._boringsWithoutLocation().length} boringen hebben geen locatie`}
              </div>` : ''}
          </section>
        </div>

        ${this._monstersOpen ? this._renderMonstersModal() : ''}
        ${this._peilbuizenOpen ? this._renderPeilbuizenModal() : ''}

        <activity-tile .projectId=${this.project?.id} .refreshSignal=${this._activitySignal || 0}
          .soilStandard=${this.soilStandardValue || DEFAULT_SOIL_STANDARD}
          @open-boring=${(e) => this._jumpToBoring(e.detail.boringId, e.detail.tab)}></activity-tile>
      </div>`;
  }

  /**
   * Alle grondmonsters van het project in één tabel.
   *
   * De monsters staan verspreid over de boringen; wie wil weten wat er in totaal is genomen,
   * of een barcode wil terugzoeken, moest elke boring opendoen. Hier staan ze onder elkaar,
   * met de boring erbij zodat elk monster te plaatsen is.
   */
  _renderMonstersModal() {
    // Per boring gegroepeerd: de boringnaam staat één keer als kopregel in plaats van bij
    // elk monster opnieuw. Vier keer 'B99-PB03' onder elkaar leest als ruis en verbergt juist
    // waar de ene boring ophoudt en de volgende begint.
    const groups = (this.projectBorings || [])
      .map((b) => ({
        name: b.name || 'Naamloos',
        monsters: [...(b.monsters || [])].sort((x, y) => (x.upperDepth ?? 0) - (y.upperDepth ?? 0)),
      }))
      .filter((g) => g.monsters.length)
      .sort((a, b) => a.name.localeCompare(b.name, 'nl', { numeric: true }));
    const total = groups.reduce((n, g) => n + g.monsters.length, 0);

    // Maten en kleuren overgenomen van mv-table, zodat dit venster niet als een ander
    // component oogt dan de peilbuistabel ernaast.
    const th = 'text-align:left; padding:0.9rem 1.5rem; font-size:0.75rem; font-weight:700; text-transform:uppercase; letter-spacing:0.06rem; color:var(--mv-text-muted); border-bottom:1px solid var(--mv-border); position:sticky; top:0; background:var(--mv-surface); white-space:nowrap;';
    const td = 'padding:1rem 1.5rem; vertical-align:middle; font-size:0.9rem; font-weight:500; color:var(--mv-text-sub); border-bottom:1px solid var(--mv-border); white-space:nowrap;';
    const depth = (m) => (m.upperDepth == null && m.lowerDepth == null)
      ? '—' : `${m.upperDepth ?? '?'}–${m.lowerDepth ?? '?'} cm`;

    return html`
      <div @click=${() => { this._monstersOpen = false; }}
        style="position:fixed; inset:0; z-index:var(--mv-z-modal); background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:5vh 1rem;">
        <div @click=${(e) => e.stopPropagation()}
          style="background:var(--mv-surface); border-radius:.8rem; width:min(920px,100%); max-height:90vh; display:flex; flex-direction:column; box-shadow:0 24px 60px rgba(0,0,0,.25);">
          <div style="display:flex; align-items:center; gap:10px; padding:1rem 1.25rem .75rem; border-bottom:1px solid var(--mv-border);">
            <h3 style="margin:0; font-size:1rem; font-weight:700; color:var(--mv-text);">Grondmonsters</h3>
            <span style="font-size:.78rem; color:var(--mv-text-muted); font-weight:600;">${total}</span>
            <button @click=${() => { this._monstersOpen = false; }} title="Sluiten"
              style="margin-left:auto; background:none; border:none; cursor:pointer; font-size:1.4rem; line-height:1; color:var(--mv-text-sub); padding:0 2px;">&times;</button>
          </div>
          <div style="overflow:auto; padding:15px 1.25rem 1rem;">
            ${!groups.length ? html`
              <div style="padding:18px 0; font-size:.85rem; color:var(--mv-text-sub);">Nog geen grondmonsters in dit project.</div>
            ` : html`
              <table style="width:100%; border-collapse:collapse; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:0.75rem; overflow:hidden;">
                <thead>
                  <tr>
                    <th style=${th}>Naam</th>
                    <th style=${th}>Barcode</th>
                    <th style=${th}>Diepte</th>
                    <th style="${th} text-align:center;">Geroerd</th>
                    <th style=${th}>Verpakking</th>
                  </tr>
                </thead>
                ${groups.map((g) => html`
                  <tbody>
                    <tr>
                      <td colspan="5" style="padding:0.7rem 1.5rem; border-bottom:1px solid var(--mv-border); background:var(--mv-surface-alt);">
                        <span style="font-weight:700; font-size:0.9rem; color:var(--mv-text);">${g.name}</span>
                        <span style="margin-left:8px; font-size:0.78rem; color:var(--mv-text-muted);">
                          ${g.monsters.length} monster${g.monsters.length === 1 ? '' : 's'}
                        </span>
                      </td>
                    </tr>
                    ${g.monsters.map((m) => html`
                      <tr>
                        <td style=${td}>${m.name || '—'}</td>
                        <td style="${td} font-family:ui-monospace, monospace; font-size:.8rem;">${m.identificatie || '—'}</td>
                        <td style=${td}>${depth(m)}</td>
                        <td style="${td} text-align:center;">
                          ${m.geroerd == null
                            ? html`<span style="color:var(--mv-text-muted);">—</span>`
                            : m.geroerd
                              ? html`<span style="color:var(--mv-primary); display:inline-flex;">${unsafeSVG(lucideIcon(Check, 15))}</span>`
                              : html`<span style="color:var(--mv-danger,#EF4444); display:inline-flex;">${unsafeSVG(lucideIcon(X, 15))}</span>`}
                        </td>
                        <td style=${td}>${monsterVerpakkingLabel(m.packingType) || '—'}</td>
                      </tr>`)}
                  </tbody>`)}
              </table>`}
          </div>
        </div>
      </div>`;
  }

  /**
   * Alle peilbuizen van het project in één tabel, met een kolomkiezer.
   *
   * Zelfde reden als bij de grondmonsters: ze staan verspreid over de boringen. De zeven
   * basiskolommen beantwoorden 'wat staat er waar en wat mat je bij plaatsing'; de rest is
   * detail dat je er via het tandwiel bij zet.
   */
  _renderPeilbuizenModal() {
    const rows = (this.projectBorings || []).flatMap((b) =>
      (b.peilbuizen || []).map((p) => ({ ...p, boringName: b.name || 'Naamloos' })));
    rows.sort((a, b) => a.boringName.localeCompare(b.boringName, 'nl', { numeric: true })
      || String(a.name || '').localeCompare(String(b.name || ''), 'nl', { numeric: true }));

    const num = (v, unit = '') => (v == null || v === '' ? '—' : `${v}${unit}`);
    // Ja/nee/onbekend: een kruisje bij ontbrekende data zou een bewering doen die er niet is.
    const yesNo = (v) => (v == null ? '—' : (v ? 'Ja' : 'Nee'));
    const code = (field, v) => (v == null || v === '' ? '—' : (codeLabel(field, v, this.soilStandardValue) || v));

    const base = [
      { id: 'boringName', title: 'Boring', sortValue: (r) => r.boringName, render: (r) => html`<span style="font-weight:600;">${r.boringName}</span>` },
      { id: 'name', title: 'Peilbuis', sortValue: (r) => r.name || '', render: (r) => r.name || '—' },
      { id: 'filter', title: 'Filter', render: (r) => (r.filterTopCm == null && r.filterBottomCm == null)
          ? '—' : `${r.filterTopCm ?? '?'}–${r.filterBottomCm ?? '?'} cm` },
      { id: 'ph', title: 'pH', render: (r) => num(r.ph) },
      { id: 'ec', title: 'EGV bij plaatsing', render: (r) => num(r.ec) },
      { id: 'temperature', title: 'Temperatuur', render: (r) => num(r.temperature, ' °C') },
      { id: 'pumpedL', title: 'Afgepompt (l)', render: (r) => num(r.pumpedL) },
    ];
    const extra = {
      referencePointType: { title: 'Referentievlak', render: (r) => code('referencePointType', r.referencePointType) },
      material: { title: 'Materiaal', render: (r) => code('material', r.material) },
      innerDiameterMm: { title: 'Binnendiameter', render: (r) => num(r.innerDiameterMm, ' mm') },
      diameter: { title: 'Buitendiameter', render: (r) => num(r.diameter, ' mm') },
      filterSieveUsed: { title: 'Filterkous', render: (r) => yesNo(r.filterSieveUsed) },
      unwatered: { title: 'Leeggezogen', render: (r) => yesNo(r.unwatered) },
      rinsed: { title: 'Gespoeld', render: (r) => yesNo(r.rinsed) },
      flowType: { title: 'Doorloop', render: (r) => code('flowType', r.flowType) },
      ecWorkWater: { title: 'EGV werkwater', render: (r) => num(r.ecWorkWater) },
      waterUsageL: { title: 'Werkwater (l)', render: (r) => num(r.waterUsageL) },
      remark: { title: 'Opmerking', render: (r) => r.remark || '—' },
    };
    // Volgorde van de optielijst aanhouden, zodat kolommen niet verspringen op aanvinkvolgorde.
    const chosen = ProjectDetail.PEILBUIS_COLUMN_OPTIONS
      .filter((o) => this._peilbuisColumns.includes(o.id))
      .map((o) => ({ id: o.id, ...extra[o.id] }));

    return html`
      <div @click=${() => { this._peilbuizenOpen = false; }}
        style="position:fixed; inset:0; z-index:var(--mv-z-modal); background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:5vh 1rem;">
        <div @click=${(e) => e.stopPropagation()}
          style="background:var(--mv-surface); border-radius:.8rem; width:min(1100px,100%); max-height:90vh; display:flex; flex-direction:column; box-shadow:0 24px 60px rgba(0,0,0,.25);">
          <div style="display:flex; align-items:center; gap:10px; padding:1rem 1.25rem .75rem; border-bottom:1px solid var(--mv-border);">
            <h3 style="margin:0; font-size:1rem; font-weight:700; color:var(--mv-text);">Peilbuizen</h3>
            <span style="font-size:.78rem; color:var(--mv-text-muted); font-weight:600;">${rows.length}</span>
            <button @click=${() => { this._peilbuizenOpen = false; }} title="Sluiten"
              style="margin-left:auto; background:none; border:none; cursor:pointer; font-size:1.4rem; line-height:1; color:var(--mv-text-sub); padding:0 2px;">&times;</button>
          </div>
          <div style="overflow:auto; padding:15px 1.25rem 1.25rem;">
            ${!rows.length ? html`
              <div style="padding:18px 0; font-size:.85rem; color:var(--mv-text-sub);">Nog geen peilbuizen in dit project.</div>
            ` : html`
              <mv-table
                .columns=${[...base, ...chosen]}
                .data=${rows}
                .columnOptions=${ProjectDetail.PEILBUIS_COLUMN_OPTIONS}
                .visibleColumns=${this._peilbuisColumns}
                @columns-changed=${(e) => this._savePeilbuisColumns(e.detail.visible)}
              ></mv-table>`}
          </div>
        </div>
      </div>`;
  }

  _boringsWithoutLocation() {
    return (this.projectBorings || []).filter((b) => b.x == null || b.y == null);
  }

  /** Eén meldingsregel: klikken brengt je naar de boring waar het opgelost moet worden. */
  /**
   * Meldingen per boring, in de volgorde die de vlakke lijst al had.
   *
   * Die lijst staat blokkerend-eerst, dus door op eerste voorkomen te groeperen komt een
   * boring die het rapport tegenhoudt vanzelf bovenaan, en staat binnen zo'n boring het
   * blokkerende punt boven het aandachtspunt. Geen tweede sortering nodig.
   */
  _issuesGrouped(issues) {
    const groups = new Map();
    for (const i of issues) {
      if (!groups.has(i.boringId)) {
        groups.set(i.boringId, { boringId: i.boringId, boringName: i.boringName, issues: [] });
      }
      groups.get(i.boringId).issues.push(i);
    }
    return [...groups.values()];
  }

  /**
   * Eén boring met zijn meldingen eronder.
   *
   * De boringnaam stond eerder op elke regel. Bij vier meldingen op dezelfde boring las je
   * dan vier keer dezelfde naam en moest je zelf bij elkaar zoeken wat bij elkaar hoort --
   * terwijl je die lijst juist doorloopt om te weten WELKE boring je nog moet nalopen. De
   * naam staat er nu één keer boven, met hoeveel er te doen is.
   */
  _renderIssueGroup(g) {
    const blocking = g.issues.filter((i) => i.severity === BLOCKING).length;
    return html`
      <div style="border-bottom:1px solid var(--mv-border); padding:8px 2px 9px;">
        <button @click=${() => this._jumpToBoring(g.boringId)}
          style="display:flex; align-items:center; gap:7px; background:none; border:none; padding:0 0 2px;
                 cursor:pointer; font:inherit;">
          <span style="font-weight:700; font-size:.83rem; color:var(--mv-primary);">${g.boringName}</span>
          <!-- Een teller alleen als er meer dan één melding is: bij precies één zou hij
               herhalen wat de regel eronder al laat zien. -->
          ${g.issues.length > 1 ? html`
            <span style="font-size:.7rem; font-weight:700; color:var(--mv-text-muted);
                         background:var(--mv-surface-alt,#f3f4f6); border-radius:99px; padding:1px 7px;">
              ${g.issues.length}${blocking ? html` · ${blocking} blokkerend` : ''}
            </span>` : ''}
        </button>
        ${g.issues.map((i) => this._renderIssueRow(i))}
      </div>`;
  }

  _renderIssueRow(i) {
    const blocking = i.severity === BLOCKING;
    const colour = blocking ? 'var(--mv-danger,#EF4444)' : '#D97706';
    return html`
      <div style="display:flex; align-items:center; gap:9px; padding:3px 0 3px 2px;">
        <span style="flex:0 0 auto; color:${colour}; display:inline-flex;" title=${blocking ? 'Blokkeert het rapport' : 'Aandachtspunt'}>
          ${this._issueIcon(i.code)}
        </span>
        <span style="flex:1; font-size:.83rem; color:var(--mv-text);">${i.label}</span>
      </div>`;
  }

  _renderExport(canDownloadXML, processedCount, failedCount = 0) {
    const totalCount = this.recordings.length;
    if (!canDownloadXML) {
      return html`
        <div class="export-pending-state">
          <div class="mini-spinner" style="width:24px; height:24px; border-width:3px;"></div>
          <div class="export-pending-label">Opnamen worden verwerkt...</div>
          <div class="export-pending-sub">
            ${processedCount} van ${totalCount} verwerkt${failedCount ? ` · ${failedCount} mislukt` : ''}
          </div>
        </div>`;
    }

    // Blokkeert de export niet, maar verzwijgen mag ook niet: wat niet verwerkt is, zit er
    // ook niet in.
    const failedNotice = failedCount ? html`
      <div style="display:flex; align-items:center; gap:8px; margin-bottom:14px; padding:10px 12px; border-radius:8px;
                  border-left:4px solid var(--mv-danger,#EF4444); background:var(--mv-danger-bg,rgba(239,68,68,0.08));">
        <span style="font-size:0.82rem; color:var(--mv-text);">
          ${failedCount} opname${failedCount !== 1 ? 'n zijn' : ' is'} niet verwerkt en ${failedCount !== 1 ? 'zitten' : 'zit'} niet in de export.
        </span>
      </div>` : '';

    const totals = this._exportTotals;
    const badge = (t) => html`<span style="font-size:0.54rem; font-weight:800; text-transform:uppercase; letter-spacing:0.05em; background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary); border-radius:4px; padding:1px 5px;">${t}</span>`;

    const byId = Object.fromEntries(this._exportFormats().map(o => [o.id, o]));
    const tile = (o) => html`
      <button @click=${() => this._openExportModal(o.id)}
        style="text-align:left; border:1px solid var(--mv-border); border-radius:12px; padding:16px; cursor:pointer; background:var(--mv-surface); display:flex; flex-direction:column; gap:11px; font-family:inherit; transition:border-color 0.12s, box-shadow 0.12s;"
        @mouseenter=${e => { e.currentTarget.style.borderColor = 'var(--mv-primary)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
        @mouseleave=${e => { e.currentTarget.style.borderColor = 'var(--mv-border)'; e.currentTarget.style.boxShadow = 'none'; }}>
        <span style="width:40px; height:40px; border-radius:10px; background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary); display:inline-flex; align-items:center; justify-content:center;">${unsafeSVG(lucideIcon(o.icon, 20))}</span>
        <span style="min-width:0;">
          <span style="display:flex; align-items:center; gap:7px; font-weight:700; font-size:0.95rem; color:var(--mv-text);">${o.title}${o.badge ? badge(o.badge) : ''}</span>
          <span style="display:block; font-size:0.8rem; color:var(--mv-text-sub); margin-top:3px; line-height:1.35;">${o.desc}</span>
        </span>
      </button>`;
    const sectionLabel = (t) => html`<div style="font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--mv-text-muted); margin:0 0 10px;">${t}</div>`;
    const grid = (tiles) => html`<div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(240px, 1fr)); gap:14px;">${tiles}</div>`;

    return html`
      <div style="max-width:760px;">
        <div style="font-size:1.15rem; font-weight:700; color:var(--mv-text);">Exporteren</div>
        <div style="font-size:0.85rem; color:var(--mv-text-sub); margin:2px 0 24px;">${totals.meetpunten} boring${totals.meetpunten === 1 ? '' : 'en'} · ${totals.lagen} lagen · ${totals.monsters} grondmonsters · ${totals.peilbuizen} peilbuizen</div>

        ${failedNotice}

        <div style="margin-bottom:28px;">
          ${sectionLabel('Bestanden')}
          ${grid(html`${tile(byId.xml)}${tile(byId.excel)}`)}
        </div>

        <div>
          ${sectionLabel('Rapportage')}
          ${grid(html`${tile(byId.report)}${tile(byId.profiles)}`)}
        </div>
      </div>
      ${this._exportModalOpen ? this._renderExportModal() : ''}
    `;
  }

  _renderExportModal() {
    const formats = this._exportFormats();
    const fmt = ['xml', 'profiles', 'report', 'excel'].includes(this._exportFormat) ? this._exportFormat : 'xml';
    const isPdf = fmt === 'profiles' || fmt === 'report';
    const cur = formats.find(f => f.id === fmt) || formats[0];
    const totals = this._exportTotals;
    const allB = this._allBoringen;
    const selectable = allB.filter(b => b.guid);
    const isSel = (b) => !b.guid || this.selectedBoringGuids.has(b.guid);
    const allSelected = selectable.length > 0 && this.selectedBoringGuids.size === selectable.length;
    const selectedBorings = allB.filter(isSel);
    const noCoordSelected = selectedBorings.filter(b => b.guid && b.data && (b.data.x == null || b.data.y == null));

    const sys = this.project?.coordinate_system || DEFAULT_COORDINATE_SYSTEM;
    const sysLabel = (COORDINATE_SYSTEMS[sys] || {}).label || sys;
    const datum = this.project?.height_datum || DEFAULT_HEIGHT_DATUM;
    const datumLabel = (HEIGHT_DATUMS[datum] || {}).shortLabel || datum;
    const std = normalizeSoilStandard(this.project?.soil_standard || DEFAULT_SOIL_STANDARD);
    const stdLabel = SOIL_STANDARDS[std].short;

    // Boringlijst
    const chip = (icon, n) => html`<span style="display:inline-flex; align-items:center; gap:3px; font-size:0.8rem; font-weight:600; color:${n ? 'var(--mv-text-sub)' : 'var(--mv-text-muted)'}; opacity:${n ? 1 : 0.45}; min-width:26px; justify-content:flex-end;">${unsafeSVG(lucideIcon(icon, 13))}${n}</span>`;
    const groups = {};
    groups[this.projectId] = { title: this.project?.project_name || 'Hoofdproject', boringen: [] };
    (this.deelprojects || []).forEach(dp => { groups[dp.id] = { title: dp.project_name || 'Deelproject', boringen: [] }; });
    allB.forEach(b => { if (groups[b.projectId]) groups[b.projectId].boringen.push(b); });
    const multiGroup = Object.values(groups).filter(g => g.boringen.length > 0).length > 1;
    const boringRow = (b) => {
      const sel = isSel(b);
      const warnings = b.data ? this._getBoringWarnings(b.data) : [];
      const noCoords = b.data && (b.data.x == null || b.data.y == null);
      return html`
        <div @click=${() => this.toggleBoringSelection(b.guid)}
          style="display:flex; align-items:center; gap:11px; padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border); cursor:pointer; background:${sel ? 'var(--mv-primary-tint,#e9f5e6)' : 'transparent'};">
          <input type="checkbox" .checked=${sel} @click=${e => e.stopPropagation()} @change=${() => this.toggleBoringSelection(b.guid)}
            style="accent-color:var(--mv-primary); width:15px; height:15px; margin:0; cursor:pointer; flex-shrink:0;">
          <div style="flex:1; min-width:0;">
            <div style="font-weight:700; color:var(--mv-text); font-size:0.85rem;">${b.name}</div>
            ${b.boormeester ? html`<div style="font-size:0.72rem; color:var(--mv-text-muted); margin-top:1px;">boring · ${b.boormeester}</div>` : ''}
            ${warnings.length ? this._renderWarningBadges(warnings) : ''}
          </div>
          ${noCoords ? html`<span title="Geen coördinaten" style="color:#B45309; display:inline-flex; flex-shrink:0;">${unsafeSVG(lucideIcon(AlertTriangle, 14))}</span>` : ''}
          <div style="display:flex; gap:10px; flex-shrink:0;">${chip(Layers, b.layers.length)}${chip(FlaskConical, b.monsters.length)}${chip(TestTube, b.peilbuizenCount || 0)}${this._fotoTelling ? chip(Camera, this._fotoTelling[b.guid]?.aantal || 0) : ''}</div>
        </div>`;
    };
    const list = allB.length === 0
      ? html`<div style="padding:1rem 0.9rem; color:var(--mv-text-muted); font-size:0.85rem;">Nog geen boringen om te exporteren.</div>`
      : html`${Object.keys(groups).map(pid => {
          const g = groups[pid];
          if (!g.boringen.length) return '';
          return html`${multiGroup ? html`<div style="font-size:0.64rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--mv-text-muted); padding:0.5rem 0.9rem 0.2rem; background:var(--mv-surface-alt);">${g.title}</div>` : ''}${g.boringen.map(boringRow)}`;
        })}`;

    const pdfPreview = () => {
      if (this._pdfBusy) return html`<div style="display:flex; align-items:center; justify-content:center; height:100%; gap:8px; color:#d0d0d0;"><span style="display:inline-flex; animation:rotate 1s linear infinite;">${unsafeSVG(lucideIcon(Loader2, 18))}</span> PDF opbouwen…</div>`;
      if (this._pdfError) return html`<div style="display:flex; align-items:center; justify-content:center; height:100%; color:#f1a0a0; font-size:0.86rem; padding:2rem; text-align:center;">${this._pdfError}</div>`;
      if (!this._pdfPages.length) return html`<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; color:#c8c8c8; gap:10px; padding:2rem; text-align:center;">${unsafeSVG(lucideIcon(FileText, 30))}<div style="font-size:0.85rem;">Nog geen voorbeeld. Klik op <strong>Vernieuwen</strong>.</div></div>`;
      const idx = Math.min(this._pdfPage, this._pdfPages.length - 1);
      return html`<div style="padding:20px; display:flex; justify-content:center; align-items:flex-start; min-height:100%;">
        <img src=${this._pdfPages[idx]} style="max-width:100%; box-shadow:0 3px 16px rgba(0,0,0,0.35); background:#fff;">
      </div>`;
    };
    // Excel: geen weergave van het bestand, wel welke tabbladen erin komen en hoeveel rijen.
    const excelPreview = () => {
      const sheets = buildExcelSheets(this._excelBorings(), this._excelOptions());
      return html`<div style="padding:20px; max-width:640px;">
        <table style="width:100%; border-collapse:collapse; font-size:0.85rem;">
          <tr><th style="text-align:left; padding:6px 8px; border-bottom:1px solid var(--mv-border); color:var(--mv-text-muted);">Tabblad</th><th style="text-align:right; padding:6px 8px; border-bottom:1px solid var(--mv-border); color:var(--mv-text-muted);">Rijen</th><th style="text-align:right; padding:6px 8px; border-bottom:1px solid var(--mv-border); color:var(--mv-text-muted);">Kolommen</th></tr>
          ${sheets.map(s => html`<tr title=${s.columns.join(' · ')}>
            <td style="padding:6px 8px; border-bottom:1px solid var(--mv-border); font-weight:600; color:var(--mv-text);">${s.name}</td>
            <td style="padding:6px 8px; border-bottom:1px solid var(--mv-border); text-align:right; color:var(--mv-text-sub);">${s.rows.length}</td>
            <td style="padding:6px 8px; border-bottom:1px solid var(--mv-border); text-align:right; color:var(--mv-text-sub);">${s.columns.length}</td>
          </tr>`)}
        </table>
        <div style="margin-top:12px; font-size:0.78rem; color:var(--mv-text-muted);">Leesbare termen in plaats van codes; diepten in meters, eenheden staan in de kolomnaam.</div>
      </div>`;
    };
    const xmlPreview = () => {
      if (this.exportGenerating) return html`<div style="display:flex; align-items:center; justify-content:center; height:100%; gap:8px; color:#9aa;"><span style="display:inline-flex; animation:rotate 1s linear infinite;">${unsafeSVG(lucideIcon(Loader2, 18))}</span> Voorbeeld genereren…</div>`;
      if (!this.xmlContent) return html`<div style="display:flex; align-items:center; justify-content:center; height:100%; color:#9aa; font-size:0.86rem;">Nog geen voorbeeld beschikbaar.</div>`;
      const lines = this.xmlContent.split('\n');
      return html`<div style="height:100%; overflow:auto; background:#1e1e1e; box-sizing:border-box; padding:0.9rem 0;">
        <table style="border-collapse:collapse; font-family:'Fira Code','Courier New',monospace; font-size:0.78rem; line-height:1.55; width:100%;">
          ${lines.map((ln, i) => html`<tr>
            <td style="text-align:right; color:#5a5a5a; padding:0 12px 0 14px; user-select:none; vertical-align:top; white-space:nowrap;">${i + 1}</td>
            <td style="color:#d4d4d4; white-space:pre; padding-right:14px;">${ln || ' '}</td>
          </tr>`)}
        </table>
      </div>`;
    };

    const busy = this.exportGenerating;
    const disabled = totals.meetpunten === 0 || busy;
    const summaryLine = `${totals.meetpunten} boring${totals.meetpunten === 1 ? '' : 'en'} · ${totals.lagen} lagen · ${totals.monsters} grondmonsters · ${totals.peilbuizen} peilbuizen`;

    return html`
      <div style="position:fixed; inset:0; z-index:1100; background:rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center; padding:2vh 2vw;"
        @click=${() => this._closeExportModal()}>
        <div @click=${e => e.stopPropagation()} style="width:min(1400px, 96vw); height:96vh; background:var(--mv-surface); border-radius:12px; box-shadow:0 24px 70px rgba(0,0,0,0.35); display:flex; flex-direction:column; overflow:hidden;">

          <!-- Kop -->
          <div style="display:flex; align-items:center; justify-content:space-between; gap:12px; padding:0.85rem 1.25rem; border-bottom:1px solid var(--mv-border);">
            <div style="display:flex; align-items:center; gap:9px;">${unsafeSVG(lucideIcon(cur.icon, 18))}<span style="font-weight:700; font-size:1rem; color:var(--mv-text);">${cur.title}</span>${cur.badge ? html`<span style="font-size:0.56rem; font-weight:800; text-transform:uppercase; letter-spacing:0.05em; background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary); border-radius:4px; padding:1px 5px;">${cur.badge}</span>` : ''}</div>
            <button title="Sluiten" @click=${() => this._closeExportModal()} style="background:none; border:none; cursor:pointer; color:var(--mv-text-muted); padding:4px; display:inline-flex;">${unsafeSVG(lucideIcon(X, 20))}</button>
          </div>

          <!-- Body: links config, rechts voorvertoning -->
          <div style="flex:1; min-height:0; display:flex;">

            <div style="flex:0 0 340px; border-right:1px solid var(--mv-border); display:flex; flex-direction:column; min-height:0;">
                <div style="display:flex; align-items:center; justify-content:space-between; padding:0.6rem 0.9rem 0.5rem;">
                  <span style="font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted);">Boringen</span>
                  <span style="font-size:0.78rem; color:var(--mv-text-muted);">${totals.meetpunten} van ${selectable.length}${selectable.length > 1 ? html` · <button @click=${this.toggleAllBoringen} style="background:none; border:none; cursor:pointer; color:var(--mv-primary); font-weight:600; font-family:inherit; font-size:0.78rem; padding:0;">${allSelected ? 'wis' : 'alles'}</button>` : ''}</span>
                </div>
                <div style="flex:1; min-height:0; overflow-y:auto;">${list}</div>
                ${noCoordSelected.length ? html`
                  <div style="display:flex; align-items:center; gap:6px; padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border); font-size:0.75rem; color:#B45309; background:var(--mv-warning-panel-bg,#FFFBEB);">
                    ${unsafeSVG(lucideIcon(AlertTriangle, 13))}
                    <span style="flex:1;">${noCoordSelected.length === 1 ? `${noCoordSelected[0].name} mist een coördinaat` : `${noCoordSelected.length} boringen missen een coördinaat`}</span>
                    <button @click=${() => { noCoordSelected.forEach(b => this.selectedBoringGuids.delete(b.guid)); this.requestUpdate(); this._scheduleXmlPreview(); }} style="background:none; border:none; cursor:pointer; color:var(--mv-primary); font-weight:600; font-family:inherit; font-size:0.75rem;">sluit uit</button>
                  </div>` : ''}

              ${fmt === 'xml' ? this._renderFotoTegel(selectedBorings, busy) : ''}
              <div style="padding:0.8rem 0.9rem; border-top:1px solid var(--mv-border); background:var(--mv-surface-alt);">
                <label style="font-size:0.63rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted); display:block; margin-bottom:4px;">Projectcode</label>
                <input type="text" .value=${this.project?.project_code || ''} placeholder="bijv. PRJ-2026-001"
                  @change=${e => this._saveProjectCode(e.target.value)}
                  style="width:100%; box-sizing:border-box; padding:8px 10px; border:1px solid var(--mv-border); border-radius:7px; font-size:0.85rem; font-family:inherit; background:var(--mv-surface); color:var(--mv-text);">
                <div style="margin-top:8px; font-size:0.72rem; color:var(--mv-text-muted); display:flex; align-items:center; gap:5px;">${unsafeSVG(lucideIcon(MapPin, 12))} ${sysLabel} · hoogte ${datumLabel} · ${stdLabel}</div>
              </div>
            </div>

            <div style="flex:1; min-width:0; display:flex; flex-direction:column; min-height:0;">
              <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; padding:0.55rem 0.9rem; border-bottom:1px solid var(--mv-border); background:var(--mv-surface-alt);">
                <span style="font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted);">Voorbeeld · ${cur.file}</span>
                ${isPdf ? html`
                  <div style="display:flex; align-items:center; gap:10px;">
                    <button class="btn-secondary" style="display:inline-flex; align-items:center; gap:6px; font-size:0.78rem; padding:5px 11px; ${this._pdfDirty ? 'border-color:var(--mv-primary); color:var(--mv-primary);' : ''}" ?disabled=${this._pdfBusy} @click=${() => this._refreshPdfPreview()}>
                      <span style="display:inline-flex; ${this._pdfBusy ? 'animation:rotate 1s linear infinite;' : ''}">${unsafeSVG(lucideIcon(RefreshCw, 13))}</span> Vernieuwen${this._pdfDirty ? ' •' : ''}
                    </button>
                    ${this._pdfPages.length ? html`
                      <div style="display:flex; align-items:center; gap:4px; font-size:0.78rem; color:var(--mv-text-sub);">
                        <button class="btn-secondary" style="padding:3px 6px;" ?disabled=${this._pdfPage <= 0} @click=${() => { this._pdfPage = Math.max(0, this._pdfPage - 1); }}>${unsafeSVG(lucideIcon(ChevronLeft, 14))}</button>
                        <span style="min-width:54px; text-align:center;">${this._pdfPage + 1} / ${this._pdfPages.length}</span>
                        <button class="btn-secondary" style="padding:3px 6px;" ?disabled=${this._pdfPage >= this._pdfPages.length - 1} @click=${() => { this._pdfPage = Math.min(this._pdfPages.length - 1, this._pdfPage + 1); }}>${unsafeSVG(lucideIcon(ChevronRight, 14))}</button>
                      </div>` : ''}
                  </div>` : (fmt === 'xml' && this.xmlContent ? html`<button class="btn-secondary" style="font-size:0.75rem; padding:4px 10px;" @click=${() => { navigator.clipboard.writeText(this.xmlContent); showAlert('XML gekopieerd naar klembord'); }}>Kopieer XML</button>` : '')}
              </div>
              <div style="flex:1; min-height:0; overflow:auto; background:${fmt === 'xml' ? '#1e1e1e' : fmt === 'excel' ? 'var(--mv-surface)' : '#525659'};">
                ${fmt === 'xml' ? xmlPreview() : fmt === 'excel' ? excelPreview() : pdfPreview()}
              </div>
            </div>
          </div>

          <!-- Onderbalk -->
          <div style="display:flex; align-items:center; gap:14px; padding:0.85rem 1.25rem; border-top:1px solid var(--mv-border); flex-wrap:wrap;">
            <div style="flex:1; min-width:200px; font-size:0.86rem; font-weight:600; color:var(--mv-text);">${summaryLine}</div>
            <button class="btn-secondary" @click=${() => this._closeExportModal()} style="padding:0.55rem 1rem; font-size:0.88rem;">Sluiten</button>
            <button class="btn btn-primary" style="display:inline-flex; align-items:center; gap:7px; justify-content:center; padding:0.6rem 1.2rem; font-size:0.9rem;" ?disabled=${disabled} @click=${() => cur.action()}>
              ${busy ? html`<span style="display:inline-flex; animation:rotate 1s linear infinite;">${unsafeSVG(lucideIcon(Loader2, 16))}</span> ${this._exportVoortgang || 'Bezig…'}` : html`${unsafeSVG(lucideIcon(Download, 16))} ${cur.label}`}
            </button>
          </div>
        </div>
      </div>`;
  }

  /** De tegel 'Foto's meenemen' in het exportvenster (alleen bij de TerraIndex-export). */
  _renderFotoTegel(selectedBorings, busy) {
    const t = this._fotoTelling;
    const gekozen = selectedBorings.filter((b) => b.guid);
    // Kwamen er boringen bij na het openen (de lijst laadt soms later), dan die nog tellen.
    const ontbreekt = gekozen.some((b) => !t?.[b.guid]);
    if ((!t || ontbreekt) && !this._fotoTellingBezig) queueMicrotask(() => this._laadFotoTelling());
    const metFoto = t ? gekozen.filter((b) => (t[b.guid]?.aantal || 0) > 0) : [];
    const aantal = t ? gekozen.reduce((s, b) => s + (t[b.guid]?.aantal || 0), 0) : 0;
    const bytes = t ? gekozen.reduce((s, b) => s + (t[b.guid]?.bytes || 0), 0) : 0;
    const mb = (bytes / (1024 * 1024)).toLocaleString('nl-NL', { maximumFractionDigits: 1 });
    const sub = (!t || ontbreekt) ? "Foto's tellen…"
      : !aantal ? "Geen foto's bij de gekozen boringen"
      : `${aantal} foto${aantal === 1 ? '' : "'s"} bij ${metFoto.length} boring${metFoto.length === 1 ? '' : 'en'}${bytes ? ` · ${mb} MB` : ''}`;
    return html`
      <div style="padding:0.8rem 0.9rem; border-top:1px solid var(--mv-border); background:var(--mv-surface-alt);">
        <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:6px;">
          <span style="font-size:0.63rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted);">Foto's</span>
          <span data-tip=${FOTO_EXPORT_UITLEG} @mousemove=${tipMove} @mouseleave=${tipHide}
            style="display:inline-flex; align-items:center; gap:5px; padding:4px 9px; border-radius:7px; cursor:help; font-size:0.72rem; font-weight:600;
                   color:var(--mv-text-sub); background:var(--mv-surface); border:1px solid var(--mv-border);">
            ${unsafeSVG(lucideIcon(Info, 13))} Hoe werkt dit?
          </span>
        </div>
        <label style="display:flex; align-items:flex-start; gap:10px; padding:10px 12px; border-radius:9px; cursor:${busy ? 'default' : 'pointer'};
                      background:var(--mv-surface); border:1px solid ${this._exportFotos ? 'var(--mv-primary)' : 'var(--mv-border)'};">
          <input type="checkbox" .checked=${this._exportFotos} ?disabled=${busy || (t && !aantal)}
            @change=${(e) => { this._exportFotos = e.target.checked; }}
            style="accent-color:var(--mv-primary); width:15px; height:15px; margin:2px 0 0; cursor:pointer; flex-shrink:0;">
          <span style="min-width:0;">
            <span style="display:block; font-weight:700; font-size:0.85rem; color:var(--mv-text);">Foto's meenemen</span>
            <span style="display:block; font-size:0.74rem; color:var(--mv-text-muted); margin-top:2px;">${sub}</span>
          </span>
        </label>
      </div>`;
  }

  _renderFilterModal() {
    const meetpunten = (this.projectBorings || []).map(b => ({
      guid: b.id,
      name: b.name || 'Naamloos',
      recording: this.recordings.find(r => r.id === b.recordingId)?.filename || '',
    }));

    return html`
      <div class="filter-modal-overlay" @click=${() => this.showFilterModal = false}>
        <div class="filter-modal" @click=${(e) => e.stopPropagation()}>
          <div class="modal-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.5rem;">
            <h3 style="margin:0;">Project Exporteren</h3>
            <button class="btn-close" @click=${() => this.showFilterModal = false} style="background:none; border:none; cursor:pointer; color:var(--mv-text-muted);">
              ${unsafeSVG(lucideIcon(X, 20))}
            </button>
          </div>

          <div style="margin-bottom:1.5rem;">
            <label style="display:block; font-size:0.875rem; font-weight:600; color:var(--mv-text); margin-bottom:0.75rem;">Bestandsformaat</label>
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px;">
              <div
                class="format-option ${this.exportFormat === 'xml' ? 'active' : ''}"
                @click=${() => this.exportFormat = 'xml'}
                style="padding:1rem; border:1px solid ${this.exportFormat === 'xml' ? 'var(--mv-primary)' : 'var(--mv-border)'}; border-radius:10px; cursor:pointer; background:${this.exportFormat === 'xml' ? 'var(--mv-primary-light)' : 'var(--mv-surface)'}; transition:all 0.2s ease; display:flex; flex-direction:column; gap:6px; align-items:center;"
              >
                ${unsafeSVG(lucideIcon(FileJson, 24))}
                <span style="font-weight:600; font-size:0.9rem;">SIKB0101 XML</span>
                <span style="font-size:0.75rem; color:var(--mv-text-muted); text-align:center;">Standaard export voor bodemonderzoek</span>
              </div>
              <div
                class="format-option ${this.exportFormat === 'pdf' ? 'active' : ''}" 
                @click=${() => this.exportFormat = 'pdf'}
                style="padding:1rem; border:1px solid ${this.exportFormat === 'pdf' ? 'var(--mv-primary)' : 'var(--mv-border)'}; border-radius:10px; cursor:pointer; background:${this.exportFormat === 'pdf' ? 'var(--mv-primary-light)' : 'var(--mv-surface)'}; transition:all 0.2s ease; display:flex; flex-direction:column; gap:6px; align-items:center;"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>
                <span style="font-weight:600; font-size:0.9rem;">PDF Rapport</span>
                <span style="font-size:0.75rem; color:var(--mv-text-muted); text-align:center;">Gelikt document met alle projectdata</span>
              </div>
            </div>
          </div>

          <div style="margin-bottom:1rem;">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0.75rem;">
              <label style="font-size:0.875rem; font-weight:600; color:var(--mv-text);">Filtering (Meetpunten)</label>
              <div style="display:flex; gap:12px;">
                <button class="link-btn" @click=${this.selectAllMeetpunten} style="background:none; border:none; color:var(--mv-primary); font-size:0.75rem; font-weight:600; cursor:pointer; padding:0;">Selecteer alles</button>
                <button class="link-btn" @click=${this.deselectAllMeetpunten} style="background:none; border:none; color:var(--mv-danger); font-size:0.75rem; font-weight:600; cursor:pointer; padding:0;">Deselecteer alles</button>
              </div>
            </div>
            
            <div class="meetpunt-selector-list" style="max-height:240px; overflow-y:auto; border:1px solid var(--mv-border); border-radius:10px; padding:4px;">
              ${meetpunten.map(mp => html`
                <div 
                  class="meetpunt-item ${this.selectedMeetpuntIds.includes(mp.guid) ? 'selected' : ''}" 
                  @click=${() => mp.guid && this.toggleMeetpuntSelection(mp.guid)}
                  style="padding:0.75rem 1rem; cursor:pointer; border-radius:8px; display:flex; align-items:center; justify-content:space-between; transition:all 0.2s ease; margin-bottom:2px; ${this.selectedMeetpuntIds.includes(mp.guid) ? 'background:var(--mv-primary-light);' : ''}"
                >
                  <div style="display:flex; flex-direction:column;">
                    <span style="font-weight:600; font-size:0.9rem;">${mp.name}</span>
                    <span style="font-size:0.75rem; color:var(--mv-text-muted);">${mp.recording}</span>
                  </div>
                  <div style="width:20px; height:20px; border:2px solid ${this.selectedMeetpuntIds.includes(mp.guid) ? 'var(--mv-primary)' : 'var(--mv-border)'}; border-radius:50%; display:flex; align-items:center; justify-content:center; background:${this.selectedMeetpuntIds.includes(mp.guid) ? 'var(--mv-primary)' : 'transparent'};">
                    ${this.selectedMeetpuntIds.includes(mp.guid) ? html`<div style="width:6px; height:6px; border-radius:50%; background:#fff;"></div>` : ''}
                  </div>
                </div>
              `)}
              ${meetpunten.length === 0 ? html`<div style="padding:2rem; text-align:center; color:var(--mv-text-muted); font-style:italic;">Geen meetpunten gevonden voor export.</div>` : ''}
            </div>
          </div>

          <div class="filter-footer" style="display:flex; gap:12px; margin-top:1.5rem;">
            <button class="btn btn-secondary" @click=${() => this.showFilterModal = false} style="flex:1;">Annuleren</button>
            <button 
              class="btn btn-primary" 
              @click=${this.handleExport}
              ?disabled=${this.selectedMeetpuntIds.length === 0}
              style="flex:2;"
            >
              ${unsafeSVG(lucideIcon(Download, 18))}
              Export Downloaden (${this.selectedMeetpuntIds.length})
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('project-detail', ProjectDetail);
